/**
 * EnvironmentModule — the Community World's terrain, sky, and Versonas (P14.3).
 *
 * Renders the SERVER-AUTHORITATIVE environment via the EngineX scene: a procedural
 * heightfield mesh (the SAME deterministic `terrainHeight` the server gates
 * placement against — so what you see is what's buildable), a day/night sky tint +
 * sun driven by the replicated `skyState.timeOfDay`, and NPC avatars at the
 * Versonas' replicated positions. The client RENDERS; the server owns the truth.
 *
 * NOTE: visual fidelity (seamless CDLOD streaming, etc.) is confirmed in a real
 * browser — this module wires the data to the engine and is GPU-rendered there.
 */

import * as THREE from 'three/webgpu';
import { waterStillAt, waterLiveAt } from '../lib/water_fx.js';
import { SWIM_DEPTH_M, SWIM_FEET_UNDER_M } from '../lib/water_physics.js';
import { assignSeats, type Seat, type StationRow, type Person } from '../lib/station_seating.js';
import { buildStationProp } from '../lib/station_props.js';
import { activityDef } from '../lib/activity_catalog.js';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import type { App, IModule } from 'enginex';
import { Phase, makeSystem } from '../engine-compat.js';
import type { DbConnection } from '../module_bindings/index.js';
import { footprintOf } from '@shared/types/structures.js';
import { VERSONA_HALL_FOOTPRINT } from '../lib/guild_rules.js';
import { atlas } from '../state.js';
import { settlementSites } from '@shared/settlements.js';
import { civilizationOf } from '../lib/biome_settlements.js';
import { civOfSettlementRowC } from '../lib/civ_row.js';
import { queueTextureWarm, schedulePipelineWarm, warmThenShow, warmPending } from '../lib/pipeline_warm.js';
import { bus } from '../bus.js';
import { terrainHeight, biomeAt, biomeWeights, BIOMES } from '@shared/terrain.js';
import { terrainEditsNear, terrainEditVersion, paintedWeights, paintOverride } from '../lib/terrain_edits.js';
import { stampedHeight, roadSurfaceMask, meshHeight, setChunkSeg, setChunkHeights, precacheSettlementPlans, settlementClearance, buildingStampsNear } from '../lib/terrain_stamp.js';
import { fieldMask } from '../lib/hinterland.js';
import { personalGroundY, PERSONAL_TERRAIN_SIZE, PERSONAL_TERRAIN_SEG } from '../lib/personal_terrain.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { lakeSurfaceAtBaked } from '../lib/lakes.js';
import { riverWaterAt, riverSegmentCount } from '../../../shared/terrain.js';
import { ImpostorInstancer, bakeImpostorAtlas, setImpostorDepth } from '../lib/impostor.js'; // surfaces (2026-09-27): + the §5.2 depth lever
import { RAIN_SHELTER_LAYER } from '../lib/rain_shelter.js';
import { deformOn } from '../lib/deform_field.js'; // weather surfaces R3
import { puddleGroundSig } from '../lib/puddle_sim.js'; // shader gaps R6: the shallow-water puddle patch (the ground reads its look buffer)
import { caveSitesNear } from '../../../shared/caves.js';
import { cavePlanFor, caveCutsTriangle, CAVE_PORTAL } from '../lib/cave_gen.js';
import { normalFromAlbedo } from '../lib/normal_from_albedo.js';
import type { AssetModule } from './AssetModule.js';
import type { RenderCullingModule } from './RenderCullingModule.js';
import type { PhysicsModule } from './PhysicsModule.js';
import { CrowdInstancer, StaticInstancer, firstMesh } from '../lib/instancing.js';
import { disposeLater } from '../lib/gpu_dispose.js';
import { markShared } from '../lib/defer_dispose.js';
import { makeAnchorGroup, renderAnchorX, renderAnchorZ, setRenderAnchor } from '../lib/local_origin.js';
import { activePreset, activeTier, foliageWindOn, groundKnobs, setGroundPom, setGroundArrayRes, setGroundTriplanar, type GroundMaterial } from '../lib/quality_tiers.js';
import { impostorDitherFor, isWebGPURenderer } from '../lib/temporal_resolve.js';
import { setGroundMicroShadow, setGroundHexTiling, setGroundPomShadow, setGroundAntiTiling } from '../lib/quality_tiers.js'; // surfaces (2026-09-27): the ground A/B levers
import { nearFoliageLod } from '../lib/near_lod.js';
import { applyWind } from '../lib/wind.js';
import { isCardGeometry, applyCardNormalBend, cardProgramKey, uCardBend } from '../lib/card_normals.js';
import { leafTransmissionFor, leafGroupOf } from '../lib/leaf_transmission.js'; // surfaces (2026-09-27): §5.3 per-species leaf thickness
import { addChunkSkirt, skirtDepthFor } from '../lib/terrain_skirt.js';
import { analyticChunkNormals, seatAnalyticNormals, stitchChunkEdges, terrainNormalsMode, terrainNormalStats } from '../lib/terrain_normals.js'; // shader gaps R5: continuous normals across LOD seams
import { initTrample, disposeTrample, stepTrample, trampleActive, trampleStats, trampleCenter, TRAMPLE_REGION_M } from '../lib/foliage_trample.js';
import { gatherTrampleAgents, snapSpeed, type TrampleAgent, type TramplePoint, type TrampleBody, type WashSource } from '../lib/trample_agents.js';
import { applyFoliageTranslucency, applyHierarchicalWind, crownParamsFromPositions, type CrownParams, type FoliageTranslucencyHandle, type HierarchicalWindHandle } from 'enginex/foliage/shading/index.js';
import { foliageTransmissionIntensity, specularAAVarianceCap } from '../lib/stage5_fx.js';
import { applyCloudShadows } from '../lib/cloud_shadows.js';
import { uSunDir, uNightFactor } from './SkyModule.js';
import { lightFrame } from '../lib/photometry.js'; // surfaces (2026-09-27): §5.3 the leaf transmits the key light (rule 3)
import { applyWetness, applyTerrainGround, applyBiomeGround, applyTerrainGroundPainted, applyGroundV2, groundV2Stats, type GroundArrays } from '../lib/wetness.js';
import { setGroundKeyDir } from '../lib/wetness.js'; // surfaces (2026-09-27): §2.4 the parallax self-shadow's key direction
import { TERRAIN_GROUND_TEXTURES, TERRAIN_TEXTURE_SCALE, BIOME_GROUND, CHUNK_SIZE, CHUNK_RADIUS, CHUNK_SEG_NEAR, CHUNK_WORKER_STALL_MS, CHUNK_WORKER_BOOT_MS } from '../config.js';
import { segForRing, refineChunkSeg } from '../lib/terrain_lod.js';
// Ground material v2 (2026-09-25): scanned layers in texture arrays, placed per vertex by rules.
import { loadGroundTextures, loadGroundMacro, groundTextureStats, type GroundTextureSet } from '../lib/ground_textures.js';
import { groundSetName } from '../lib/ground_layers.js';
import { attachGround, interleaveGround } from '../lib/ground_geometry.js';
import { chunkGroundBuffer, nearestRoutePoint } from '../lib/ground_sampling.js';
import { currentTerrainKernel } from '../lib/terrain_kernel.js';
import { chunkOf, chunkWindow, diffWindows, type ChunkKey } from '../lib/terrain_chunks.js';
import { groundKeepOutsNear, insideGroundKeepOut, groundKeepOutVersion, groundKeepOutChangesSince, groundKeepOutOff } from '../lib/ground_keepout.js';
import { loadHydrology, postRiverTable } from '../lib/hydrology_load.js';
import { terrainSeat } from '../lib/terrain_seat.js';
import { groundYIn } from '../lib/grounding.js';
import { structureClearance } from '../lib/structure_seat.js';
import type { Biome } from '@shared/terrain.js';
import { BIOME_FLORA } from '../lib/biome_flora.js';
import { forestTreesForChunk, FOREST_SPECIES, giantFallback, type TreeClass } from '../lib/forest_field.js';
import { trunkProxyFromGeometry } from '../lib/tree_collider.js';
import type { ColliderProxy } from '../lib/collider_proxy.js';
import { GrassDriver } from '../lib/grass/grass_driver.js'; // visual-realism R2 (2026-09-25): the vertex-procedural grass layer
import { FloraScatter } from '../lib/geology/flora_scatter.js'; // visual-realism R3/R6: geology + understory (worker-planted)
import { ForestHorizon } from '../lib/forest_horizon.js'; // visual-realism R4b: stand impostors to the horizon
import { clearingAt, pickSpecies, ecoWetness, UNDERSTORY } from '../lib/ecology.js'; // visual-realism R5: viability, clearings
import { forestDensity, nearestSiteDist as nearestSiteDistFor } from '../lib/forest_field.js';
import { applySeasonTint, setSeasonTint, keyFoliageGraph } from '../lib/instancing.js';
import { pinStaticMotionVectors as pinStaticMotionVectorsOn } from 'enginex/render/batching/staticMotionVectors.js';

/** Foliage ring of a chunk: 0 near (full mix), 1 mid (LOD1), 2 far woods (large + giant at LOD2), 3 nothing. */
type FoliageRing = 0 | 1 | 2 | 3;
/** Chunk rings beyond the tier's foliage ring that still carry the woods' large and giant trees (LOD2). */
const FOREST_FAR_RINGS = 2;
/** Trees within this many metres of the player carry their trunk trimesh; released past it plus a margin. */
const FOREST_COLLIDE_M = 40;
interface ForestTreeRec { x: number; y: number; z: number; yaw: number; scale: number; assetId: bigint; cls: TreeClass; targetH: number }

const TERRAIN_SIZE = 512;
const TERRAIN_SEGMENTS = 64;

// Which body a versona wears is a PURE rule (lib/versona_bodies.ts, versona wave 4): per-civilisation civilian pools
// with elders, wider archetype pools. The ten pre-wave bodies load at world entry; a wave-4 twin loads ON DEMAND the
// first time one of its wearers is in the area (wantBody) and the pre-wave body stands in until it lands.
import { avatarFor, baseAvatarFor, BASE_AVATAR_KEYS } from '../lib/versona_bodies.js';
export { avatarFor };

import { townSlots, type TownSlot } from '@shared/town_layout.js';
import { enterableAt, enterables, interiorSpec, type EnterableBuilding, type InteriorSpec } from '../lib/building_interiors.js';
import { interiorHomeSpot, HOME_STATES } from '../lib/home_layout.js';
import { deedResidentSpot } from '../lib/deed_home.js';
import { activeInterior, activeDescriptorFor, type ActiveInterior } from '../lib/interior_wiring.js';
import { interiorLifeEnabled, planLife, poseAt, residentCabAt, slotOf, liftShare, lifeClass, type LifeRoom, type LifePlan, type LifeResident, type LifeRig } from '../lib/interior_life.js';
import { lifeRoomForVisit } from '../lib/interior_life_visit.js';
import { letStoreVersion } from '../lib/rental_client.js'; // (slice H) the let rooms key the itinerary's room memo
import { venueCounter, venueOfEnterable } from '../lib/venue_client.js';
import { packPlan, furnishPack } from '../lib/furnish_packs.js';
import { resolvePurpose } from '../lib/interior_purpose.js';
import type { PlanSlot } from '../lib/interior_plans.js';
import { settlementSites as sharedSites, SETTLEMENT_FOOTPRINT as SHARED_FOOTPRINT, type SettlementSite as SharedSite } from '@shared/settlements.js';
import { sfxAt } from '../lib/sfx.js';
import { windupPose } from '../lib/combat_feel.js';
import { windups, lunges, lungeAt, isHurt, lastStruckByPlayer } from '../lib/combat_telegraph.js';
import { VersonaRigLayer } from '../lib/versona_rig.js';
import { indoorTwin, insideActiveInterior, indoorBodiesEnabled } from '../lib/indoor_body.js'; // (slice H) a body in the room is lit by the room
import { handPropFor, founderIsElder, meleeSwinging } from '../lib/versona_clips.js';
import { blendYaw, attentionOf, countsAgainstCap, GAZE_NOTICE_M, STARTLE_M, STARTLE_S, PRIORITY_TURN, CASUAL_TURN, type AttentionKind } from '../lib/versona_gaze.js';
import { dispositionOf, FEAR_REFUSE } from '../lib/relationship.js';
import { routePoints, walkFor, pointAlong, polylineLength, routePace, routeMatchesRow, routeHorizon, type RoutePt } from '../lib/versona_route.js';
import { yieldToMain } from '../lib/yield_main.js';

/** One rendered versona's glide segment + presentation state. */
interface VersonaAnimEntry {
  key: string;
  crowd: CrowdInstancer;
  fromX: number; fromZ: number;
  toX: number; toZ: number;
  t0: number; dur: number; yaw: number;
  /** Authoritative activity (versona_state.state) — drives the presentation
   *  layer: pose loops, facing rules, carried props. */
  state: string;
  /** Per-versona phase (id hash) so crowd motion never marches in step. */
  phase: number;
  /** Even/odd conversational turn — chatting pairs nod ALTERNATELY. */
  parity: number;
  /** Last RENDERED position + stamp — the visual speed governor's memory
   *  (rubber-band fix: clearance projections could sweep a body around a
   *  building near-instantly; nothing on screen may outrun a fast walk). */
  lx?: number; lz?: number; lat?: number;
  /** Indoor cache (home-life wave): which enterable the anchor falls in
   *  (recomputed only on a 1 m cell change — the registry scan is not free),
   *  and the resolved interior spot (bed/sofa/rug) for the current state. */
  inX?: number; inZ?: number; inB?: string | null;
  cdx?: number; cdz?: number;   // last clearance offset (re-projected every 4th frame, staggered)
  spot?: { x: number; z: number; y: number; yaw: number; lying: boolean } | null;
  /** ROUTE AHEAD (2026-09-15): the polyline this body walks from its last drawn point through the
   *  hot row and along the server's corners, at pace v (m/s) from t0; null = the straight glide. */
  route?: { pts: RoutePt[]; s0: number; t0: number; v: number; L: number; sMax: number } | null;
  spotState?: string;
  /** The active interior's `version` the spot was resolved against (slice C: re-resolved when the room's plan changes). */
  spotVer?: number;
  /** (slice L) Under the itinerary this frame: the pose's spot (one object per body, rewritten in place), what its clip
   *  may be told, and whether it is not drawn at all (not come in yet; in the lift car while the player holds it). */
  lifeSpot?: { x: number; z: number; y: number; yaw: number; lying: boolean };
  life?: LifeRig | null;
  lifeHide?: boolean;
  /** (slice L) The anchor the last pose was SETTLED at (undefined while walking): a change with no walk between is a
   *  pop — the body appears at the new spot, never a glide through the room. */
  lifeAt?: string;
  /** The enterables registry stamp `inB` was resolved against (slice L): a building that registers later re-asks. */
  inG?: number;
}

// (The overhead activity glyphs retired 2026-08-13: the Morrowind dialogue
// system carries "what are they doing" now — ask them. Carried crates and
// venue labels stay: those are world objects, not icons.)
const CRATE_MAX = 32;
/** Stations inside this range get their prop built; past the far line it is dropped (hysteresis). */
const STATION_NEAR_M = 150;
const STATION_FAR_M = 190;

/**
 * Cave mouths (terrain wave 2026-09-08): drop every terrain triangle whose
 * centroid lies in a cave's mouth footprint — the hole the tunnel enters by.
 * The trimesh collider is cut from the same index, so the capsule walks in.
 */
function cutCaveMouths(geo: THREE.BufferGeometry, seed: string, worldCX: number, worldCZ: number, seg: number): void {
  // Only the NEAR ring (5.3 m triangles): a coarser ring's 10–25 m triangles
  // would open a hole the portal outcrop cannot roof, and nobody walks into a
  // mouth 300 m away — the hole appears when the chunk comes near.
  if (seg < CHUNK_SEG_NEAR) return;
  const half = CHUNK_SIZE / 2;
  const sites = caveSitesNear(seed, worldCX - half, worldCZ - half, worldCX + half, worldCZ + half, CAVE_PORTAL.a * 1.2 + CAVE_PORTAL.u0);
  if (sites.length === 0) return;
  const idx = geo.index;
  const pos = geo.attributes.position as THREE.BufferAttribute;
  if (!idx) return;
  // The cut follows the ACTUAL cavity (2026-09-10): a triangle under a
  // cave's portal outcrop goes when it touches the tunnel's air, so the hole
  // matches the walls and never outgrows the roof (lib/cave_gen.ts).
  const plans = sites.map((s) => cavePlanFor(seed, s));
  const src = idx.array as ArrayLike<number>;
  const keep: number[] = [];
  for (let i = 0; i + 2 < src.length; i += 3) {
    const a = src[i], b = src[i + 1], c = src[i + 2];
    const ax = worldCX + pos.getX(a), ay = pos.getY(a), az = worldCZ + pos.getZ(a);
    const bx = worldCX + pos.getX(b), by = pos.getY(b), bz = worldCZ + pos.getZ(b);
    const cx = worldCX + pos.getX(c), cy = pos.getY(c), cz = worldCZ + pos.getZ(c);
    let cut = false;
    for (let s = 0; s < sites.length && !cut; s++) {
      const site = sites[s];
      cut = caveCutsTriangle(plans[s], ax - site.x, ay - site.h, az - site.z, bx - site.x, by - site.h, bz - site.z, cx - site.x, cy - site.h, cz - site.z);
    }
    if (cut) continue;
    keep.push(a, b, c);
  }
  if (keep.length !== src.length) geo.setIndex(keep);
}

function impostorsOptedOut(): boolean { try { return localStorage.getItem('atlas:impostors') === 'off'; } catch { return false; } }

/** Bodies beyond this distance (m) draw from the LOD2 far instancer (see swapNpcMesh). */
const NEAR_BODY_M = 28;

/**
 * THE BUILDING KEEP-OUT (2026-09-24): no plant, prop or tree stands inside a structure. The radius is the footprint's
 * half-diagonal + 2 m — it used to be the catalog scale × 1.4, which for a deed whose size lives in the renderer (the
 * guild hall: catalog scale 1, drawn ×50) was 1.4 m, and trees grew through a 95 m hall. Versona guild halls too.
 * Only what can reach the chunk (centre cx, cz; half-size half) is returned.
 */
function structureKeepOuts(conn: DbConnection, worldId: bigint, cx: number, cz: number, half: number): Array<{ x: number; z: number; r2: number }> {
  const out: Array<{ x: number; z: number; r2: number }> = [];
  const add = (x: number, z: number, r: number): void => {
    if (Math.abs(x - cx) > half + r || Math.abs(z - cz) > half + r) return;
    out.push({ x, z, r2: r * r });
  };
  for (const st of conn.db.structure.iter()) {
    if (st.worldId !== worldId) continue;
    const fp = footprintOf(st.structureType);
    add(st.posX, st.posZ, Math.hypot(fp.hx, fp.hz) + 2);
  }
  const vgs = (conn.db as unknown as { versonaGuild?: { iter(): Iterable<{ worldId: bigint; x: number; z: number }> } }).versonaGuild;
  if (vgs) for (const v of vgs.iter()) if (v.worldId === worldId) add(v.x, v.z, Math.hypot(VERSONA_HALL_FOOTPRINT.hx, VERSONA_HALL_FOOTPRINT.hz) + 3);
  return out;
}

export class EnvironmentModule implements IModule {
  readonly name = 'Environment';
  private scene!: THREE.Scene;
  private bound = false;
  private terrainMesh: THREE.Mesh | null = null;
  private terrainRegionCount = -1;
  private terrainMeshSeed = '';
  private terrainEditVer = -1;
  private versonaRoot = new THREE.Group();
  /** Instanced crowd meshes + crates hang here with ANCHOR-RELATIVE matrices
   *  (lib/local_origin.ts): float32 instance attributes can't ride the scene
   *  rebase — 6 cm vertex snapping at 720 km was the "shattered versona" bug.
   *  Plain Object3D children (banners, sparks, labels) stay on versonaRoot. */
  private readonly crowdAnchor = makeAnchorGroup('crowd-anchor');
  /** Rig wave (2026-09-05): the nearest versonas as skinned, animated actors. */
  private readonly rigLayer = new VersonaRigLayer(this.crowdAnchor.group);
  private readonly crowdKeys = new Map<CrowdInstancer, string>();
  private readonly _rigCands: Array<{ id: string; bodyKey: string; x: number; z: number }> = [];
  private npcGeo = new THREE.CapsuleGeometry(0.4, 1.0, 4, 8);
  private npcMat = new THREE.MeshStandardMaterial({ color: 0xe8c07a, roughness: 0.6 });
  private crowd: CrowdInstancer | null = null;   // capsule FALLBACK crowd (avatars that fail to load)
  private crowds = new Map<string, CrowdInstancer>(); // avatar prefab key → its own InstancedMesh crowd
  /** NPC AI 7a: per-Versona from→to interpolation segments (glide instead of teleport-step).
   *  Segments are XZ-only — Y is sampled from the rendered ground at the
   *  INTERPOLATED position every frame (high-relief audit 2026-08-05: lerping
   *  Y between endpoints cut a straight chord through slopes for the whole
   *  segment — feet sank on convex ground, floated on concave). */
  private readonly versonaAnim = new Map<string, VersonaAnimEntry>();
  private clearFrame = 0;
  /** Rubber-band governor counters (probe-visible via __versonaLife.gov). */
  private govClamped = 0;
  private govMaxRaw = 0;
  /** Ground sampler bound to the versonas' world at update time — routes
   *  Commons → meshHeight, personal worlds → personalGroundY (the old direct
   *  meshHeight call floated NPCs by the erosion delta off-Commons). */
  private versonaGround: ((x: number, z: number) => number) | null = null;
  /** Living-events wave: versonas BattleModule draws as full bodies (weapon, health bar) — the crowd skips them. */
  readonly hiddenIds = new Set<string>();
  /** Pilot versonas airborne in war_air_unit rows — WarAirModule owns this set
   *  (separate from hiddenIds, which BattleModule clears wholesale). */
  readonly airHiddenIds = new Set<string>();
  /** Living-events wave: where an attendee faces (the podium, the altar, the fire) — by versona id. */
  readonly facing = new Map<string, { x: number; z: number }>();
  /** Merchant slice (2026-09-15): versonas at their counter, by id → 'serving' | 'handover'
   *  (AtlasMerchantModule keys it from merchant_service rows and points `facing` at the customer). */
  readonly serving = new Map<string, string>();
  /** …and the subset serving THIS client's player — their attention partner (a merchant serving
   *  another player faces that customer through `facing`, never me). */
  readonly servingMe = new Set<string>();
  /** Clearance projector bound alongside — pushes rendered bodies out of
   *  settlement pads / port hulls (Commons) + deed-structure discs (server
   *  NPC movement is geometry-blind; without this a wander offset near an
   *  anchor renders the NPC inside a tower). */
  private versonaClear: ((x: number, z: number) => { x: number; z: number } | null) | null = null;

  /** Current interpolated WORLD positions of rendered versonas (the admin
   *  inspector's click-pick reads these — crowd instancing has no per-NPC
   *  meshes to raycast). */
  versonaPositions(): Array<{ id: string; x: number; y: number; z: number }> {
    const now = performance.now();
    const out: Array<{ id: string; x: number; y: number; z: number }> = [];
    for (const [id, a] of this.versonaAnim) {
      const f = Math.min(1, (now - a.t0) / a.dur);
      let x = a.fromX + (a.toX - a.fromX) * f;
      let z = a.fromZ + (a.toZ - a.fromZ) * f;
      const c = this.versonaClear ? this.versonaClear(x, z) : null;
      if (c) { x = c.x; z = c.z; } // match the rendered (cleared) body position
      out.push({ id, x, y: this.versonaGround ? this.versonaGround(x, z) : 0, z });
    }
    return out;
  }

  /** Living-events wave: ONE versona's interpolated, cleared, grounded position (null outside the AOI window). */
  versonaPosition(id: string): { x: number; y: number; z: number; state: string } | null {
    const a = this.versonaAnim.get(id);
    if (!a) return null;
    const f = Math.min(1, (performance.now() - a.t0) / a.dur);
    let x = a.fromX + (a.toX - a.fromX) * f;
    let z = a.fromZ + (a.toZ - a.fromZ) * f;
    const c = this.versonaClear ? this.versonaClear(x, z) : null;
    if (c) { x = c.x; z = c.z; }
    return { x, y: this.versonaGround ? this.versonaGround(x, z) : 0, z, state: a.state };
  }

  /** Living-events wave: rendered ground height at a point (terrain + edits), 0 before the world binds. */
  groundAt(x: number, z: number): number {
    return this.versonaGround ? this.versonaGround(x, z) : 0;
  }

  /**
   * Living-events wave: a standalone body for a versona — the SAME avatar mesh
   * the crowd would instance (feet at the group origin), or the capsule
   * fallback. BattleModule dresses it with a weapon and a health plate.
   */
  bodyFor(id: string): THREE.Group {
    const real = this.versonaAnim.get(id)?.crowd ?? null;
    let standIn = false;
    if (real && this.standIns.size > 0) { try { standIn = this.standIns.has(BigInt(id)); } catch { /* not a versona id */ } }
    return this.bodyFromCrowd(real, standIn);
  }

  /**
   * A body from a STORED look (battle_look: archetype + civ + id) — the fallen
   * have no versona row any more, and a fighter outside the crowd window has no
   * animation slot; both used to render as the capsule forever (2026-09-16).
   * Falls back to the capsule only while the body's crowd master is streaming.
   */
  bodyForLook(archetype: string, versonaId: bigint, civ: string | null): THREE.Group {
    const crowd = this.crowdFor(archetype, versonaId, civ);
    return this.bodyFromCrowd(crowd, crowd !== null && this.standIns.has(versonaId));
  }

  /**
   * The crowd a versona's body draws from: its OWN body's once that twin has landed; until then the body is asked for
   * (wantBody) and the pre-wave body of its civilisation / archetype stands in — never a capsule for a streaming twin.
   */
  private crowdFor(archetype: string, versonaId: bigint, civ: string | null): CrowdInstancer | null {
    const ideal = avatarFor(archetype, versonaId, civ);
    const own = this.crowds.get(ideal);
    // A twin that has LANDED is still hidden while it warms (warmThenShow): handed out at once, its wearers left the
    // visible stand-in and were not drawn until the reveal. It is theirs once shown; the reveal re-packs the crowd.
    if (own && !warmPending(own.mesh)) { if (this.standIns.size > 0) this.standIns.delete(versonaId); return own; }
    const base = this.crowds.get(baseAvatarFor(archetype, versonaId, civ)) ?? this.crowds.get(baseAvatarFor(archetype, versonaId)) ?? null;
    if (own && (!base || base === own || warmPending(base.mesh))) return own; // nothing visible to stand in: a pre-wave body is its own
    if (!own) this.wantBody(ideal);
    if (this.standIns.size > 4096) this.standIns.clear(); // a diagnostic, never a ledger
    this.standIns.set(versonaId, ideal);
    return base;
  }
  /** Re-pack the crowd once a body's mesh is revealed (several reveals in one frame are one re-pack). */
  private repackAfterWarm(): void {
    if (this.warmRepack) return;
    this.warmRepack = true;
    setTimeout(() => { this.warmRepack = false; if (atlas.conn) this.refresh(); }, 0);
  }
  private warmRepack = false;
  /** Ask for a wave-4 body's twin (once): the loader runs another pass for it. */
  private wantBody(key: string): void {
    if (this.bodyWanted.has(key) || BASE_AVATAR_KEYS.includes(key)) return;
    this.bodyWanted.add(key);
    this.npcSwapped = false;
    void this.swapNpcMesh();
  }
  /** The twins to hold: the pre-wave bodies always, a wave-4 body once someone here wears it. */
  private bodyKeysToLoad(): string[] { return [...BASE_AVATAR_KEYS, ...this.bodyWanted]; }

  private bodyFromCrowd(real: CrowdInstancer | null, standIn = false): THREE.Group {
    const crowd = real ?? this.crowd;
    const g = new THREE.Group();
    if (crowd) {
      const m = new THREE.Mesh(crowd.mesh.geometry, crowd.mesh.material);
      m.position.y = crowd.yOffset;
      m.castShadow = true;
      g.add(m);
    } else {
      const m = new THREE.Mesh(this.npcGeo, this.npcMat);
      m.position.y = 0.9;
      g.add(m);
    }
    // (war rework) mark the capsule fallback so battle bodies can UPGRADE to
    // the real civ avatar once it exists — the one-shot snapshot was why
    // soldiers rendered as pills forever outside the 400 m crowd window.
    // Before the pre-wave twins land a versona is packed in the CAPSULE instancer, which is not null: its one-shot body
    // was a pill that said "real", and nothing ever upgraded it. A body cut from the capsule crowd is a capsule.
    g.userData.capsule = real === null || real === this.crowd;
    // A one-shot body cut from a STAND-IN crowd (its own twin still streaming) said "done": a fighter kept the pre-wave
    // body for the whole battle while everyone around it changed. Marked, so the owner upgrades it like a capsule.
    g.userData.standIn = standIn;
    // The body's geometry + material ARE the live crowd's (or the module's
    // capsule): stamp them shared at the source so any consumer that hands
    // the body to deferDisposeTree (SkyShip disembark) frees only its own.
    markShared(g);
    return g;
  }
  private avatarsLoading = false;                // guard against concurrent avatar-load passes
  private npcSwapped = false;
  /** Wave-4 bodies a versona in the area wears (their twins are fetched on demand), and the fetches that failed. */
  private bodyWanted = new Set<string>();
  private bodyTries = new Map<string, number>();
  /** Versonas drawn in a stand-in while their own body streams (id → the body they will wear) — the diagnostic's count. */
  private standIns = new Map<bigint, string>();
  /** Baked-geometry bounds per avatar crowd (the __versonaCrowds diagnostic). */
  private readonly crowdDims = new Map<string, { minY: number; height: number }>();
  private foliage: StaticInstancer | null = null; // instance_group scatter → ONE batch per asset
  private foliageSig = '';
  private foliageReleases: Array<() => void> = []; // held asset refs (one release closure per scatter load — Law 10a: the VARIANT key was acquired, `dispose(id)` released the canonical one; 2026-09-12)
  private foliageMats: THREE.Material[] = []; // per-group wind-material clones (we own + dispose these)

  // ── Commons chunk streamer state ──────────────────────────────────────────────
  /** Currently loaded chunks: key → mesh */
  private chunkMeshes = new Map<string, THREE.Mesh>();
  /** The window of chunks loaded as of the last frame. */
  private prevWindow: ChunkKey[] = [];
  // Last chunk cell the window was computed for (NaN ⇒ recompute) — see chunkTick.
  private lastWindowPcx = Number.NaN;
  private lastWindowPcz = Number.NaN;
  /** Shared biome material (created once when all 6 textures load). */
  private biomeMat: THREE.MeshStandardMaterial | null = null;
  /** The shared biome ground material once its textures landed (null before) — the terrain far tier wears it too. */
  get biomeMaterial(): THREE.MeshStandardMaterial | null { return this.biomeMat; }
  /** Pending per-frame build queue (added chunks not yet built). */
  private chunkBuildQueue: ChunkKey[] = [];
  // ── Wave-2 worker offload: a NEAR chunk costs ~35 ms of noise eval (measured) —
  // generation runs in a Web Worker; the main thread only assembles (~1-2 ms).
  private chunkWorker: Worker | null = null;
  private chunkWorkerBroken = false; // Worker unavailable (harness/jsdom) or layout mismatch → sync fallback
  private chunkWorkerEverBeat = false; // any onmessage received — separates slow BOOT from a mid-session stall
  private chunkWorkerRespawns = 0;     // one respawn before the permanent sync fallback
  private readonly chunkPending = new Map<string, ChunkKey & { job?: number }>(); // key → job in flight (+ its request id)
  /**
   * Resident terrain LOD: key → the seg count the chunk's CURRENT mesh was
   * built with. Chunks used to keep whatever seg they were first requested
   * with until they left the window, so a chunk that streamed in at the window
   * edge stayed at seg 24/10 even when the player later stood on it — and
   * cave mouths, which are only cut on the seg-48 path, never appeared.
   * See desiredChunkSeg / refineChunkSeg.
   */
  private readonly chunkSegResident = new Map<string, number>();
  private chunkWorkerBeatMs = 0; // last dispatch OR result wall-clock — the stall watchdog's heartbeat
  private chunkResults: Array<{ key: string; seg: number; job?: number; heights: Float32Array; biomeWA: Float32Array; biomeWB: Float32Array; roadW?: Float32Array; fieldW?: Float32Array; ground?: Float32Array }> = [];
  /** Ground layout generation each in-flight job was requested for (a reply from before a v1 ↔ v2 flip is rebuilt). */
  private readonly chunkPendingGen = new Map<string, number>();
  /** Monotonic chunk-request id (see requestChunk / assembleChunk). */
  private chunkJobSeq = 0;
  /** Per-chunk foliage: key → THREE.Group holding all scatter instances for that chunk. */
  private chunkFoliage = new Map<string, THREE.Group>();
  // Cross-chunk foliage consolidation (2026-07-21): ONE global instancer batch
  // per species instead of ~4 InstancedMeshes PER CHUNK (~200 draw items → ~8;
  // three r182 pays per-object UBO cost, so the object count IS the frame).
  // The per-chunk Group in `chunkFoliage` stays as the mid-scatter liveness
  // token; instances live in the shared instancer, keyed here for disposal.
  private chunkFoliageInstancer: StaticInstancer | null = null;
  private chunkFoliageHandles = new Map<string, number[]>();
  /** Per handle of `chunkFoliageHandles`, its instance's (x, y, z) — THE GROUND KEEP-OUT removes the ones a building
   *  that assembled after the scatter now stands over (lib/ground_keepout.ts, 2026-10-02). A removed handle reads −1. */
  private chunkFoliageHandleXYZ = new Map<string, number[]>();
  /** The ground keep-out's version the resident scatter was last filtered against. */
  private keepOutV = 0;
  private keepOutCheckAt = 0;
  /** Instances the keep-out removed after their scatter, flora chunks re-planted, chunks queued to grow back (`__foliageCensus`). */
  private readonly keepOutStats = { removed: 0, floraReplanted: 0, regrow: 0, changes: 0 };
  /** Chunks whose flora re-plant / regrow waits for the keep-out to go quiet (a settlement registers building by building). */
  private readonly keepOutFloraDue = new Map<string, number>();
  private readonly keepOutRegrowDue = new Map<string, number>();
  /** The world foliage groups' instances (id, x, y, z) — the same after-the-fact keep-out (`scatterGroup`). */
  private worldFoliageXYZ: number[] = [];
  /** The grass layer's host (visual-realism R2, 2026-09-25) — created in init, driven from updateFoliageDrivers. */
  private grassDriver: GrassDriver | null = null;
  private lastTrampleMs = 0;
  /** Geology + understory host (visual-realism R3/R6) — created in init, fed from scatterChunkFoliage. */
  private floraScatter: FloraScatter | null = null;
  /** Stand impostors from the ring-2 edge to the horizon (visual-realism R4b). */
  private forestHorizon: ForestHorizon | null = null;
  private readonly standSpecies = new Map<string, { raw: number } | 'loading' | 'failed'>();
  /** vegetation (2026-09-26): stand species key → asset id (undefined = not in the catalog), and the catalog size it was read at. */
  private readonly standAssetOf = new Map<string, bigint | undefined>();
  private standAssetRows = -1;
  private horizonTick = 0;
  /** Octahedral impostors per tree asset (perf frontier item 5): null = bake failed, never retried this session. */
  private readonly impostors = new Map<bigint, ImpostorInstancer | null>();
  private readonly chunkImpostorHandles = new Map<string, Array<{ inst: ImpostorInstancer; id: number }>>();
  /** Probe lever: species baked and billboards live. */
  impostorStats(): { species: number; failed: number; instances: number; lit: number } {
    // `lit`: species baked with the unlit albedo + WORLD-normal atlas pair (R4c — lit by the scene's sun at runtime)
    let instances = 0, failed = 0, species = 0, lit = 0;
    for (const inst of this.impostors.values()) { if (inst) { species++; instances += inst.instanceCount; if (inst.atlas.normals) lit++; } else failed++; }
    return { species, failed, instances, lit };
  }
  /** Bake (once per species) and return the impostor instancer for a tree asset, or null. */
  private impostorFor(assetId: bigint, geometry: THREE.BufferGeometry, material: THREE.Material): ImpostorInstancer | null {
    const cached = this.impostors.get(assetId);
    if (cached !== undefined) return cached;
    if (impostorsOptedOut()) { this.impostors.set(assetId, null); return null; }
    const native = this.appRef?.renderer.native as unknown as THREE.WebGPURenderer | undefined;
    if (!native) return null; // not cached: retry once the renderer exists
    try {
      const tmp = new THREE.Mesh(geometry, material);
      const atlas = bakeImpostorAtlas(native, tmp);
      const pre = activePreset();
      const inst = new ImpostorInstancer(this.scene, atlas, 16384, `tree-${assetId}`, pre.impostorBlend === true, // render wave 1: three-frame blend; 16 k: the ring-2 woods + the horizon stands (R4b)
        // surfaces (2026-09-27): §5.2 the dithered frame pick where a temporal resolve integrates it — gaps wave round 2:
        // one that RUNS (TAAU is WebGPU-only; on WebGL2 high/ultra the dither shimmered with nothing to resolve it)
        impostorDitherFor(pre.impostorBlend === true, { upscale: pre.upscale, upscaleMode: pre.upscaleMode, traa: !!pre.post.traa?.enabled, webgpu: isWebGPURenderer(native), xrPresenting: !!native.xr?.isPresenting }));
      this.impostors.set(assetId, inst);
      return inst;
    } catch (e) {
      console.warn('[Environment] impostor bake failed for', String(assetId), e);
      this.impostors.set(assetId, null);
      return null;
    }
  }
  // ONE shared wind-material clone per species — the per-chunk clones were the
  // pipeline-compile factory (and identical anyway: same wind everywhere).
  private speciesMats = new Map<string, THREE.Material>();
  /** The foliage-FX switches the cached species materials were BUILT with. */
  private builtFoliageFx: { shading: boolean; wind: boolean } | null = null;
  private readonly speciesMatSet = new Set<THREE.Material>();
  /** Shader wave stage 2: the foliage translucency + wind handles on the tree species materials. */
  private readonly foliageFx: { t: FoliageTranslucencyHandle[]; w: HierarchicalWindHandle[] } = { t: [], w: [] };
  private lastWindFxMs = 0;
  private readonly sunView = new THREE.Vector3();
  foliageFxCount(): { translucency: number; wind: number } { return { translucency: this.foliageFx.t.length, wind: this.foliageFx.w.length }; }
  /** surfaces (2026-09-27): §5.3 the translucency's albedo tint + crown mask, 0..1 each (the live A/B lever's state). */
  private readonly leafLook = { albedo: 1, crown: 1 };
  /** surfaces (2026-09-27): §5.3 per-species leaf transmission — the species key of each translucency handle, the prefab key
   *  of each tree asset (a one-time walk of the catalog per asset), and the live lever's overrides (key substring → k). */
  private readonly leafKeyOf = new Map<FoliageTranslucencyHandle, string>();
  private readonly speciesKeyCache = new Map<string, string>();
  private readonly leafOverride = new Map<string, number>();
  private speciesKeyOf(assetId: bigint): string {
    const id = String(assetId); const hit = this.speciesKeyCache.get(id); if (hit !== undefined) return hit;
    let key = ''; const conn = atlas.conn;
    if (conn) for (const pm of conn.db.prefabModel.iter()) if (pm.assetId === assetId && (!key || pm.prefabKey.startsWith('foliage:'))) key = pm.prefabKey;
    if (key) this.speciesKeyCache.set(id, key);
    return key;
  }
  private leafKFor(key: string): number {
    let k = leafTransmissionFor(key);
    for (const [sub, v] of this.leafOverride) if (sub === '' || key.includes(sub)) k = v;
    return k;
  }
  /** surfaces (2026-09-27): one crown-mask derivation per species geometry (a pass over its vertices, once). */
  private readonly crownOf = new WeakMap<THREE.BufferGeometry, CrownParams | null>();
  private crownFor(g: THREE.BufferGeometry): CrownParams | null {
    if (this.crownOf.has(g)) return this.crownOf.get(g) ?? null;
    const a = g.getAttribute('position') as THREE.BufferAttribute | undefined;
    let c: CrownParams | null = null;
    if (a && a.count >= 8) {
      // getX/Y/Z de-normalise a quantised attribute — the value the shader's positionGeometry reads
      const f = new Float32Array(a.count * 3);
      for (let i = 0; i < a.count; i++) { f[i * 3] = a.getX(i); f[i * 3 + 1] = a.getY(i); f[i * 3 + 2] = a.getZ(i); }
      c = crownParamsFromPositions(f);
    }
    this.crownOf.set(g, c);
    return c;
  }
  /**
   * Per-chunk asset ref bookkeeping for the Commons foliage path.
   * Each scatterChunkFoliage call pushes the assetIds it holds here (keyed by chunk key).
   * disposeChunkFoliage releases them via this.assets.dispose() so the G5 LRU can reclaim them.
   * (The non-Commons buildFoliage path uses the shared foliageReleases[] instead.)
   */
  private chunkFoliageRefs = new Map<string, { id: bigint; v: boolean }[]>();
  /** Ring the chunk's foliage was scattered for: 0 = near (full detail), 1 = far (LOD1 trees, no undergrowth). */
  private chunkFoliageRing = new Map<string, FoliageRing>();
  // ── THE WOODS (flora update 2026-09-07): forest trees per chunk (for the trunk colliders),
  // their asset releases, the near-ring trunk colliders, and the per-species trunk proxies.
  private chunkForestTrees = new Map<string, ForestTreeRec[]>();
  private chunkForestReleases = new Map<string, Array<() => void>>();
  private forestColliders = new Map<string, string>(); // collider id → chunk key
  private trunkProxies = new Map<string, ColliderProxy | null>();
  private trunkProxyReleases: Array<() => void> = [];
  private lastForestColliderAt = 0;
  readonly forestStats = { trees: 0, giants: 0, farTrees: 0, proxies: 0 };
  /** Whether the biome textures are still loading. */
  private biomeTexLoading = false;
  /** Commons seed captured for the per-frame tick. */
  private commonsSeedForChunks = '';
  /** Fallback flat material used before biome textures load. */
  private chunkFallbackMat: THREE.MeshStandardMaterial | null = null;
  /** Trailing-debounce handle for the row-storm environment ping (see bind). */
  private pingTimer: number | null = null;
  private onBazaarKey: ((e: KeyboardEvent) => void) | null = null;
  private busOffs: Array<() => void> = [];
  /** Foliage scatter passes in flight (loading-screen readiness signal). */
  private foliagePending = 0;

  /** Boot loading screen: terrain/foliage streaming progress on the Commons.
   *  chunksTarget is 0 until the streamer's first window is computed. */
  loadReadiness(): { chunksBuilt: number; chunksTarget: number; chunksPending: number; foliagePending: number } {
    return {
      chunksBuilt: this.chunkMeshes.size,
      chunksTarget: this.prevWindow.length,
      chunksPending: this.chunkPending.size + this.chunkBuildQueue.length,
      foliagePending: this.foliagePending,
    };
  }

  constructor(
    private readonly assets: AssetModule,
    private readonly culling: RenderCullingModule,
    private readonly physics: PhysicsModule | null = null,
  ) {}

  private app: App | null = null;

  private appRef: App | null = null;

  init(app: App): void {
    this.appRef = app;
    this.app = app;
    this.scene = app.world.scene;
    this.scene.add(this.versonaRoot); // the sun + sky now live in LightingModule (G3)
    this.scene.add(this.crowdAnchor.group);
    // The bazaar terminal key: B within 7 m of the market slot's pedestal.
    this.onBazaarKey = (e: KeyboardEvent): void => {
      if (e.code !== 'KeyB' || e.repeat) return;
      const t = e.target;
      if (t instanceof Element && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || (t as HTMLElement).isContentEditable)) return;
      if (atlas.spaceFlight) return; // B is the boarding key in space
      const term = this.bazaarTerminal;
      if (!term || !term.visible) return;
      if (Math.hypot(term.position.x - atlas.localX, term.position.z - atlas.localZ) > 7) return;
      const crafting = this.app?.getModule('AtlasCrafting') as { openBazaar?(): void } | null;
      crafting?.openBazaar?.();
    };
    window.addEventListener('keydown', this.onBazaarKey);
    // The crowd is one InstancedMesh; the RenderSub pass count-packs it by frustum each frame.
    this.crowd = new CrowdInstancer(this.npcGeo, this.npcMat, 4096, 1.5, 1, 'crowd-capsule'); // capsule is center-origin → lift half-height
    this.crowd.mesh.castShadow = true; // G3: NPCs cast CSM sun shadows
    this.crowdAnchor.group.add(this.crowd.mesh);
    this.culling.registerCrowdCuller((_camPos, planes) => this.crowd?.cull(planes));
    this.busOffs.push(bus.on('atlas:account-ready', () => void this.swapNpcMesh()));

    this.busOffs.push(bus.on('atlas:account-ready', () => {
      this.bind();
      this.refresh();
      this.warmFloraAssets();
    }));
    this.busOffs.push(bus.on('atlas:environment-updated', () => this.refresh()));
    // Attention priority (2026-09-15): the player's soft target (a drawn weapon
    // on a versona) and every landed shot feed the pose loop's selector.
    this.busOffs.push(bus.on('atlas:soft-target', (t) => { this.aimedVersona = t && t.kind === 'versona' ? t.id : null; }));
    this.busOffs.push(bus.on('atlas:impact', ({ x, z }) => this.startle(x, z, performance.now() / 1000)));
    this.busOffs.push(bus.on('atlas:world-entered', () => { this.refresh(); this.warmFloraAssets(); }));
    // Terrain flattening under enterable buildings: rebuild the chunks the new
    // stamps touch (mesh + trimesh collider follow stampedHeight).
    // Probe hook: terrain readings through THE APP'S module instance (a
    // page-side dynamic import can land on a parallel instance — measured
    // 2026-08-15: app registry 1 stamp, probe-import instance 0).
    // Foliage census (2026-09-09, "you can not make foliage density 0% and trees do not go away").
    (window as unknown as Record<string, unknown>).__foliageCensus = () => {
      let instances = 0, meshes = 0;
      for (const grp of this.chunkFoliage.values()) grp.traverse((o) => { const im = o as THREE.InstancedMesh; if (im.isInstancedMesh) { meshes++; instances += im.count; } });
      let impostors = 0; for (const ih of this.chunkImpostorHandles.values()) impostors += ih.length;
      let handles = 0; for (const h of this.chunkFoliageHandles.values()) handles += h.length;
      return { scale: activePreset().foliageDensityScale, chunks: this.chunkFoliage.size, meshes, instances, sharedInstances: handles, impostors, forestTrees: this.forestStats.trees, giants: this.forestStats.giants, colliders: this.forestColliders.size, queue: this.rescatterQueue.length, keepOut: { ...this.keepOutStats, version: this.keepOutV, floraPending: this.keepOutFloraDue.size, regrowPending: this.keepOutRegrowDue.size } };
    };
    (window as unknown as Record<string, unknown>).__terrain = {
      meshAt: (x: number, z: number): number => meshHeight(this.commonsSeedForChunks ?? '', x, z),
      analyticAt: (x: number, z: number): number => terrainHeight(this.commonsSeedForChunks ?? '', x, z), // the main thread's own landform (probes: main vs worker)
      riverSegs: (): number => riverSegmentCount(this.commonsSeedForChunks ?? ''),
      stamps: (): number => buildingStampsNear(-1e12, -1e12, 1e12, 1e12).length,
      chunks: (): number => this.chunkMeshes.size,          // resident terrain chunks (draw-distance probe)
      radius: (): number => activePreset().chunkRadius ?? 0, // tier terrain window radius (chunks)
      foliageChunks: (): number => this.chunkFoliage.size,  // chunks carrying scattered foliage
      forest: (): { chunks: number; trees: number; giants: number; farTrees: number; colliders: number; proxies: number } => ({ chunks: this.chunkForestTrees.size, ...this.forestStats, colliders: this.forestColliders.size }),
    };
    // Edit Mode terrain strokes: Commons chunks rebuild through the restamp
    // path (disc → rect), a personal world rebuilds its single heightfield.
    this.busOffs.push(bus.on('atlas:terrain-edits-changed', ({ stamps }) => {
      if (atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId) {
        bus.emit('atlas:terrain-restamp', { stamps: stamps.map((s) => ({ x: s.x, z: s.z, hw: s.r, hd: s.r, yaw: 0, h: 0 })) });
      } else {
        this.terrainMeshSeed = ''; // force buildTerrain past its unchanged gate
        this.refresh();
      }
    }));
    this.busOffs.push(bus.on('atlas:terrain-restamp', ({ stamps }) => {
      for (const [key, mesh] of [...this.chunkMeshes]) {
        const [cx, cz] = key.split(',').map(Number);
        const x0 = cx * CHUNK_SIZE, z0 = cz * CHUNK_SIZE;
        const touched = stamps.some((st) => {
          const r = Math.hypot(st.hw, st.hd) + 4;
          return st.x + r >= x0 && st.x - r <= x0 + CHUNK_SIZE && st.z + r >= z0 && st.z - r <= z0 + CHUNK_SIZE;
        });
        if (!touched) continue;
        this.scene.remove(mesh);
        disposeLater(mesh.geometry); // the frame in flight may still reference it (warp-teardown law)
        this.chunkMeshes.delete(key);
        this.chunkSegResident.delete(key);
        setChunkSeg(key, null);
        this.physics?.removeTerrainCollider(key);
        this.disposeChunkFoliage(key);
        this.chunkPending.delete(key);
        // Drop the key from the window ledger and force a window recompute:
        // requests only fire on a cell crossing, and an evicted key still in
        // prevWindow would never re-diff as "add" — the flat rebuild stalled
        // until the player happened to cross a chunk boundary.
        this.prevWindow = this.prevWindow.filter((k) => `${k.cx},${k.cz}` !== key);
        this.lastWindowPcx = Number.NaN;
        this.lastWindowPcz = Number.NaN;
      }
    }, ))
    // Ground material v2 (2026-09-25): the ground's knobs rebuild it LIVE (it used to be
    // built once per page — render.md C 2.1); a v1 ↔ v2 flip rebuilds every chunk.
    this.busOffs.push(bus.on('atlas:quality-changed', () => {
      if (!this.commonsSeedForChunks) return;
      this.ensureGroundMaterial();
      // R8a: the near-mesh density (48 ↔ 96) re-LODs ring 0 through the window diff, which only ran on a
      // chunk crossing — a standing player saw the switch do nothing for 256 m. Re-diff on the next tick.
      const nearSeg = groundKnobs().nearSeg;
      if (nearSeg !== this.appliedNearSeg) { this.appliedNearSeg = nearSeg; this.lastWindowPcx = Number.NaN; this.lastWindowPcz = Number.NaN; }
    }));
    (globalThis as unknown as Record<string, unknown>).__ground = () => ({
      version: this.groundKind, layoutGen: this.groundLayoutGen, sig: this.groundSig, building: this.groundBuildSig,
      material: (this.biomeMat?.name ?? ''), chunks: this.chunkMeshes.size,
      staleChunks: [...this.chunkMeshes.keys()].filter((k) => this.chunkGroundGen.get(k) !== this.groundLayoutGen).length,
      retired: this.retiredGroundMats.length, textures: { ...groundTextureStats }, knobs: groundKnobs(), stats: { ...groundV2Stats },
      /** The rules a resident chunk carries at its vertex nearest (x, z): 12 weights, tint, masks, the mesh slope. */
      rulesAt: (x: number, z: number) => this.groundRulesAt(x, z),
      /** Resident vertices within r of (x, z) whose MESH slope exceeds `minSlope` (the probe's cliff / bank census). */
      census: (x: number, z: number, r: number, minSlope = 0) => this.groundCensus(x, z, r, minSlope),
      /** The nearest point on a regional route (the ruts' probe leg). */
      nearestRoute: (x: number, z: number) => nearestRoutePoint(this.commonsSeedForChunks, x, z),
      /** Force the FP64 render origin (the no-swimming probe: the ground must not change with the origin). */
      setOrigin: (ox: number, oz: number) => { const sc = this.scene; sc.position.set(-ox, 0, -oz); sc.updateMatrixWorld(true); setRenderAnchor(ox, oz); },
      origin: () => ({ x: renderAnchorX(), z: renderAnchorZ() }),
      /** ground (2026-09-26): a diagnostic colour ('pom' | 'rock', lib/wetness.ts GroundV2Opts.debugView) — rebuilds the material. */
      debugView: (mode: 'off' | 'pom' | 'rock') => { this.groundDebugView = mode; this.ensureGroundMaterial(); return this.groundSigNow(); },
      /** ground (2026-09-26): flip ONE ground knob and rebuild the ground ONLY — `__gfx.set` fires atlas:quality-changed at every
       *  module (sky, post, lighting …), whose re-settle swung the whole frame (the hand too) between two ground A/B captures. */
      setKnob: (key: 'pom' | 'res' | 'triplanar' | 'microShadow' | 'hexTiling' | 'pomShadow' | 'antiTiling', v: unknown) => {
        if (key === 'pom') setGroundPom(v === 'rock' || v === 'all' ? v : 'off');
        else if (key === 'res') setGroundArrayRes(([512, 1024, 2048, 4096, 8192] as const).find((r) => r === Number(v)) ?? 1024);
        else if (key === 'triplanar') setGroundTriplanar(!!v);
        // surfaces (2026-09-27): the micro-shadow / hex-tiling / parallax-shadow A/B levers (the ground only, no apply)
        else if (key === 'microShadow') setGroundMicroShadow(!!v);
        else if (key === 'hexTiling') setGroundHexTiling(!!v);
        else if (key === 'pomShadow') setGroundPomShadow(!!v);
        else if (key === 'antiTiling') setGroundAntiTiling(!!v);
        this.ensureGroundMaterial(); return this.groundSigNow();
      },
    });
    // Tier switch: foliage density is a preset knob (the triangle whale — see
    // quality_tiers.foliageDensityScale). Re-scatter every resident chunk so
    // the Esc menu applies it live instead of only to freshly streamed chunks.
    // The trampling field (lib/foliage_trample.ts). Quality rides the tier:
    // it is one small RG16F texture, so even the cheap rung is affordable, but
    // the CPU deposit cost scales with resolution.
    const tq = activeTier();
    initTrample(tq === 'ultra' ? 'high' : tq === 'high' ? 'medium' : 'low');
    (globalThis as unknown as Record<string, unknown>).__trample = () => ({ ...trampleStats(), census: { ...this.trampleCensus } });
    // Society wave: the stations near the player, their props, and the seats this client dealt.
    (globalThis as unknown as Record<string, unknown>).__stations = () => this.stationDiag();
    // ── THE GRASS LAYER (visual-realism R2, 2026-09-25): lib/grass — vertex-procedural blades + clump cards
    // around the camera, driven every frame from updateFoliageDrivers. `__grass` is the probe lever.
    this.grassDriver = new GrassDriver({
      scene: this.scene,
      commons: () => (this.commonsSeedForChunks && atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId && !atlas.spaceFlight
        ? { seed: this.commonsSeedForChunks, sea: commonsSeaLevel(this.commonsSeedForChunks) } : null),
      tier: () => activePreset().grassLayer ?? 'off',
      chunkMesh: (cx, cz) => this.chunkMeshes.get(`${cx},${cz}`),
      chunkSeg: (cx, cz) => this.chunkSegResident.get(`${cx},${cz}`), // the turf colour reads the ground's own vertex data (grass_driver.groundAt)
      structures: (x0, z0, x1, z1) => {
        const conn = atlas.conn, wid = atlas.commonsWorldId;
        return conn && wid !== null ? structureKeepOuts(conn, wid, (x0 + x1) / 2, (z0 + z1) / 2, Math.max(x1 - x0, z1 - z0) / 2) : [];
      },
      cameraAbs: (out) => {
        const cam = this.appRef?.renderer.cameras.main as unknown as THREE.Camera | undefined;
        return cam ? out.setFromMatrixPosition(cam.matrixWorld).sub(this.scene.position) : null; // render space + origin = absolute
      },
      warm: (o) => { if (this.appRef) schedulePipelineWarm(this.appRef, o); },
      sunDir: uSunDir, night: uNightFactor,
    });
    // ── GEOLOGY + UNDERSTORY (visual-realism R3/R6): the flora scatter worker's main-thread half.
    this.floraScatter = new FloraScatter({
      scene: this.scene,
      keyToAsset: () => { const m = new Map<string, bigint>(); const c = atlas.conn; if (c) for (const pm of c.db.prefabModel.iter()) m.set(pm.prefabKey, pm.assetId); return m; },
      loadRep: (id, lod) => this.assets.loadRepresentation(id, lod),
      speciesMaterial: (id, lod, geo, mat) => this.understoryMaterial(id, lod, geo, mat),
      blocked: () => false, // replaced per job (requestFloraScatter binds the chunk's keep-outs)
      physics: this.physics ? { addStaticCollider: (i, p, t) => this.physics!.addStaticCollider(i, p, t), removeStaticCollider: (i) => this.physics!.removeStaticCollider(i) } : null,
      warm: (o) => { if (this.appRef) schedulePipelineWarm(this.appRef, o); },
      onError: (m) => console.warn(`[Environment] ${m}`),
    });
    // ── STAND IMPOSTORS (visual-realism R4b): the species' atlases are the woods' own (baked from LOD2 on first use).
    this.forestHorizon = new ForestHorizon({
      impostor: (key) => {
        const conn = atlas.conn; if (!conn) return null;
        // vegetation (2026-09-26): called once per stand ROW of a job (~10k) — the key → asset lookup is memoised per species
        // (a whole-table scan each call), dropped when the catalog's row count moves
        const nRows = Number(conn.db.prefabModel.count());
        if (nRows !== this.standAssetRows) { this.standAssetOf.clear(); this.standAssetRows = nRows; }
        let assetId = this.standAssetOf.get(key);
        if (assetId === undefined && !this.standAssetOf.has(key)) { for (const pm of conn.db.prefabModel.iter()) if (pm.prefabKey === key) { assetId = pm.assetId; break; } this.standAssetOf.set(key, assetId); }
        if (assetId === undefined) return null;
        const hit = this.impostors.get(assetId);
        // vegetation (2026-09-26): the stand's billboard scale needs the species' raw height (`rawHeight`) whichever path baked
        // the atlas — returning the woods' instancer early left the height unknown for ever, and every stand of a species the
        // woods had baked first sat "pending" (WebGPU: 943 seated, 9,912 waiting; WebGL2 won the race by luck).
        if (hit !== undefined && this.standSpecies.has(key)) return hit;
        if (!this.standSpecies.has(key)) {
          this.standSpecies.set(key, 'loading');
          void this.assets.loadRepresentation(assetId, 2).then((r) => {
            const m = firstMesh(r.object);
            if (!m) { this.standSpecies.set(key, 'failed'); r.release(); return; }
            m.geometry.computeBoundingBox(); const bb = m.geometry.boundingBox!;
            this.standSpecies.set(key, { raw: Math.max(0.2, bb.max.y - bb.min.y) });
            this.impostorFor(assetId!, m.geometry, m.material);
            this.trunkProxyReleases.push(r.release); // held for the session like the trunk proxies
          }).catch(() => this.standSpecies.set(key, 'failed'));
        }
        return null;
      },
      rawHeight: (key) => { const s = this.standSpecies.get(key); return s && typeof s === 'object' ? s.raw : null; },
    });
    // R9: the season / dryness tint on every foliage material (trees, understory, impostors) and the grass turf.
    (globalThis as unknown as Record<string, unknown>).__seasonTint = (r: number, g: number, b: number) => { setSeasonTint(r, g, b); this.grassDriver?.layer?.setTint(r, g, b); return [r, g, b]; };
    (globalThis as unknown as Record<string, unknown>).__horizon = () => ({ ...(this.forestHorizon?.stats ?? {}), reach: activePreset().forestHorizonM ?? 0, species: [...this.standSpecies.entries()].map(([k, v]) => `${k.replace('foliage:', '')}:${typeof v === 'object' ? 'ready' : v}`), impostors: this.impostorStats(), bearings: this.forestHorizon?.bearings(atlas.localX, atlas.localZ) ?? null });
    // vegetation (2026-09-26): the baked atlases themselves, for a probe to read back and LOOK at (a counter is not a picture)
    (globalThis as unknown as Record<string, unknown>).__impostorAtlases = () => [...this.impostors.entries()].flatMap(([id, inst]) => (inst ? [{ id: String(id), atlas: inst.atlas }] : []));
    // surfaces (2026-09-27): §5.2 the impostor card's depth offset A/B lever (1 = the baked depth, 0 = the flat card)
    (globalThis as unknown as Record<string, unknown>).__impostorDepth = (on: boolean) => { setImpostorDepth(on); return on; };
    // surfaces (2026-09-27): the groundcover card-normal bend A/B lever (0 = the cooked normals exactly)
    (globalThis as unknown as Record<string, unknown>).__cardBend = (k?: number) => { if (typeof k === 'number' && Number.isFinite(k)) uCardBend.value = Math.min(2, Math.max(0, k)); return uCardBend.value; };
    // surfaces (2026-09-27): §5.3 the leaf-translucency A/B lever — albedo (0 = the old flat tint) and crown (0 = no mask)
    (globalThis as unknown as Record<string, unknown>).__leafLook = (o?: { albedo?: number; crown?: number }) => {
      if (o?.albedo !== undefined) this.leafLook.albedo = Math.min(1, Math.max(0, o.albedo));
      if (o?.crown !== undefined) this.leafLook.crown = Math.min(1, Math.max(0, o.crown));
      for (const h of this.foliageFx.t) { h.albedoTint.value = this.leafLook.albedo; h.crownMix.value = this.leafLook.crown; } // (a mask-less material ignores crownMix)
      return { ...this.leafLook, handles: this.foliageFx.t.length };
    };
    // surfaces (2026-09-27): §5.3 the per-species leaf-thickness lever — `__leafThickness({ key: 'pine', k: 0.5 })` overrides every
    // species whose key contains `key` ('' = all), `{ key, k: null }` clears it; returns each handle's species, group, k, back.
    (globalThis as unknown as Record<string, unknown>).__leafThickness = (o?: { key?: string; k?: number | null }) => {
      if (o && typeof o.key === 'string') { if (o.k === null || o.k === undefined || !Number.isFinite(o.k)) this.leafOverride.delete(o.key); else this.leafOverride.set(o.key, Math.min(3, Math.max(0, o.k))); }
      const rows: Array<{ key: string; group: string; k: number; back: number }> = [];
      for (const h of this.foliageFx.t) { const key = this.leafKeyOf.get(h) ?? ''; const k = this.leafKFor(key); h.backIntensity.value = 0.55 * k; rows.push({ key, group: leafGroupOf(key), k, back: h.backIntensity.value }); }
      return rows;
    };
    // surfaces (2026-09-27): the forest trees standing within r of an ABSOLUTE point (the §5.3 calibration probe picks a tree by species)
    (globalThis as unknown as Record<string, unknown>).__forestTreesNear = (x: number, z: number, r = 150) => {
      const out: Array<{ key: string; x: number; y: number; z: number; h: number; cls: string }> = [];
      for (const recs of this.chunkForestTrees.values()) for (const t of recs) if (Math.hypot(t.x - x, t.z - z) <= r) out.push({ key: this.speciesKeyOf(t.assetId), x: t.x, y: t.y, z: t.z, h: t.targetH, cls: t.cls });
      return out;
    };
    (globalThis as unknown as Record<string, unknown>).__geology = {
      stats: () => ({ ...(this.floraScatter?.stats ?? {}), chunks: this.floraScatter?.chunkStats() ?? [] }),
      near: (x: number, z: number, r = 60) => this.floraScatter?.piecesNear(x, z, r) ?? [],
      colliders: () => this.floraScatter?.colliderIds().length ?? 0,
    };
    // THE GROUND KEEP-OUT's probe lever (2026-10-02): what still grows inside an interior's box — the grass field's texels
    // with density, the chunk ground cover's live instances, the world foliage groups' — and the after-the-fact counters.
    (globalThis as unknown as Record<string, unknown>).__groundKeepOut = {
      stats: () => ({ ...this.keepOutStats, version: this.keepOutV, floraPending: this.keepOutFloraDue.size, regrowPending: this.keepOutRegrowDue.size, off: groundKeepOutOff() }),
      inside: (id: string, inset = 0.3) => {
        const b = enterables.get(id); if (!b) return null;
        const c = Math.cos(b.yaw), sn = Math.sin(b.yaw), hw = b.w / 2 - inset, hd = b.d / 2 - inset;
        const isIn = (x: number, z: number): boolean => { const dx = x - b.x, dz = z - b.z; const lx = dx * c - dz * sn, lz = dx * sn + dz * c; return Math.abs(lx) <= hw && Math.abs(lz) <= hd; };
        let grassTexels = 0, grassSampled = 0, plants = 0, worldPlants = 0;
        const f = this.grassDriver?.layer?.field;
        if (f) for (let u = -hw; u <= hw; u += 0.5) for (let v = -hd; v <= hd; v += 0.5) {
          const x = b.x + u * c + v * sn, z = b.z - u * sn + v * c;
          const d = f.sampleAt(x, z); if (!d) continue; grassSampled++; if (d.density > 0.02) grassTexels++;
        }
        for (const [key, h] of this.chunkFoliageHandles) { const xyz = this.chunkFoliageHandleXYZ.get(key); if (!xyz) continue; for (let i = 0; i < h.length; i++) if (h[i] >= 0 && isIn(xyz[3 * i], xyz[3 * i + 2])) plants++; }
        const W = this.worldFoliageXYZ; for (let i = 0; i < W.length; i += 4) if (W[i] >= 0 && isIn(W[i + 1], W[i + 3])) worldPlants++;
        return { id, w: b.w, d: b.d, grassSampled, grassTexels, plants, worldPlants };
      },
      /** Probe: re-grow the ground within `r` m of (x, z) under the current rule (the lever just flipped) — the grass
       *  window refills and those chunks re-scatter first. Returns the chunks queued. */
      refreshNear: (x: number, z: number, r = 160): number => {
        this.grassDriver?.layer?.field.invalidate(x - r, z - r, x + r, z + r);
        let n = 0;
        for (const key of [...this.chunkFoliage.keys()]) {
          const [cx, cz] = key.split(',').map(Number);
          if ((cx + 0.5) * CHUNK_SIZE < x - r - CHUNK_SIZE || (cx + 0.5) * CHUNK_SIZE > x + r + CHUNK_SIZE || (cz + 0.5) * CHUNK_SIZE < z - r - CHUNK_SIZE || (cz + 0.5) * CHUNK_SIZE > z + r + CHUNK_SIZE) continue;
          if (!this.rescatterQueue.includes(key)) { this.rescatterQueue.unshift(key); n++; }
        }
        return n;
      },
      queue: () => this.rescatterQueue.length,
    };
    (globalThis as unknown as Record<string, unknown>).__grass = {
      stats: () => ({ ...(this.grassDriver?.layer?.stats ?? { tier: 'off' }), field: this.grassDriver?.layer?.field.stats ?? null, visible: this.grassDriver?.layer?.isVisible ?? false, speed: this.grassDriver?.cameraSpeed ?? 0, diag: this.grassDriver?.diag ?? null, chunks: this.chunkMeshes.size, player: { x: atlas.localX, z: atlas.localZ } }),
      sample: (x: number, z: number) => ({ field: this.grassDriver?.layer?.field.sampleAt(x, z) ?? null, mesh: meshHeight(this.commonsSeedForChunks ?? '', x, z) }),
      setDensity: (v: number) => this.grassDriver?.setDensity(v),
      budget: (ms: number) => { if (this.grassDriver) this.grassDriver.budgetMs.value = ms; },
      objects: () => this.grassDriver?.layer?.objects ?? [],
      windMin: (v: number) => this.grassDriver?.layer?.setWindMin(v),
      /** turf ↔ grass (session 2): the ground's channel off the chunk mesh, the one rule's colour, the field's baked colour. */
      turf: (x: number, z: number) => this.grassDriver?.turfAt(x, z) ?? null,
      census: () => {
        const cam = this.appRef?.renderer.cameras.main as unknown as THREE.Camera | undefined;
        const layer = this.grassDriver?.layer;
        if (!cam || !layer) return null;
        cam.updateMatrixWorld();
        const abs = new THREE.Vector3().setFromMatrixPosition(cam.matrixWorld).sub(this.scene.position);
        return layer.census(cam, abs, { x: renderAnchorX(), z: renderAnchorZ() }, (x, z) => meshHeight(this.commonsSeedForChunks ?? '', x, z));
      },
    };

    this.busOffs.push(bus.on('atlas:quality-changed', () => {
      // A foliage-FX switch changes the MATERIAL, and species materials are
      // built once and cached — a re-scatter alone would hand the new trees
      // the old shaders, so the menu toggle looked dead until a world reload
      // (2026-09-14). Drop the cache first when either switch moved.
      const b = this.builtFoliageFx;
      if (b && (b.shading !== (activePreset().foliageShading !== false) || b.wind !== foliageWindOn())) {
        this.flushSpeciesMaterials();
      }
      this.rescatterFoliage();
    }));

    // Per-frame chunk streamer tick: runs in the RenderSub phase after culling so
    // newly built chunks are visible in the same frame. Priority 35 — after LOD@10
    // and before shadow prep. Builds at most 4 chunks per frame to avoid hitching.
    app.world.addSystem(makeSystem('CommonsChunkStreamer', Phase.RenderSub, 35, () => this.chunkTick()));
    // NPC AI 7a: per-frame Versona glide (interpolates the discrete authoritative steps).
    app.world.addSystem(makeSystem('VersonaSmooth', Phase.Animation, 38, () => {
      // Foliage first and unconditionally: renderVersonaFrame returns early
      // with no animated versonas, and the trees must keep moving regardless.
      this.updateFoliageDrivers();
      this.renderVersonaFrame();
    }));
  }

  /**
   * Load each unique avatar GLB into its OWN CrowdInstancer (one draw call per
   * avatar type). Idempotent + retryable: called on account-ready AND when the
   * prefab_model subscription applies, so it succeeds whenever the catalog lands.
   * Any archetype whose avatar fails to load falls back to the capsule crowd.
   */
  private civCache = new Map<string, string>(); // versonaId → 'a' | 'b'
  private civSites: ReturnType<typeof settlementSites> | null = null;

  /** A versona's civilization = its nearest settlement's pack (deterministic
   *  from the seed; cached per versona — homes never change packs). */
  private civOf(versonaId: bigint, x: number, z: number): string | null {
    const k = versonaId.toString();
    const hit = this.civCache.get(k);
    if (hit) return hit;
    const seed = this.commonsSeedForChunks;
    if (!seed) return null;
    this.civSites ??= settlementSites(seed);
    let best: { siteSeed: number } | null = null;
    let bd = Infinity;
    for (const s of this.civSites) {
      const d = (s.centerX - x) * (s.centerX - x) + (s.centerZ - z) * (s.centerZ - z);
      if (d < bd) { bd = d; best = s; }
    }
    if (!best) return null;
    // ROW-FIRST (conquest law): the nearest site's settlement_civ row wins —
    // a conquered town's people dress in the conqueror's colors over time
    // (existing cached bodies keep their look until the cache clears).
    const bestSite = best as { siteSeed: number; id?: number };
    const civ = String(bestSite.id !== undefined
      ? civOfSettlementRowC(BigInt(bestSite.id + 1), bestSite.siteSeed)
      : civilizationOf(bestSite.siteSeed));
    this.civCache.set(k, civ);
    return civ;
  }

  /** Rig wave: a body's RIGGED twin (`avatar-rig:<key>`, Meshy rig + the shared
   *  clip set, wave 2: the full ~290k mesh with the rig transferred on) feeds
   *  the near-skinned layer. Loaded AFTER the crowd, one body at a time with a
   *  yield between, never awaited by the crowd swap; a body whose rig is not
   *  registered (or fails) keeps drawing its far twin. */
  private rigLoading = false;
  /** Rig masters that failed to load (three tries each, then the body keeps drawing its far twin). */
  private rigTries = new Map<string, number>();
  /** The avatar catalogue as last read (prefab key → asset id) and when the frame loop last asked for a master. */
  private avatarCatalog: Map<string, bigint> | null = null;
  private rigPokeAt = 0;
  private async loadRigMasters(keyToAsset: Map<string, bigint>): Promise<void> {
    if (this.rigLoading) return;
    this.rigLoading = true;
    try {
      const pending = (k: string): boolean => this.crowds.has(k) && !this.rigLayer.hasMaster(k)
        && keyToAsset.has(`avatar-rig:${k.slice('avatar:'.length)}`) && (this.rigTries.get(k) ?? 0) < 3;
      for (;;) {
        // NEAREST FIRST: the planner reports which bodies the versonas around the player are
        // wearing without a master; those stream before the catalogue order (a 38 MB master each).
        // A WAVE-4 master loads ONLY when someone near wears it (versona wave 4): thirty masters held for
        // a session would be three times the old ten — the frame loop asks again when one comes near.
        const key = this.rigLayer.wantedKeys().find(pending) ?? BASE_AVATAR_KEYS.find(pending);
        if (key === undefined) break;
        const rigId = keyToAsset.get(`avatar-rig:${key.slice('avatar:'.length)}`);
        if (rigId === undefined) break;
        try {
          const rig = await this.assets.load(rigId);
          if ((rig.animations ?? []).length > 0) this.rigLayer.setMaster(key, rig);
          else this.rigTries.set(key, 3); // a rig with no clips never becomes one: stop asking
        } catch { this.rigTries.set(key, (this.rigTries.get(key) ?? 0) + 1); /* the far body still draws */ }
        await yieldToMain();
      }
    } finally { this.rigLoading = false; }
  }

  private async swapNpcMesh(): Promise<void> {
    if (this.npcSwapped || this.avatarsLoading) return;
    const conn = atlas.conn; if (!conn) return;
    this.avatarsLoading = true;
    try {
      const keyToAsset = new Map<string, bigint>();
      for (const pm of conn.db.prefabModel.iter()) keyToAsset.set(pm.prefabKey, pm.assetId);
      this.avatarCatalog = keyToAsset;
      let loadedAny = false;
      // The far bodies (3 MB posed twins) FIRST — they are the crowd. The rigged
      // twins stream in behind them (loadRigMasters): wave-2 rigs are ~38 MB /
      // 290k-vertex skins and loading ten of them here starved the crowd for
      // minutes (probe: crowds=0 while rig masters trickled in one per 40 s).
      for (const key of this.bodyKeysToLoad()) {
        if (this.crowds.has(key)) continue;            // already loaded
        if ((this.bodyTries.get(key) ?? 0) >= 3) continue; // a twin that failed three times keeps its stand-in
        const assetId = keyToAsset.get(key);
        if (assetId === undefined) continue;            // not registered yet — retry next pass
        let obj: THREE.Object3D;
        // FAR BODY LOD PER TIER (2026-09-06): the twin is the full 290k mesh on high/ultra; potato/low/med
        // draw the cooker's LOD2/LOD1 variant (loadVariant falls back to LOD0 when none is registered).
        const farLod = activePreset().farBodyLod ?? 0;
        try { obj = farLod > 0 ? await this.assets.loadVariant(assetId, farLod) : await this.assets.load(assetId); } catch { this.bodyTries.set(key, (this.bodyTries.get(key) ?? 0) + 1); continue; } // keep fallback
        const m = firstMesh(obj);
        if (!m) { this.bodyTries.set(key, (this.bodyTries.get(key) ?? 0) + 1); continue; } // a file with no mesh never becomes one: count it, or the retry pass spins
        // A build that THROWS after its load counted no try: the pass rejected and `finally` re-armed one every 400 ms for
        // as long as the body was wanted. Counted here, forgiven when the body lands.
        this.bodyTries.set(key, (this.bodyTries.get(key) ?? 0) + 1);
        // Bake the mesh NODE transform into the instanced geometry (waist-deep
        // burial fix 2026-08-05): the 2026-07 avatar batch is KHR-quantized —
        // int16 positions spanning ±1 with the dequantize (scale ~0.949,
        // translate y +0.95) carried on the NODE. Instancing the raw geometry
        // dropped it, rendering those bodies centered at the waist. Quantized
        // attributes must be expanded to float32 BEFORE applyMatrix4 — writing
        // metre-scale values back into a normalized int16 attribute clamps at
        // ±1 and shreds the mesh. yOffset then absorbs any residual foot
        // offset (≈0 for the old feet-at-origin batch).
        obj.updateMatrixWorld(true);
        let geo = m.geometry;
        if (!m.mesh.matrixWorld.equals(new THREE.Matrix4())) {
          geo = geo.clone();
          for (const name of ['position', 'normal'] as const) {
            const a = geo.getAttribute(name) as THREE.BufferAttribute | undefined;
            if (a && a.normalized) {
              const f = new Float32Array(a.count * a.itemSize);
              for (let i = 0; i < a.count; i++) {
                for (let k = 0; k < a.itemSize; k++) f[i * a.itemSize + k] = a.getComponent(i, k);
              }
              geo.setAttribute(name, new THREE.BufferAttribute(f, a.itemSize));
            }
          }
          geo.applyMatrix4(m.mesh.matrixWorld);
        }
        geo.computeBoundingBox();
        const lift = geo.boundingBox ? -geo.boundingBox.min.y : 0;
        this.crowdDims.set(key, {
          minY: geo.boundingBox?.min.y ?? 0,
          height: geo.boundingBox ? geo.boundingBox.max.y - geo.boundingBox.min.y : 0,
        });
        const crowd = new CrowdInstancer(geo, m.material, 1024, 2, lift, 'crowd-avatar'); // r=2 covers the 1.9 m body
        crowd.mesh.userData.indoorSource = m.material; // (slice H) the indoor twin is cut from the SOURCE, not the crowd's motion clone
        crowd.mesh.castShadow = true;
        this.crowdAnchor.group.add(crowd.mesh);
        if (this.appRef) void warmThenShow(this.appRef, crowd.mesh).then(() => this.repackAfterWarm()); // R4: the census showed crowd avatars linking at first draw
        this.culling.registerCrowdCuller((_camPos, planes) => crowd.cull(planes));
        this.crowds.set(key, crowd);
        this.crowdKeys.set(crowd, key);
        this.bodyTries.delete(key); // landed
        loadedAny = true;
        // SCREEN-SPACE LOD FOR THE FAR BODIES (perf frontier 2026-09-08, item 4):
        // on the tiers that draw the full 290k mesh up close, a second
        // instancer carries the cooker's LOD2 (~45k) and every body beyond
        // NEAR_BODY_M is stamped into it instead — at 28 m a 1.8 m body spans
        // ~120 px, where 45k triangles are already under a third of a pixel
        // each. The near mesh is untouched ("we want 300k vertices").
        if (farLod === 0) {
          try {
            const farObj = await this.assets.loadVariant(assetId, 2);
            const fm = firstMesh(farObj);
            if (fm && fm.geometry !== geo) {
              farObj.updateMatrixWorld(true);
              let fgeo = fm.geometry;
              if (!fm.mesh.matrixWorld.equals(new THREE.Matrix4())) {
                fgeo = fgeo.clone();
                for (const name of ['position', 'normal'] as const) {
                  const a = fgeo.getAttribute(name) as THREE.BufferAttribute | undefined;
                  if (a && a.normalized) {
                    const f = new Float32Array(a.count * a.itemSize);
                    for (let i = 0; i < a.count; i++) for (let k = 0; k < a.itemSize; k++) f[i * a.itemSize + k] = a.getComponent(i, k);
                    fgeo.setAttribute(name, new THREE.BufferAttribute(f, a.itemSize));
                  }
                }
                fgeo.applyMatrix4(fm.mesh.matrixWorld);
              }
              fgeo.computeBoundingBox();
              const farLift = fgeo.boundingBox ? -fgeo.boundingBox.min.y : 0;
              const far = new CrowdInstancer(fgeo, fm.material, 1024, 2, farLift, 'crowd-avatar-far');
              far.mesh.castShadow = true;
              this.crowdAnchor.group.add(far.mesh);
              if (this.appRef) void warmThenShow(this.appRef, far.mesh);
              this.culling.registerCrowdCuller((_camPos, planes) => far.cull(planes));
              this.crowdsFar.set(crowd, far);
              this.crowdKeys.set(far, key);
            }
          } catch { /* no LOD2 variant registered: the near mesh serves every distance */ }
        }
      }
      // Done once every avatar is either loaded or absent from the catalog (a fetch
      // failure on a registered key leaves npcSwapped false so a later pass retries).
      // keyToAsset.size guard: an EMPTY catalog (prefab_model subscription not yet
      // applied) must not latch "done" — it left every versona a capsule forever.
      if (keyToAsset.size > 0 && this.bodyKeysToLoad().every((k) => this.crowds.has(k) || !keyToAsset.has(k) || (this.bodyTries.get(k) ?? 0) >= 3)) this.npcSwapped = true;
      void this.loadRigMasters(keyToAsset);
      // Diagnostic surface (burial regression class): per-crowd ground lift +
      // BAKED geometry bounds. Health invariant: minY + yOffset ≈ 0 (feet land
      // on the ground whatever the source pivot) and height ≈ a 1.9 m body —
      // a quantized avatar whose node dequantize gets dropped again shows
      // height 2.0 with feet mid-body unless the lift compensates.
      (window as unknown as Record<string, unknown>).__versonaRig = () => this.rigLayer.snapshot();
      // Versona wave 4: which bodies this town asked for, which landed, who still wears a stand-in.
      (window as unknown as Record<string, unknown>).__versonaBodies = () => ({
        base: BASE_AVATAR_KEYS.length,
        wanted: [...this.bodyWanted],
        loaded: [...this.crowds.keys()],
        waiting: [...this.bodyWanted].filter((k) => !this.crowds.has(k)),
        // wanted but not in this shard's catalogue (a body never ingested here): its wearers keep their stand-in
        unregistered: [...this.bodyWanted].filter((k) => !(this.avatarCatalog?.has(k) ?? false)),
        standInBodies: [...new Set([...this.standIns.entries()].filter(([id]) => this.versonaAnim.has(id.toString())).map(([, k]) => k))],
        failed: [...this.bodyTries.entries()].filter(([, n]) => n >= 3).map(([k]) => k),
        standIns: [...this.standIns.keys()].filter((id) => this.versonaAnim.has(id.toString())).length, // of the versonas drawn now
        rigTries: Object.fromEntries(this.rigTries),
        loading: this.avatarsLoading, rigLoading: this.rigLoading, swapped: this.npcSwapped,
        bodyOf: (id: string | number | bigint): string | null => this.bodyKeyOf(String(id)),
      });
      (window as unknown as Record<string, unknown>).__versonaCrowds =
        () => [...this.crowds.entries()].map(([k, c]) => {
          const d = this.crowdDims.get(k);
          const r3 = (v: number): number => Math.round(v * 1000) / 1000;
          return { key: k, yOffset: r3(c.yOffset), minY: r3(d?.minY ?? 0), height: r3(d?.height ?? 0) };
        });
      if (loadedAny) this.refresh(); // re-pack Versonas into the per-archetype crowds
    } finally {
      this.avatarsLoading = false;
      // a body asked for while this pass ran (wantBody found the loader busy) gets its own pass
      if (!this.npcSwapped && [...this.bodyWanted].some((k) => !this.crowds.has(k) && (this.bodyTries.get(k) ?? 0) < 3)) setTimeout(() => void this.swapNpcMesh(), 400);
    }
  }

  private bind(): void {
    if (this.bound) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.bound = true;
    // Coalesce row-storm pings: versona_state alone delivers dozens of row
    // updates/second near a settlement, and every ping fans out to FIVE
    // listeners (lighting/fog/weather/water + the full refresh here). One
    // trailing emit per 300 ms burst keeps glide targets fresh — versona rows
    // step every ~3.5 s, so 100 ms pings just tripled the steady-state
    // refresh() rate (full versona-table rescans) for zero visible freshness
    // (perf sweep 2026-08-13).
    const ping = (): void => {
      if (this.pingTimer !== null) return;
      this.pingTimer = window.setTimeout(() => {
        this.pingTimer = null;
        bus.emit('atlas:environment-updated', {});
      }, 300);
    };
    // versona_state (wave-2 hot NPC positions) accessed via a shim — absent from the
    // generated bindings until the publish+regen, so include it only when present.
    const hotTbl = (conn.db as unknown as { versonaState?: unknown }).versonaState;
    const tables: unknown[] = [conn.db.terrainRegion, conn.db.skyState, conn.db.versona, conn.db.instanceGroup];
    if (hotTbl) tables.push(hotTbl);
    const routeTblPing = (conn.db as unknown as { versonaRoute?: unknown }).versonaRoute;
    if (routeTblPing) tables.push(routeTblPing); // the route ahead lands with its hot row — one ping
    for (const tbl of tables) {
      const t = tbl as { onInsert(cb: () => void): void; onUpdate(cb: () => void): void; onDelete(cb: () => void): void };
      t.onInsert(ping);
      t.onUpdate(ping);
      t.onDelete(ping);
    }
    // versona_state is AOI-scoped: AtlasNetworkModule subscribes it per cell alongside
    // player_state/world_object (100x Commons — no global NPC subscription). The event
    // bindings above still ping a refresh whenever windowed rows arrive/expire.
    // The foliage scatter reads prefab_model (props vs plants → sparse vs dense) and structure
    // (the building footprints to keep scatter OUT of). Subscribe both here and force ONE rebuild
    // once they're applied — otherwise foliage may scatter before those rows arrive (props scatter
    // dense, plants grow through buildings) because the lookups read an empty cache.
    conn.subscriptionBuilder().onApplied(() => { this.foliageSig = ''; void this.swapNpcMesh(); this.refresh(); }).subscribe(['SELECT * FROM prefab_model', 'SELECT * FROM structure', 'SELECT * FROM war_clash', 'SELECT * FROM settlement_civ', 'SELECT * FROM versona_origin' /* birth civ — names (versona_names.ts) */,
      'SELECT * FROM activity_station' /* society wave: ~10 static rows per settlement — benches, tables, the green */,
      'SELECT * FROM settlement_residue' /* the lamplighter's round (StreetLampsModule reads it): one row per staffed town */]);
    // War-skirmish awareness: toast the player the moment a clash anchors.
    try {
      const wc = (conn.db as unknown as { warClash?: { onInsert(cb: (c: unknown, row: { fallenName: string }) => void): void } }).warClash;
      wc?.onInsert((_c, row) => {
        bus.emit('atlas:status', {
          text: row.fallenName
            ? `⚔ Militias clash on the road — ${row.fallenName} has fallen. The fight rages nearby.`
            : '⚔ Militias clash on the road — steel on both sides. The fight rages nearby.',
        });
      });
    } catch { /* pre-publish server */ }
    // Conquest re-dresses the town: clear the per-versona civ cache so NEW
    // body spawns pick the flipped row (existing instances keep their look
    // until they leave and re-enter the crowd AOI — occupation takes hold
    // gradually, not as a mass outfit-swap).
    try {
      const sc = (conn.db as unknown as { settlementCiv?: { onUpdate(cb: () => void): void } }).settlementCiv;
      sc?.onUpdate(() => this.civCache.clear());
    } catch { /* pre-publish server */ }
  }

  private commons(conn: DbConnection): { worldId: bigint; seed: string } | null {
    // PK lookup once the id is resolved (refresh() runs on every 300 ms row-ping
    // burst; the world table holds one row per account — a full scan each time).
    const known = atlas.commonsWorldId !== null ? conn.db.world.worldId.find(atlas.commonsWorldId) : undefined;
    if (known) {
      precacheSettlementPlans(known.terrainSeed);
      void loadHydrology(known.terrainSeed); // baked rivers into the main-thread oracle (workers load their own)
      return { worldId: known.worldId, seed: known.terrainSeed };
    }
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') {
      // Warm settlement plans off the critical path so the first chunk build
      // near a city never generates a plan mid-frame (idempotent per seed).
      precacheSettlementPlans(w.terrainSeed);
      void loadHydrology(w.terrainSeed);
      return { worldId: w.worldId, seed: w.terrainSeed };
    }
    return null;
  }

  private refresh(): void {
    const conn = atlas.conn;
    if (!conn) return;
    // No world entered yet (the main-menu phase): build NOTHING. The commons
    // fallback below otherwise streamed terrain + versonas BEHIND the menu,
    // and the pipeline-compile backlog froze the first real world entry (the
    // personal-world freeze, 2026-07-30). atlas:world-entered re-fires this.
    if (atlas.currentWorldId === null) return;
    const commons = this.commons(conn);
    if (!commons) return;

    // Resolve the world the player is actually IN (falls back to the Commons).
    const cw = atlas.currentWorldId;
    const cworld = cw !== null ? conn.db.world.worldId.find(cw) : undefined; // PK index, not a spread+scan per ping
    const wid = cworld ? cworld.worldId : commons.worldId;
    const seed = cworld ? cworld.terrainSeed : commons.seed;

    // Space world: NO ground content. Every world row carries a terrainSeed —
    // including kind 'space' — and the personal-world fallback below happily
    // built a floating 512 m heightfield + foliage for the SECTOR's seed (the
    // "terrain floating in space near the planet" report, 2026-07-27).
    if (cworld?.kind === 'space') {
      this.clearChunks();
      if (this.terrainMesh) {
        this.scene.remove(this.terrainMesh);
        disposeLater(this.terrainMesh.geometry);
        disposeLater(this.terrainMesh.material as THREE.Material);
        this.terrainMesh = null;
        this.terrainMeshSeed = '';
        this.terrainRegionCount = -1;
      }
      this.foliageSig = '';
      this.releaseFoliage();
      this.updateVersonas(conn, wid, seed); // no versonas live in space — releases lingering crowd instances
      return;
    }

    // Merchant Hive: FLAT artificial ground — MerchantHiveModule owns the
    // plane, the shop city, and colliders. Without this branch the personal
    // fallback below would float a 512 m eroded heightfield over the hive.
    if (cworld?.kind === 'merchant') {
      this.clearChunks();
      if (this.terrainMesh) {
        this.scene.remove(this.terrainMesh);
        disposeLater(this.terrainMesh.geometry);
        disposeLater(this.terrainMesh.material as THREE.Material);
        this.terrainMesh = null;
        this.terrainMeshSeed = '';
        this.terrainRegionCount = -1;
      }
      this.foliageSig = '';
      this.releaseFoliage();
      this.updateVersonas(conn, wid, seed); // releases any lingering crowd instances
      return;
    }

    // Terrain: Commons path uses the chunk streamer; non-Commons worlds render
    // their OWN terrainSeed through the engine erosion bake (personal_terrain).
    if (atlas.currentWorldId === atlas.commonsWorldId) {
      // Commons: start the chunk streamer (lazy biome-texture load + per-frame tick).
      this.buildChunkTerrain(commons.seed);
    } else {
      // A STYLED personal world (world_preset row): PersonalPresetModule owns
      // the ground + scenery; here we only clear (the merchant-branch law —
      // else the eroded heightfield floats through the preset terrain).
      let styled = false;
      try {
        const pr = (conn.db as unknown as { worldPreset?: { worldId: { find(id: bigint): { preset: string } | null | undefined } } }).worldPreset?.worldId.find(wid);
        styled = !!pr && pr.preset !== 'blank' && cworld?.kind === 'personal';
      } catch { /* pre-publish */ }
      if (styled) {
        this.clearChunks();
        if (this.terrainMesh) {
          this.scene.remove(this.terrainMesh);
          disposeLater(this.terrainMesh.geometry);
          disposeLater(this.terrainMesh.material as THREE.Material);
          this.terrainMesh = null;
          this.terrainMeshSeed = '';
          this.terrainRegionCount = -1;
        }
        this.foliageSig = '';
        this.releaseFoliage();
      } else {
        // Personal world (or any non-Commons world): render the per-world eroded
        // 512 m heightfield and clear any lingering Commons chunks.
        this.clearChunks();
        this.buildTerrain(conn, wid, seed);
      }
    }
    this.updateVersonas(conn, wid, seed);
    // Commons foliage is now driven per-chunk by scatterChunkFoliage (called from buildOneChunk).
    // The old instance_group scatter path is retained for non-Commons worlds only.
    // The null check matters: refresh() fires on account-ready BEFORE any world
    // is entered, `null !== commonsWorldId` passed this guard, and `wid` falls
    // back to the COMMONS id (line above) — so the retired path scattered every
    // Commons instance_group row (14,009 instances / 202M triangles of legacy
    // flora, tier-immune, never released) UNDER the per-chunk foliage. This was
    // the per-tier census whale (F11) and the bulk of every tier's GPU cost.
    if (atlas.currentWorldId !== null && atlas.currentWorldId !== atlas.commonsWorldId) {
      this.buildFoliage(conn, wid, seed);
    } else if (this.foliage) {
      // Retract a boot-raced scatter the moment the Commons is the active world.
      this.foliageSig = '';
      this.releaseFoliage();
    }
  }

  /** Build (or rebuild when the region set OR the world seed changes) the heightfield mesh. */
  private buildTerrain(conn: DbConnection, worldId: bigint, seed: string): void {
    const regions = [...conn.db.terrainRegion.iter()].filter((r) => r.worldId === worldId);
    const editVer = terrainEditVersion();
    if (this.terrainMesh && regions.length === this.terrainRegionCount && this.terrainMeshSeed === seed && this.terrainEditVer === editVer) return; // unchanged
    this.terrainRegionCount = regions.length;
    this.terrainMeshSeed = seed;
    this.terrainEditVer = editVer;

    if (this.terrainMesh) {
      this.scene.remove(this.terrainMesh);
      // Deferred (warp-teardown law) AND the material: every personal-world
      // sculpt/paint stroke rebuilt the terrain and leaked one node material
      // (+ a pipeline compile) — only the geometry was ever disposed.
      disposeLater(this.terrainMesh.geometry);
      disposeLater(this.terrainMesh.material as THREE.Material);
    }
    // Terrain realism wave: personal worlds displace by the ENGINE-ERODED
    // per-seed grid (droplet hydraulic + thermal talus over the analytic
    // base — see lib/personal_terrain.ts). personalGroundY is the exact
    // sampler of this mesh, so the avatar clamp/seating agree by construction.
    const geo = new THREE.PlaneGeometry(
      PERSONAL_TERRAIN_SIZE, PERSONAL_TERRAIN_SIZE, PERSONAL_TERRAIN_SEG, PERSONAL_TERRAIN_SEG,
    );
    geo.rotateX(-Math.PI / 2); // lay flat in the XZ plane (Y up)
    const pos = geo.attributes.position as THREE.BufferAttribute;
    // paintW = [regolith, moss, dirt, mask] — the Edit Mode paint layer over
    // the noise blend (always baked: a missing attribute is an invalid pipeline).
    const paintW = new Float32Array(pos.count * 4);
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      pos.setY(i, personalGroundY(seed, x, z));
      const p = paintOverride(x, z, 3);
      if (p) { paintW[i * 4 + p.layer] = 1; paintW[i * 4 + 3] = p.mask; }
    }
    pos.needsUpdate = true;
    geo.setAttribute('paintW', new THREE.BufferAttribute(paintW, 4));
    geo.computeVertexNormals();
    const mat = new THREE.MeshStandardMaterial({ color: 0x3f5d3a, roughness: 0.95, metalness: 0 });
    applyWetness(mat); // G4 fallback: flat green ground (darkens in rain) until the textures load
    // Blend three tiled ground albedos (regolith base + moss + dirt patches) for variety + to break
    // the obvious single-tile repetition (non-blocking — flat color until they load).
    void Promise.all([
      this.assets.loadTexture(TERRAIN_GROUND_TEXTURES.regolith),
      this.assets.loadTexture(TERRAIN_GROUND_TEXTURES.moss),
      this.assets.loadTexture(TERRAIN_GROUND_TEXTURES.dirt),
    ]).then(([regolith, moss, dirt]) => {
      for (const t of [regolith, moss, dirt]) t.repeat.set(TERRAIN_TEXTURE_SCALE, TERRAIN_TEXTURE_SCALE);
      applyTerrainGroundPainted(mat, regolith, moss, dirt);
      mat.needsUpdate = true;
    }).catch(() => { /* keep the flat-color fallback */ });
    this.terrainMesh = new THREE.Mesh(geo, mat);
    this.terrainMesh.receiveShadow = true; // G3: the terrain is the main CSM shadow receiver
    this.scene.add(this.terrainMesh);
  }

  /**
   * Route each Versona into its archetype's avatar crowd — as an interpolation TARGET.
   * NPC AI 7a: authoritative positions arrive in discrete steps (external tick cadence),
   * so stamping them directly teleport-stepped every NPC. Each refresh records a
   * from→to XZ segment (movement-facing yaw); the per-frame VersonaSmooth system
   * glides the crowd matrices along it, re-seating Y on the rendered ground at
   * every interpolated position.
   */
  private updateVersonas(conn: DbConnection, worldId: bigint, seed: string): void {
    this.versonaGround = (x, z) => groundYIn(conn, worldId, x, z);
    const isCommons = worldId === atlas.commonsWorldId;
    this.labelSeed = isCommons ? seed : '';
    this.versonaClear = (x, z) => {
      const s = isCommons ? settlementClearance(seed, x, z) : null;
      if (s) { x = s.x; z = s.z; }
      const d = structureClearance(x, z);
      if (d) return d;
      return s ? { x, z } : null;
    };
    interface HotRow { versonaId: bigint; curX: number; curZ: number; state: string }
    const hotTbl = (conn.db as unknown as { versonaState?: { iter(): Iterable<HotRow> } }).versonaState;
    const hot = new Map<string, HotRow>();
    if (hotTbl) for (const h of hotTbl.iter()) hot.set(h.versonaId.toString(), h);
    // ROUTE AHEAD (2026-09-15): the polyline each near mover will walk, boxed like the hot rows.
    interface RouteRow { versonaId: bigint; x0: number; z0: number; x1: number; z1: number; x2: number; z2: number; x3: number; z3: number; n: number; stepM: number }
    const routeTbl = (conn.db as unknown as { versonaRoute?: { iter(): Iterable<RouteRow> } }).versonaRoute;
    const routes = new Map<string, RouteRow>();
    if (routeTbl) for (const r of routeTbl.iter()) routes.set(r.versonaId.toString(), r);
    // AOI semantics: with the hot table present, a Versona is RENDERED only while its
    // windowed hot row exists (hot-row presence == inside the AOI window). Without the
    // hot table (pre-publish server), every Versona legacy-renders from static cur*.
    // INVERTED live-list (perf): iterate the ~50 BOX-windowed hot rows and
    // look the fat row up by id — the old shape scanned all ~2k fat rows on
    // every coalesce ping, a steady main-thread tax after the population wave.
    const live: Array<(typeof conn.db.versona extends { iter(): Iterable<infer R> } ? R : never)> = [];
    if (hotTbl) {
      for (const h of hot.values()) {
        const dx = h.curX - atlas.localX;
        const dz = h.curZ - atlas.localZ;
        if (dx * dx + dz * dz >= 400 * 400) continue;
        const v = conn.db.versona.versonaId.find(h.versonaId);
        if (v && v.worldId === worldId) live.push(v);
      }
    } else {
      for (const v of conn.db.versona.iter()) {
        if (v.worldId !== worldId) continue;
        const dx = v.curX - atlas.localX;
        const dz = v.curZ - atlas.localZ;
        if (dx * dx + dz * dz < 400 * 400) live.push(v);
      }
    }
    const now = performance.now();
    const liveKeys = new Set<string>();
    for (const v of live) {
      const civ = this.civOf(v.versonaId, v.curX, v.curZ);
      const crowd = this.crowdFor(v.archetype, v.versonaId, civ) // its own body, else the pre-wave body while the twin streams
        ?? this.crowd; // fall back to capsule
      if (!crowd) continue;
      const key = v.versonaId.toString();
      liveKeys.add(key);
      const h = hot.get(key);
      const cx = h ? h.curX : v.curX;
      const cz = h ? h.curZ : v.curZ;
      const st = h ? h.state : v.state;
      const phase = (Number(v.versonaId % 997n) / 997) * Math.PI * 2;
      const parity = Number(v.versonaId % 2n);
      const prev = this.versonaAnim.get(key);
      if (!prev) {
        // A body born ON its row: with a route beside it, it walks the forecast from the origin.
        const rr0 = h ? routes.get(key) : undefined;
        let route0: VersonaAnimEntry['route'] = null;
        if (rr0 && routeMatchesRow(rr0, cx, cz)) {
          const pts0 = routePoints(rr0);
          const L0 = polylineLength(pts0);
          route0 = { pts: pts0, s0: 0, t0: now, v: routePace(rr0.stepM, 4000), L: L0, sMax: routeHorizon(0, rr0.stepM, L0) };
        }
        this.versonaAnim.set(key, { key, crowd, fromX: cx, fromZ: cz, toX: cx, toZ: cz, t0: now, dur: 1000, yaw: 0, state: st, phase, parity, route: route0 });
      } else if (prev.toX !== cx || prev.toZ !== cz || prev.crowd !== crowd) {
        // Current interpolated pose becomes the new segment start; duration = the
        // observed update interval (clamped) so motion spans until the next tick lands.
        const f = Math.min(1, (now - prev.t0) / prev.dur);
        let ix = prev.fromX + (prev.toX - prev.fromX) * f;
        let iz = prev.fromZ + (prev.toZ - prev.fromZ) * f;
        const dx = cx - ix;
        const dz = cz - iz;
        const hop = Math.hypot(dx, dz);
        const durObs = Math.min(6000, Math.max(300, now - prev.t0));
        // ZIP GUARD + PACE MATCH: burst arrivals once produced 300 ms segments
        // over multi-tick hops (teleport-streaks), and speed-mismatched glides
        // finished early then stalled (the rubberband). Glide at the TRUE walk
        // pace (~2 m/s server-side) so the body is still mid-stride when the
        // next row lands; teleport-scale jumps (respread, admin) SNAP.
        let dur = Math.max(durObs, (hop / 2.1) * 1000);
        if (hop > 40) { ix = cx; iz = cz; dur = durObs; }
        // ROUTE AHEAD: with a route row written beside this hot row, the body walks the
        // polyline from where it stands through the row and along the corners at the
        // server's pace — still mid-stride when the next row lands, turning where the
        // street turns. A teleport-scale hop snaps as before (no route).
        const rr = h ? routes.get(key) : undefined;
        let route: VersonaAnimEntry['route'] = null;
        if (rr && hop <= 40 && routeMatchesRow(rr, cx, cz)) {
          const w = walkFor(routePoints(rr), ix, iz);
          const L = polylineLength(w.pts);
          route = { pts: w.pts, s0: w.s0, t0: now, v: routePace(rr.stepM, durObs), L, sMax: routeHorizon(w.sOrigin, rr.stepM, L) };
        }
        this.versonaAnim.set(key, {
          key, crowd,
          fromX: ix, fromZ: iz,
          toX: cx, toZ: cz,
          t0: now,
          dur,
          yaw: hop > 1e-3 ? Math.atan2(dx, dz) : prev.yaw, // face travel; hold when idle
          state: st, phase, parity, route,
        });
      } else {
        prev.crowd = crowd; // same target (e.g. avatar swap) — keep the running segment
        prev.state = st;    // activity can change while settled (e.g. → chatting)
        // The forecast is VOID when its route row is gone with the hot row unchanged —
        // the server stopped this versona (a refused slope, a blocked door, an arrival,
        // out of the near set): glide from where the body stands back to the row.
        const rr = h ? routes.get(key) : undefined;
        if (prev.route && !(rr && routeMatchesRow(rr, cx, cz))) {
          const p = pointAlong(prev.route.pts, Math.min(prev.route.sMax, prev.route.s0 + prev.route.v * (now - prev.route.t0) / 1000));
          const back = Math.hypot(cx - p.x, cz - p.z);
          prev.fromX = p.x; prev.fromZ = p.z; prev.toX = cx; prev.toZ = cz; prev.t0 = now; prev.dur = Math.max(300, (back / 2.1) * 1000);
          prev.yaw = back > 1e-3 ? Math.atan2(cx - p.x, cz - p.z) : prev.yaw;
          prev.route = null;
          this.routeVoids++;
        }
      }
    }
    for (const k of [...this.versonaAnim.keys()]) if (!liveKeys.has(k)) this.versonaAnim.delete(k);
    this.renderVersonaFrame(); // stamp immediately so a refresh never leaves a blank frame
  }

  /** Per-frame (Animation/38): glide every Versona along its from→to segment. */
  // Scratch for renderVersonaFrame — it runs EVERY frame (Animation/38);
  // per-frame allocation of these + a Map was steady GC churn (perf sweep).
  private readonly _vfM = new THREE.Matrix4();
  private readonly _vfP = new THREE.Vector3();
  private readonly _vfQ = new THREE.Quaternion();
  private readonly _vfS = new THREE.Vector3(1, 1, 1);
  private readonly _vfUp = new THREE.Vector3(0, 1, 0);
  private readonly _vfCounts = new Map<CrowdInstancer, number>();
  private readonly _impCamPos = new THREE.Vector3();
  /** LOD2 instancer per near crowd (only on tiers whose near body is the full mesh). */
  private readonly crowdsFar = new Map<CrowdInstancer, CrowdInstancer>();

  // ── A BODY IN THE ROOM (slice H, 2026-10-02 — lib/indoor_body.ts) ─────────────────────────────────────────────
  // A versona inside the interior the player stands in (its row says so — a resident at home, a venue's keeper — or it
  // simply stands inside the room's walls) is drawn UNLIT, lit by the room: a skinned actor wears its materials' indoor
  // twins (the rig layer, `pose({ indoor })`), an instanced twin is stamped into a THIRD instancer per body built over
  // the same geometry with the twin material (a material cannot change per instance; built the first time a body of
  // that kind stands indoors). Outdoors the very next frame, it is drawn exactly as before. Lever: `atlas:interior-bodies`.
  private readonly crowdsIndoor = new Map<CrowdInstancer, CrowdInstancer>();
  /** Is this point inside the walls of the interior the player stands in? (null-fast outdoors — lib/indoor_body.ts) */
  private insideActiveRoom(x: number, y: number, z: number): boolean { return insideActiveInterior(x, y, z); }
  /** The indoor instancer of a body's near crowd (built on first need; null with the lever off or before the crowd exists). */
  private indoorCrowdFor(crowd: CrowdInstancer | undefined): CrowdInstancer | null {
    if (!crowd || !indoorBodiesEnabled()) return null;
    let ind = this.crowdsIndoor.get(crowd);
    if (ind) return ind;
    const key = this.crowdKeys.get(crowd);
    if (!key) return null; // the capsule fallback has no body to light
    const src = (crowd.mesh.userData.indoorSource as THREE.Material | undefined) ?? (crowd.mesh.material as THREE.Material);
    ind = new CrowdInstancer(crowd.mesh.geometry, indoorTwin(src), 1024, 2, crowd.yOffset, 'crowd-avatar-indoor');
    ind.mesh.castShadow = false; // the room is unlit; no indoor body casts into the sun's maps
    this.crowdAnchor.group.add(ind.mesh);
    if (this.appRef) void warmThenShow(this.appRef, ind.mesh);
    const ic = ind;
    this.culling.registerCrowdCuller((_camPos, planes) => ic.cull(planes));
    this.crowdsIndoor.set(crowd, ind);
    this.crowdKeys.set(ind, key);
    return ind;
  }

  /** WeatherModule, when it is up — the wind field the foliage/water/particles share. */
  private windSource(): { wind(): { x: number; z: number; strength: number } } | null {
    const m = this.app?.getModule('Weather') as { wind?: () => { x: number; z: number; strength: number } } | undefined;
    return m?.wind ? (m as { wind(): { x: number; z: number; strength: number } }) : null;
  }

  /**
   * Per-frame drivers for the FOLIAGE and impostor layers: leaf translucency's
   * view-space sun, the wind shader's frame delta (which feeds tree motion
   * vectors), and impostor billboards re-aiming at the camera.
   *
   * WHY THIS IS ITS OWN METHOD (2026-09-14): these lived at the bottom of
   * renderVersonaFrame, which returns early when `versonaAnim` is empty. The
   * forest therefore froze — no wind, stale translucency, impostors still
   * facing wherever the camera last was — whenever no versona was animated
   * nearby, which is most of the wilds. Vegetation does not depend on the
   * crowd, so it is driven on its own.
   */
  /**
   * THE GRASS PARTS FOR EVERYONE (physics follow-up A, 2026-10-05; lib/trample_agents.ts): the player on foot, the
   * hovercraft WASH (the local ride and remote riders — a radial ring push by speed and hover height), other players on
   * foot, the walking versonas, the herd animals (`AtlasHerdsModule.drawnAnimalsNear`) and the wild creatures — each
   * kind nearest-first under its cap, inside the field's region; one list for both trample branches.
   */
  private trampleAgents(): TrampleAgent[] {
    const app = this.app;
    const c = trampleCenter();
    const veh = app?.getModule('AtlasVehicle') as { hoverWash?: () => WashSource | null } | null | undefined;
    const herds = app?.getModule('AtlasHerds') as { drawnAnimalsNear?: (x: number, z: number, r: number) => TrampleBody[] } | null | undefined;
    const wilds = app?.getModule('Wilds') as { creaturePositions?: () => TramplePoint[] } | null | undefined;
    const washes: WashSource[] = [];
    const own = atlas.hoverRide ? (veh?.hoverWash?.() ?? null) : null;
    if (own) washes.push(own);
    const remotes: TramplePoint[] = [];
    for (const r of atlas.remotes.values()) {
      const m = r.mesh;
      const x = m ? m.position.x : r.targetX, z = m ? m.position.z : r.targetZ;
      if (r.animState.startsWith('ride:')) washes.push({ x, z, speed: snapSpeed(r.snaps), gap: 1, hoverM: 1 }); // a remote rider is seated at its hover height
      else if (!r.animState.startsWith('fly:')) remotes.push({ x, z });
    }
    const versonas: TramplePoint[] = [];
    for (let i = 0; i < this._vfN; i++) { const sl = this._vfSlots[i]; if (sl && !sl.indoors) versonas.push({ x: sl.x, z: sl.z }); }
    const reach = TRAMPLE_REGION_M * 0.75;
    const agents = gatherTrampleAgents({
      px: atlas.localX, pz: atlas.localZ, cx: c.x, cz: c.z, region: TRAMPLE_REGION_M,
      playerOnFoot: !atlas.hoverRide && !atlas.skyFlight && !atlas.spaceFlight,
      washes, remotes, versonas,
      herds: herds?.drawnAnimalsNear?.(atlas.localX, atlas.localZ, reach) ?? [],
      creatures: wilds?.creaturePositions?.() ?? [],
    });
    this.trampleCensus = { agents: agents.length, washes: washes.length, remotes: remotes.length, versonas: versonas.length };
    return agents;
  }
  private trampleCensus = { agents: 0, washes: 0, remotes: 0, versonas: 0 };

  private updateFoliageDrivers(): void {
    // The grass layer (visual-realism R2): camera, lattice snap, field tiles, upload — no per-blade CPU.
    this.grassDriver?.update();
    this.groundKeepOutTick(); // nothing grows inside a building that assembled after its ground cover (2026-10-02)
    // Stand impostors to the horizon (R4b): a job only when the camera moved a few hundred metres (every 30th frame checks).
    if (this.forestHorizon && ++this.horizonTick % 30 === 0) {
      const inCommons = !!this.commonsSeedForChunks && atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId && !atlas.spaceFlight;
      const p = activePreset();
      if (inCommons && (p.foliageDensityScale ?? 1) > 0) this.forestHorizon.update(atlas.localX, atlas.localZ, this.commonsSeedForChunks, commonsSeaLevel(this.commonsSeedForChunks), p.forestHorizonM ?? 0, (p.foliageRing ?? 1) + FOREST_FAR_RINGS, CHUNK_SIZE);
      else this.forestHorizon.update(0, 0, '', 0, 0, 0, CHUNK_SIZE);
    }
    // The trample field steps whenever the GRASS draws, trees or no trees (it used to step only inside the tree-wind
    // branch below, so on an open meadow with no tree species nearby the bend field never decayed or took the player).
    if (trampleActive() && this.foliageFx.w.length === 0 && this.grassDriver?.layer?.isVisible) {
      const nowT = performance.now();
      const dT = Math.min(0.1, Math.max(1 / 240, (nowT - this.lastTrampleMs) / 1000));
      this.lastTrampleMs = nowT;
      stepTrample(dT, atlas.localX, atlas.localZ, this.trampleAgents()); // the crowd too, not the player alone (physics follow-up A)
    }
    // surfaces (2026-09-27): §2.4 the ground's parallax self-shadow marches toward the KEY light (world frame; one uniform
    // write a frame whether or not the knob is built in — the moon when it is the key, like the leaf translucency below).
    if (lightFrame.valid && lightFrame.keyIsMoon) setGroundKeyDir(lightFrame.keyDir.x, lightFrame.keyDir.y, lightFrame.keyDir.z);
    else { const sd = uSunDir.value as THREE.Vector3; setGroundKeyDir(sd.x, sd.y, sd.z); }
    // Foliage translucency: the sun in VIEW space (the engine helper's frame).
    if (this.foliageFx.t.length) {
      const cam = this.app?.renderer.cameras.main;
      if (cam) {
        // surfaces (2026-09-27): §5.3 ON THE CURVE — the leaf transmits the KEY LIGHT only (the lighting recipe rule 3,
        // `sunSceneLight()` = lightFrame.keyScene, RGB, its direction lightFrame.keyDir): radiance = the leaf's
        // transmittance tint × E/π × the back lobe. The sky never goes THROUGH a leaf (Habel 2007: the transmitted term is
        // the sun's), and the sunset key reddens and dims the glow with the sun. Before the first light frame: the old rule.
        if (lightFrame.valid) {
          const ks = lightFrame.keyScene;
          if (lightFrame.keyIsMoon) this.sunView.set(lightFrame.keyDir.x, lightFrame.keyDir.y, lightFrame.keyDir.z);
          else this.sunView.copy(uSunDir.value as THREE.Vector3);
          this.sunView.transformDirection(cam.matrixWorldInverse);
          for (const h of this.foliageFx.t) { h.lightDirView.value.copy(this.sunView); h.lightColor.value.set(ks.x / Math.PI, ks.y / Math.PI, ks.z / Math.PI); h.lightIntensity.value = 1; }
        } else {
          this.sunView.copy(uSunDir.value as THREE.Vector3).transformDirection(cam.matrixWorldInverse);
          // Audit 16.2 (2026-09-15): the transmission is LIGHT through the leaf —
          // it follows the sun's elevation and dies with the night.
          const sunI = foliageTransmissionIntensity((uSunDir.value as THREE.Vector3).y, (uNightFactor as unknown as { value: number }).value ?? 0);
          for (const h of this.foliageFx.t) { h.lightDirView.value.copy(this.sunView); h.lightColor.value.set(1, 1, 1); h.lightIntensity.value = sunI; }
        }
      }
    }
    if (this.foliageFx.w.length) {
      // Tree wind motion vectors: last frame's distance in time.
      const nowMs = performance.now();
      const d = Math.min(0.1, Math.max(1 / 240, (nowMs - this.lastWindFxMs) / 1000));
      this.lastWindFxMs = nowMs;
      // THE WEATHER DRIVES THE FOLIAGE (2026-09-14). Strength, direction and
      // gust depth were fixed at their construction values, so a tree rustled
      // exactly the same on a dead-calm morning as in a storm. WeatherModule's
      // `wind()` is the same field the water and the particle emitters read, so
      // the leaves now agree with the rain, the dust and the waves.
      //
      // 0.45 is the calm-day amplitude the species materials were tuned at;
      // the band runs from a little under that up to ~2.4× in a full blow, and
      // gusts deepen with the wind so a storm arrives in waves rather than as
      // a constant lean.
      // Trampling: the player first (never dropped by the agent cap), then the
      // nearby crowd. Radius/strength are a footprint, not a shove — grass
      // parts around a walker rather than being blown flat.
      if (trampleActive()) stepTrample(d, atlas.localX, atlas.localZ, this.trampleAgents());

      // The wind switch also silences ALREADY-BUILT materials immediately, so
      // turning it off reads at once instead of waiting for the rebuild.
      if (!foliageWindOn()) {
        for (const h of this.foliageFx.w) { h.strength.value = 0; h.frameDt.value = d; }
        return;
      }
      const w = this.windSource()?.wind();
      if (w) {
        const s = Math.min(1, Math.max(0, w.strength));
        const strength = 0.45 * (0.55 + 1.85 * s);
        const gust = 0.35 + 0.45 * s;
        const len = Math.hypot(w.x, w.z) || 1;
        for (const h of this.foliageFx.w) {
          h.strength.value = strength;
          h.gustStrength.value = gust;
          h.direction.value.set(w.x / len, w.z / len);
          h.frameDt.value = d;
        }
      } else {
        for (const h of this.foliageFx.w) h.frameDt.value = d;
      }
    }
    // Impostor billboards re-aim at the camera every REFACE_EVERY frames.
    if (this.impostors.size) {
      const cam = this.appRef?.renderer.cameras.main as unknown as THREE.Camera | undefined;
      // ABSOLUTE, like the instances (bug sweep 2026-09-22): matrixWorld is RENDER space (the FP64
      // rebase holds scene.position at −origin), so past the first rebase every far card turned toward
      // a point a whole origin away — forests squashed to slivers across almost the whole map.
      const cp = cam
        ? this._impCamPos.setFromMatrixPosition(cam.matrixWorld).sub(this.scene.position)
        : this._impCamPos.set(atlas.localX, atlas.localY + 1.6, atlas.localZ);
      for (const inst of this.impostors.values()) inst?.reface(false, cp);
    }
  }

  private renderVersonaFrame(): void {
    for (const c of this.crowds.values()) c.clear();
    for (const c of this.crowdsFar.values()) c.clear();
    for (const c of this.crowdsIndoor.values()) c.clear(); // (slice H) the indoor bodies' instancers
    this.crowd?.clear();
    this.updateStations(performance.now());
    if (this.versonaAnim.size === 0) {
      // An EMPTY crowd still tears the presentation down (bug sweep 2026-09-22): the early return
      // skipped the rig planner and the clash layer, so a hop to a world with no versonas left up to
      // 24 frozen 290k-vertex rig actors (and any clash banners, depthTest off) standing in the scene.
      this.rigLayer.plan([], atlas.localX, atlas.localZ);
      this.updateLifeLayers(0, 0);
      this.updateWarClashes(performance.now() / 1000);
      return;
    }
    const now = performance.now();
    const t = now / 1000;
    const counts = this._vfCounts;
    counts.clear();
    const m = this._vfM;
    const p = this._vfP;
    const q = this._vfQ;
    const s = this._vfS;
    const up = this._vfUp;
    const ground = this.versonaGround;
    const clear = this.versonaClear;
    // Pass 1 — resolved positions into reusable slots (the chatting/facing +
    // crate/label passes need EVERY position before any pose is composed).
    // The player's ACTIVE conversation partner (T window open) stands still:
    // their position freezes at the last rendered spot no matter what
    // segments the life sim keeps feeding — walking off mid-sentence reads
    // as broken. Derived live from the talk window's open state (leak-proof).
    const npcMod = this.app?.getModule('NpcInteract') as
      | { activeConversation?: string | null; partnerMoodHint?: string } | undefined;
    const talkingTo = npcMod?.activeConversation ?? null;
    // Cognition wave (feature 25): the partner's dominant-emotion tone colors
    // their stance while talking (grief slumps, joy lifts).
    const talkMood = npcMod?.partnerMoodHint ?? '';
    let n = 0;
    this.clearFrame++;
    this.routeCount = 0; this.routeHeld = 0;
    if (enterables.size !== this.enterSize) { this.enterSize = enterables.size; this.enterStamp++; } // (slice L) the indoor memo below
    for (const a of this.versonaAnim.values()) { // values(): an entries() pair array per versona per frame is garbage V8 cannot elide
      const animKey = a.key;
      if ((this.hiddenIds.size > 0 && this.hiddenIds.has(animKey)) || this.airHiddenIds.has(animKey)) { a.lx = undefined; a.lz = undefined; continue; } // drawn by BattleModule / flying in WarAir; forget the governor's memory
      const isTalking = talkingTo !== null && animKey === talkingTo;
      const f = Math.min(1, (now - a.t0) / a.dur);
      let x = a.fromX + (a.toX - a.fromX) * f;
      let z = a.fromZ + (a.toZ - a.fromZ) * f;
      // ROUTE AHEAD (2026-09-15): a body with a route walks its polyline by arc length at the
      // server's pace and faces the segment it is on; the straight glide is the fallback.
      let onRoute = false;
      if (a.route) {
        const s = a.route.s0 + a.route.v * (now - a.route.t0) / 1000;
        const p = pointAlong(a.route.pts, Math.min(a.route.sMax, s));
        x = p.x; z = p.z; onRoute = !p.done && s < a.route.sMax; // at the horizon the body waits for the next row
        if (onRoute) a.yaw = p.yaw;
        this.routeCount++;
        if (s >= a.route.sMax) this.routeHeld++;
      }
      if (isTalking && a.lx !== undefined && a.lz !== undefined) { x = a.lx; z = a.lz; }
      // A versona in a HOME state (resting / sleeping / playing / chatting)
      // whose anchor falls within an enterable dwelling is INDOORS: no
      // clearance push-out (they are meant to be inside), hidden from the
      // street, and BEDDED/SEATED on a real interior spot when the player
      // enters that building (home-life wave: families live inside).
      let homeB: EnterableBuilding | null = null;
      if (HOME_STATES.has(a.state)) {
        const rx = Math.round(x), rz = Math.round(z); // integers, not a template key per versona per frame
        // THE REGISTRY MOVES UNDER THE MEMO (slice L, 2026-10-02): a resident whose row was known BEFORE its building
        // registered (the settlement assembles after the AOI rows land) had `inB = null` memoised for as long as it
        // stood still — never indoors, never seated, drawn at the server's point inside the walls (the world leg:
        // 16 of 17 residents of a civic hall). The memo is re-asked whenever the registry's size changed.
        const moved = a.inX !== rx || a.inZ !== rz;
        if (moved || a.inG !== this.enterStamp) {
          const nb = enterableAt(x, z)?.id ?? null;
          if (moved || nb !== a.inB) a.spot = undefined;
          a.inX = rx; a.inZ = rz; a.inG = this.enterStamp; a.inB = nb;
        }
        if (a.inB) homeB = enterables.get(a.inB) ?? null;
      } else if (a.inB !== undefined) { a.inB = undefined; a.inX = undefined; a.inZ = undefined; a.spot = undefined; a.life = undefined; a.lifeHide = false; }
      if (homeB && homeB.id !== atlas.interiorId && a.life !== undefined) { a.life = undefined; a.lifeHide = false; a.spot = undefined; }
      if (homeB && homeB.id === atlas.interiorId) {
        // Only residents of the interior the PLAYER is standing in resolve a
        // furniture spot (everyone else is culled at the render gate anyway).
        // RESIDENTS ON REAL ANCHORS (interiors programme, slice C, 2026-10-01): the room the visit drew is a planned
        // DESCRIPTOR now, so the resident reads THAT room (its beds' and seats' sockets, its proven standing points)
        // — re-resolved whenever the visit's plan, pack or pin changes (`version`). With the lever off, a bespoke
        // room, or the plan not landed yet, `room` is null and the two legacy functions answer exactly as before.
        const act = activeInterior();
        const roomVer = act && act.id === homeB.id ? act.version : -1;
        // RESIDENTS USE THE WHOLE BUILDING (slice L): while the room is a planned descriptor the resident is where the
        // ITINERARY says — at an anchor nobody else has, or walking the planner's routes, the stairs or the lift toward
        // one (`lib/interior_life.ts`: a pure function of the room, the residents and the clock, the same on every
        // client). No itinerary (the lever, a legacy room, no routed anchor) → today's placement below, untouched.
        const life = this.lifeFor(homeB, act);
        if (life) {
          const pose = poseAt(life.room, life.plan, animKey, life.tS);
          const cos = Math.cos(homeB.yaw), sin = Math.sin(homeB.yaw);
          if (pose && !(pose.inCab && life.cabHeld)) {
            const sp = (a.lifeSpot ??= { x: 0, z: 0, y: 0, yaw: 0, lying: false });
            sp.x = homeB.x + pose.x * cos + pose.z * sin;
            sp.z = homeB.z - pose.x * sin + pose.z * cos;
            sp.y = homeB.groundY + pose.y; sp.yaw = homeB.yaw + pose.yaw; sp.lying = pose.lying;
            const still = pose.mode !== 'walk' && pose.mode !== 'stairs' && pose.mode !== 'cab' && pose.mode !== 'wait';
            // first frame under it — or a settled body whose anchor changed with no walk between (it woke, turned in,
            // or the deal moved under it): appear there, never a glide through the walls
            if (a.life === undefined || a.life === null || a.lifeHide || (still && a.lifeAt !== undefined && a.lifeAt !== pose.anchor)) { a.lx = undefined; a.lz = undefined; }
            a.lifeAt = still ? pose.anchor : undefined;
            a.spot = sp; a.spotState = '#life'; a.life = pose.rig; a.lifeHide = false;
            a.yaw = sp.yaw;
          } else { a.life = null; a.lifeHide = true; a.spot = null; a.spotState = '#life'; a.lifeAt = undefined; }
        } else if (a.life !== undefined) { a.life = undefined; a.lifeHide = false; a.spot = undefined; a.lx = undefined; a.lz = undefined; a.lifeAt = undefined; }
        if (!life && (a.spot === undefined || a.spotState !== a.state || a.spotVer !== roomVer)) {
          const spec = this.homeSpecFor(homeB);
          const room = activeDescriptorFor(homeB.id)?.room ?? null;
          // A PLAYER HOUSE is furnished by its pack (or empty): its residents take the plan's real pieces.
          const sp = homeB.id.startsWith('deed:') ? deedResidentSpot(animKey, homeB.id, room ? [] : this.deedPlanFor(homeB, spec), spec, a.state, room) : interiorHomeSpot(animKey, homeB.id, spec, a.state, room);
          const cos = Math.cos(homeB.yaw), sin = Math.sin(homeB.yaw);
          a.spot = {
            x: homeB.x + sp.lx * cos + sp.lz * sin,
            z: homeB.z - sp.lx * sin + sp.lz * cos,
            y: homeB.groundY + (sp.floor === 0 ? 0.08 : sp.floor * 3.4) + sp.lift,
            yaw: homeB.yaw + sp.yaw, lying: sp.lying,
          };
          a.spotState = a.state;
          a.spotVer = roomVer;
        }
        if (a.spot) { x = a.spot.x; z = a.spot.z; }
      }
      // THE VENUE COUNTER (interiors programme, slice E, 2026-10-01): while THIS client stands in a bound venue (the
      // town's tavern / clinic), the versona who keeps its post and a customer who has arrived at its door are SHOWN at
      // the counter's anchors (`AtlasVenueModule` publishes who stands where). Presentation only — the server's one
      // authoritative point never moves, and everyone outside sees them where the server has them. A body farther than
      // a few steps APPEARS at its anchor (the player is indoors and cannot see the street), and goes back to the
      // server's point at once when the player leaves — never a glide through the wall.
      const vc = atlas.interiorId ? venueCounter() : null;
      const venueSpot = !homeB && !isTalking && vc && vc.id === atlas.interiorId ? vc.spots.get(animKey) : undefined;
      if (venueSpot) {
        if (a.lx !== undefined && a.lz !== undefined && Math.hypot(venueSpot.x - a.lx, venueSpot.z - a.lz) > 8) { a.lx = undefined; a.lz = undefined; }
        a.spot = venueSpot; x = venueSpot.x; z = venueSpot.z;
      } else if (!homeB && a.spot && a.inB === undefined) { a.spot = undefined; a.lx = undefined; a.lz = undefined; }
      // SOCIETY WAVE — a versona that has ARRIVED at an activity station takes the seat this client dealt
      // it (lib/station_seating.ts: sorted by id, so every client seats the same people the same way). The
      // speed governor below walks the body to its seat; the server's one authoritative point never moves.
      const seat = !homeB && !venueSpot && !isTalking && this.stationSeats.size > 0 ? this.stationSeats.get(animKey) : undefined;
      if (seat) { x = seat.x; z = seat.z; }
      // Bodies stay OUT of building footprints/port hulls (geometry-blind
      // server positions can be inside them)…
      // The projection scans all 312 sites and every structure seat per call:
      // every 4th frame per body (staggered), the last offset reused between —
      // a body moves < 0.2 m in those three frames (2026-09-12).
      if (!homeB && !seat && !venueSpot && clear) { // (a station stands on ground the server already cleared; a counter is indoors on purpose)
        if (a.cdx === undefined || a.cdz === undefined || ((this.clearFrame + n) & 3) === 0) { const c = clear(x, z); a.cdx = c ? c.x - x : 0; a.cdz = c ? c.z - z : 0; }
        x += a.cdx; z += a.cdz;
      } else { a.cdx = undefined; a.cdz = undefined; }
      // VISUAL SPEED GOVERNOR (the rubber-band fix): the clearance projection
      // above can sweep a body around a footprint far faster than any walk
      // (nearest-exit flips as the glide crosses a building's midline), and
      // burst row-applies can jump the segment target. Whatever the source,
      // the RENDERED body never outruns a fast walk — only a true teleport
      // (respread/admin, >30 m) snaps.
      if (a.lat !== undefined && a.lx !== undefined && a.lz !== undefined) {
        const dtv = Math.min(0.25, Math.max(0.001, (now - a.lat) / 1000));
        const gx = x - a.lx;
        const gz = z - a.lz;
        const gd = Math.hypot(gx, gz);
        const maxStep = 3.2 * dtv;
        if (gd > maxStep) { const v = gd / dtv; if (v > this.govMaxRaw) this.govMaxRaw = v; }
        if (gd > 30) { /* true teleport — accept the snap */ }
        else if (gd > maxStep) {
          x = a.lx + (gx / gd) * maxStep;
          z = a.lz + (gz / gd) * maxStep;
          this.govClamped++;
        }
      }
      a.lx = x; a.lz = z; a.lat = now;
      let slot = this._vfSlots[n];
      if (!slot) { slot = { a, key: animKey, x: 0, z: 0, gy: 0, yaw: 0, bob: 0, moving: false, indoors: null, talking: false, home: null, job: '', jobAt: 0, elder: false, near: false, noticedAt: -Infinity, gazeW: 0, attn: 'none', attnX: 0, attnZ: 0, lookYaw: null, seat: null, bedY: 0, swimming: false, waterAt: NaN, life: null }; this._vfSlots[n] = slot; }
      slot.a = a;
      slot.seat = seat ?? null;
      slot.key = animKey;
      slot.x = x;
      slot.z = z;
      slot.indoors = homeB?.id ?? (venueSpot ? atlas.interiorId : null);
      // (slice L) under the itinerary a body that has not come in yet — or rides the lift car while the player holds
      // it — is not drawn: it reads as "indoors somewhere else" to the pose loop and the rig planner below
      const underLife = !!homeB && homeB.id === atlas.interiorId && a.life !== undefined;
      if (underLife && a.lifeHide) slot.indoors = '#not-drawn';
      slot.life = underLife && !a.lifeHide ? a.life ?? null : null;
      slot.home = homeB && a.spot ? a.spot : venueSpot ?? null;
      // …and feet follow the rendered ground at the CURRENT position (cheap:
      // the chunk-grid fast path is 4 array reads) — never a Y lerp through a
      // hill. Indoors the FLOOR SLAB is the ground (a bedroom on storey 1).
      slot.gy = slot.home ? slot.home.y : ground ? ground(x, z) : 0;
      slot.bedY = slot.gy;
      // SWIMMING VERSONAS (2026-09-23, "if they walk into water they swim instead of sink"): over deep water
      // (the still level ≥ SWIM_DEPTH_M over the bed) the body floats at the surface, feet SWIM_FEET_UNDER_M
      // under it — it used to walk the lake floor. The still-water lookup runs every 4th frame per body
      // (staggered, like the clearance projection); the live surface every frame it floats.
      if (!slot.home && slot.indoors === null) {
        if (((this.clearFrame + n) & 3) === 0 || Number.isNaN(slot.waterAt) && slot.swimming) { const st = waterStillAt(x, z); slot.waterAt = st !== null && st - slot.bedY >= SWIM_DEPTH_M ? st : NaN; }
        slot.swimming = Number.isFinite(slot.waterAt);
        if (slot.swimming) slot.gy = (waterLiveAt(x, z) ?? slot.waterAt) - SWIM_FEET_UNDER_M;
      } else { slot.swimming = false; slot.waterAt = NaN; }
      // (a body SHOWN at a venue's counter stands there: the server's row may still be walking its last metres to the
      //  door cell — arrival is judged within 6 m of it — and a walk clip in place, side-on to the counter, is a mime)
      slot.moving = !isTalking && !venueSpot && (a.route ? onRoute : (f < 1 && Math.abs(a.toX - a.fromX) + Math.abs(a.toZ - a.fromZ) > 0.6));
      if (slot.life) slot.moving = !isTalking && slot.life.moving; // (slice L) the itinerary says whether it walks
      slot.talking = isTalking;
      // TASK CLIPS (2026-09-10): the trade (npc_agent.job) picks the working clip on the near actor; refreshed every 15 s.
      if (now - slot.jobAt > 15_000) { slot.jobAt = now; slot.job = this.jobOf(animKey); slot.elder = founderIsElder(animKey) && (this.crowdKeys.get(a.crowd) ?? '') !== 'avatar:child_colonist'; }
      n++;
    }
    this._vfN = n;
    // Pass 2 — the LIFE poses (no rig yet, so the whole body acts): walkers
    // bob, smiths swing, traders haggle-nod, chatters face their partner and
    // take turns nodding, resters breathe. Phase-offset per versona so a
    // crowd never marches in step.
    const px = atlas.localX, pz = atlas.localZ;
    const nowMs = performance.now();
    // CIVIL INATTENTION: how many versonas are looking at the player this frame (capped).
    let lookers = 0;
    // Attention priority (2026-09-15): the partner / threat / startle looks this frame (never capped).
    const attn = this.attnCounts; attn.partner = 0; attn.threat = 0; attn.startle = 0;
    while (this.startles.length > 0 && t - this.startles[0].at > STARTLE_S) this.startles.shift();
    if (nowMs - this.familiarAt > 10_000) this.refreshFamiliar(nowMs);
    // Rig wave: the nearest versonas with a rigged body become skinned actors
    // this frame; their crowd instance is hidden below.
    this._rigCands.length = 0;
    for (let i = 0; i < n; i++) {
      const sl = this._vfSlots[i];
      // An indoor versona is never posed below (invisible from the street) — so it must not own an
      // actor either, or the actor stood frozen at its last street pose (bug sweep 2026-09-22).
      if (sl.indoors && sl.indoors !== atlas.interiorId) continue;
      this._rigCands.push({ id: sl.key, bodyKey: this.crowdKeys.get(sl.a.crowd) ?? '', x: sl.x, z: sl.z });
    }
    this.rigLayer.plan(this._rigCands, px, pz);
    // someone near wears a body with no master yet (a wave-4 body met for the first time): ask, at most once a second
    if (!this.rigLoading && this.avatarCatalog && this.rigLayer.wantedKeys().length > 0 && now - this.rigPokeAt > 1000) {
      this.rigPokeAt = now;
      void this.loadRigMasters(this.avatarCatalog);
    }
    for (let i = 0; i < n; i++) {
      const sl = this._vfSlots[i];
      const a = sl.a;
      const st = a.state;
      // Indoors: invisible from the street; visible only to a player standing
      // in the same interior (the home's resident is at home).
      if (sl.indoors && sl.indoors !== atlas.interiorId) continue;
      // Near/far body: beyond NEAR_BODY_M the LOD2 instancer (when the tier
      // built one) draws this versona; the counts map is keyed per instancer.
      const farC = this.crowdsFar.get(a.crowd);
      const ddx = sl.x - px, ddz = sl.z - pz;
      // (slice H) a body in the room the player stands in is lit by the room (its own row's room, or inside its walls)
      const inRoom = atlas.interiorId !== null && (sl.indoors === atlas.interiorId || this.insideActiveRoom(sl.x, sl.gy, sl.z));
      // A far or indoor instancer that is still warming is hidden: the body keeps its shown crowd until the reveal.
      const indC = inRoom ? this.indoorCrowdFor(a.crowd) : null;
      const target = (indC && !warmPending(indC.mesh) ? indC : null)
        ?? (farC && !warmPending(farC.mesh) && ddx * ddx + ddz * ddz > NEAR_BODY_M * NEAR_BODY_M ? farC : a.crowd);
      let yaw = a.yaw, pitch = 0, bobY = 0, breathe = 1;
      if (sl.talking) {
        // In conversation with the PLAYER: hold still, face them squarely,
        // attentive listening nods (gentle, not the haggle bob). The partner's
        // MOOD colors the stance (cognition wave): grief slumps, joy lifts.
        yaw = Math.atan2(px - sl.x, pz - sl.z);
        const pm = talkMood;
        if (pm === 'down' || pm === 'angry' || pm === 'uneasy') {
          pitch = 0.14 + Math.max(0, Math.sin(t * 1.1 + a.phase)) * 0.03; // hung head
          breathe = 1 + Math.sin(t * 0.9 + a.phase) * 0.01;
        } else if (pm === 'glad') {
          pitch = Math.max(0, Math.sin(t * 2.0 + a.phase)) * 0.06;
          bobY = Math.max(0, Math.sin(t * 2.6 + a.phase)) * 0.03;
          breathe = 1 + Math.sin(t * 1.8 + a.phase) * 0.015;
        } else {
          pitch = Math.max(0, Math.sin(t * 1.8 + a.phase)) * 0.05;
          breathe = 1 + Math.sin(t * 1.5 + a.phase) * 0.012;
        }
      } else if (this.serving.has(sl.key)) {
        // Merchant slice (2026-09-15): at the counter — face the customer
        // (facing keyed to their position), attentive nods; the handover beat
        // leans in over the counter. Drawn from the durable row, never from a
        // replayed event: a late observer sees the same beat.
        const fc = this.facing.get(sl.key);
        if (fc) yaw = Math.atan2(fc.x - sl.x, fc.z - sl.z);
        if (this.serving.get(sl.key) === 'handover') {
          pitch = 0.16 + Math.max(0, Math.sin(t * 2.4 + a.phase)) * 0.06;
          bobY = Math.max(0, Math.sin(t * 2.4 + a.phase)) * 0.02;
        } else {
          pitch = Math.max(0, Math.sin(t * 1.6 + a.phase)) * 0.05;
          breathe = 1 + Math.sin(t * 1.3 + a.phase) * 0.012;
        }
      } else if (sl.seat && !sl.moving) {
        // AT A STATION: face the way the formation faces (the bench looks at the plaza, the circle at its
        // teller). The far twin has no clip, so the whole body carries the activity in small: a dancer hops,
        // a driller lunges, the rest breathe; a seated twin sinks to the seat it is drawn on.
        yaw = sl.seat.yaw;
        if (st === 'dancing' || st === 'performing') { bobY = Math.max(0, Math.sin(t * 4.4 + a.phase)) * 0.12; yaw += Math.sin(t * 1.1 + a.phase) * 0.6; }
        else if (st === 'drilling' || st === 'wrestling' || st === 'exercising') { const l = Math.max(0, Math.sin(t * 3.4 + a.phase)); pitch = 0.08 + l * 0.16; bobY = l * 0.06; }
        else if (st === 'praying_kneel') { pitch = 0.3 + Math.sin(t * 0.8 + a.phase) * 0.04; }
        else if (st === 'storytelling' || st === 'teaching') { pitch = Math.sin(t * 1.9 + a.phase) * 0.05; yaw += Math.sin(t * 0.6 + a.phase) * 0.35; }
        else breathe = 1 + Math.sin(t * 1.2 + a.phase) * 0.012;
        if ((sl.seat.seat === 'chair' || sl.seat.seat === 'desk') && !this.rigLayer.owns(sl.key)) bobY -= 0.34; // the static twin settles onto the bench
      } else if (sl.moving) {
        bobY = Math.abs(Math.sin(t * 7 + a.phase)) * 0.07; // stride bounce
        pitch = 0.06;                                       // lean into the walk
      } else if (st === 'fighting') {
        // War skirmish (visible-combat wave): square up on the nearest other
        // fighter and trade sharp lunges — the melee reads from across the road.
        let fd = 14 * 14, fx = 0, fz = 0, foundFoe = false;
        for (let j = 0; j < n; j++) {
          if (j === i || this._vfSlots[j].a.state !== 'fighting') continue;
          const o = this._vfSlots[j];
          const d = (o.x - sl.x) ** 2 + (o.z - sl.z) ** 2;
          if (d < fd) { fd = d; fx = o.x; fz = o.z; foundFoe = true; }
        }
        if (foundFoe) yaw = Math.atan2(fx - sl.x, fz - sl.z);
        const lunge = Math.max(0, Math.sin(t * 5.2 + a.phase));
        pitch = 0.12 + lunge * 0.22;                        // strike bursts
        bobY = lunge * 0.09;
        breathe = 1 + Math.sin(t * 3 + a.phase) * 0.02;
      } else if (st === 'celebrating') {
        // Festival (living-events wave): face the fire, sway, hop — phase-offset
        // so the square reads as a crowd, not a chorus line.
        const fc = this.facing.get(sl.key);
        yaw = (fc ? Math.atan2(fc.x - sl.x, fc.z - sl.z) : yaw) + Math.sin(t * 0.9 + a.phase) * 0.5;
        bobY = Math.max(0, Math.sin(t * 4.2 + a.phase)) * 0.12;
        pitch = Math.sin(t * 2.1 + a.phase) * 0.06;
        breathe = 1 + Math.sin(t * 4.2 + a.phase) * 0.02;
      } else if (st === 'feasting') {
        // Feast: lean to the table, bites, table talk.
        const fc = this.facing.get(sl.key);
        yaw = (fc ? Math.atan2(fc.x - sl.x, fc.z - sl.z) : yaw) + Math.sin(t * 0.6 + a.phase) * 0.3;
        pitch = 0.12 + Math.max(0, Math.sin(t * 1.7 + a.phase)) * 0.1;
      } else if (st === 'praying') {
        // Rite: bowed toward the altar, the slow breath of a congregation.
        const fc = this.facing.get(sl.key);
        if (fc) yaw = Math.atan2(fc.x - sl.x, fc.z - sl.z);
        pitch = 0.28 + Math.sin(t * 0.8 + a.phase) * 0.04;
        breathe = 1 + Math.sin(t * 1.0 + a.phase) * 0.01;
      } else if (st === 'listening' || st === 'attending') {
        // Hustings / investiture / speech: face the speaker, attentive nods.
        const fc = this.facing.get(sl.key);
        if (fc) yaw = Math.atan2(fc.x - sl.x, fc.z - sl.z);
        pitch = Math.max(0, Math.sin(t * 1.3 + a.phase)) * 0.05;
        breathe = 1 + Math.sin(t * 1.4 + a.phase) * 0.012;
      } else if (st === 'working') {
        const w = Math.sin(t * 3.6 + a.phase);              // the work swing
        pitch = 0.1 + w * 0.1;
        bobY = Math.min(0, w) * 0.04;
      } else if (st === 'trading') {
        pitch = Math.sin(t * 2.1 + a.phase) * 0.05;         // haggle nod
        yaw += Math.sin(t * 0.7 + a.phase) * 0.12;
      } else if (st === 'chatting') {
        let bd = 8 * 8, bx = 0, bz = 0, found = false;
        for (let j = 0; j < n; j++) {
          if (j === i) continue;
          const o = this._vfSlots[j];
          const d = (o.x - sl.x) ** 2 + (o.z - sl.z) ** 2;
          if (d < bd) { bd = d; bx = o.x; bz = o.z; found = true; }
        }
        if (found) yaw = Math.atan2(bx - sl.x, bz - sl.z);  // FACE the partner
        pitch = Math.max(0, Math.sin(t * 2.4 + a.parity * Math.PI)) * 0.09; // alternating turns
        breathe = 1 + Math.sin(t * 1.6 + a.phase) * 0.012;
      } else if (st === 'social') {
        pitch = Math.max(0, Math.sin(t * 2 + a.phase)) * 0.06;
        yaw += Math.sin(t * 0.5 + a.phase) * 0.2;
      } else if (st === 'sleeping') {
        // Asleep in the dwelling: reclined on the bed/bedroll, the slow
        // breath of deep night. Never gaze-tracks the player.
        if (sl.home) yaw = sl.home.yaw;
        pitch = sl.home?.lying ? -1.15 : 0.3;               // reclined; slumped upright without a bed spot
        breathe = 1 + Math.sin(t * 0.7 + a.phase) * 0.022;
      } else if (st === 'playing') {
        // A child at play: bouncing hops and quick turns around the rug.
        bobY = Math.max(0, Math.sin(t * 5.5 + a.phase)) * 0.16;
        yaw += Math.sin(t * 1.7 + a.phase) * 0.9;
        pitch = Math.sin(t * 2.6 + a.phase) * 0.05;
        breathe = 1 + Math.sin(t * 5.5 + a.phase) * 0.02;
      } else if (st === 'calling') {
        // The town crier (civic wave): planted, head up, calling to the four
        // winds — a slow full turn with proclamation bursts.
        yaw = a.yaw + t * 0.55 + Math.sin(t * 1.3 + a.phase) * 0.1;
        pitch = -0.12 + Math.max(0, Math.sin(t * 2.2 + a.phase)) * 0.14;
        bobY = Math.max(0, Math.sin(t * 2.2 + a.phase)) * 0.05;
        breathe = 1 + Math.sin(t * 2.2 + a.phase) * 0.02;
      } else if (st === 'resting') {
        if (sl.home) yaw = sl.home.yaw;                     // seated: face the grouping
        breathe = 1 + Math.sin(t * 1.1 + a.phase) * 0.014;  // slow breath
        yaw += Math.sin(t * 0.23 + a.phase) * 0.25;         // idle gaze drift
      } else {
        breathe = 1 + Math.sin(t * 1.4 + a.phase) * 0.01;
      }
      // THE VENUE COUNTER (slice E): a body shown at the counter faces the way its anchor does — the keeper the room,
      // the customer the counter. (`home` is set for a non-home state only by the venue spot above.)
      if (sl.home && !sl.moving && !HOME_STATES.has(st)) yaw = sl.home.yaw;
      // CIVIL INATTENTION (2026-09-10, "they all approach me at once"): a
      // settled versona near the player gives a BRIEF glance — the orient
      // beat on first notice, then private glance cycles, a held look only
      // inside the personal zone — and at most GAZE_MAX_LOOKERS look at once
      // (lib/versona_gaze). The old rule locked every face within 4.5 m on
      // the player and held it: a plaza of twenty staring at the traveler.
      // The turn is smoothed so a capped-out glance never snaps in.
      // ATTENTION PRIORITY (2026-09-15, the versona review): that cap rations
      // CASUAL glances only. A partner (in conversation / served at the
      // counter), a threat (aimed at, struck, or winding up on the player) or
      // a startle (a shot landing within STARTLE_M — head to the IMPACT) wins
      // outright, never counts against the cap, and overrides work, chat, a
      // rite's facing or a festival's fire; only sleepers, fighters and
      // walkers are left to their own pose.
      if (!sl.moving && st !== 'fighting' && st !== 'sleeping') {
        const pd = (px - sl.x) ** 2 + (pz - sl.z) ** 2;
        const near = pd < GAZE_NOTICE_M * GAZE_NOTICE_M;
        if (near && !sl.near) sl.noticedAt = t;
        sl.near = near;
        const casualAllowed = st !== 'working' && st !== 'chatting' && st !== 'calling' && st !== 'praying' && st !== 'praying_kneel' && !this.facing.has(sl.key) && !sl.seat;
        const vkeyAttn = 'versona:' + sl.key;
        let startleAt = -Infinity, sx = 0, sz = 0;
        if (this.startles.length > 0) {
          // The nearest impact; at equal distance the NEWEST (a burst of shots on one spot keeps the
          // head turned — the oldest of a stack is already in its release ramp).
          let best = Infinity;
          for (let k = this.startles.length - 1; k >= 0; k--) {
            const s = this.startles[k];
            const d2 = (s.x - sl.x) ** 2 + (s.z - sl.z) ** 2;
            if (d2 < STARTLE_M * STARTLE_M && d2 < best) { best = d2; startleAt = s.at; sx = s.x; sz = s.z; }
          }
        }
        const at = attentionOf({
          phase: a.phase, t, dist: Math.sqrt(pd), familiar: this.familiarIds.has(sl.key), noticedAt: sl.noticedAt,
          partner: sl.talking || this.servingMe.has(sl.key),
          aimedAt: this.aimedVersona === sl.key,
          windingOnMe: (windups.size > 0 && (windups.get(vkeyAttn)?.targetMe ?? false)) || (this.fearedIds.size > 0 && this.fearedIds.has(sl.key) && pd < (GAZE_NOTICE_M * 3) ** 2), // a versona who fears you watches you (disposition)
          struckAt: lastStruckByPlayer(vkeyAttn) / 1000,
          startleAt, lookers, casualAllowed,
        });
        if (at.kind !== 'none') { sl.attn = at.kind; if (at.kind === 'startle') { sl.attnX = sx; sl.attnZ = sz; } else { sl.attnX = px; sl.attnZ = pz; } }
        const turn = at.kind === 'casual' || at.kind === 'none' ? CASUAL_TURN : PRIORITY_TURN;
        sl.gazeW += (at.weight - sl.gazeW) * turn;
        if (sl.gazeW > 0.02) {
          if (countsAgainstCap(sl.attn)) lookers++;
          else if (sl.attn === 'partner') attn.partner++; else if (sl.attn === 'threat') attn.threat++; else if (sl.attn === 'startle') attn.startle++;
          const lookYaw = Math.atan2(sl.attnX - sl.x, sl.attnZ - sl.z);
          sl.lookYaw = lookYaw;
          // HEAD LAYER (2026-09-15): a rigged near actor turns its HEAD for a casual glance and keeps
          // its body; a partner / threat / blast still turns the body (and the head follows).
          if (!(sl.attn === 'casual' && this.rigLayer.owns(sl.key))) yaw = blendYaw(yaw, lookYaw, sl.gazeW);
        } else { sl.attn = 'none'; sl.lookYaw = null; }
      } else { sl.near = false; sl.gazeW = 0; sl.attn = 'none'; sl.lookYaw = null; }
      // COMBAT WAVE: a versona winding up on a player (its npc_strike row is
      // the telegraph, WildsModule mirrors it) squares up on the target, leans
      // back and swells over the windup, then lunges at the server-timed
      // snapshot — the same tell a creature gives, drawn from the same map.
      // (skip the per-versona key build when no tell is live anywhere — the
      // common frame; ~2,000 residents would otherwise allocate a string each)
      const vkey = windups.size > 0 || lunges.size > 0 ? 'versona:' + sl.key : '';
      const wv = vkey ? windups.get(vkey) : undefined;
      const lv = vkey ? lungeAt(vkey, nowMs) : -1;
      if (wv || lv >= 0) {
        const pose = windupPose(nowMs, wv?.start ?? 0, wv?.ms ?? 0, lv);
        if (wv?.targetMe) yaw = Math.atan2(px - sl.x, pz - sl.z);
        pitch = pose.pitch;
        bobY = 0;
        breathe = pose.scale;
      }
      sl.yaw = yaw;
      sl.bob = bobY;
      p.set(sl.x, sl.gy + target.yOffset + bobY, sl.z);
      q.setFromAxisAngle(up, yaw);
      if (pitch !== 0) {
        this._vfQ2.setFromAxisAngle(this._vfX, pitch);
        q.multiply(this._vfQ2);
      }
      s.set(1, breathe, 1);
      const i2 = counts.get(target) ?? 0;
      if (this.rigLayer.owns(sl.key)) {
        target.set(i2, null); // the skinned actor draws this versona
        this.rigLayer.pose(sl.key, this.crowdKeys.get(a.crowd) ?? '', {
          // (slice L) under the itinerary the clip is told what is really under the body: a seat's `chair`, a bed's `cot`
          x: sl.x, y: sl.gy, z: sl.z, yaw, state: sl.life && !sl.talking ? sl.life.state : st, moving: sl.moving, talking: sl.talking, sprinting: false, job: sl.job, elder: sl.elder,
          phase: a.phase, parity: a.parity, winding: !!wv || lv >= 0, hurt: isHurt(vkey, nowMs),
          // THE MELEE BEAT: the crowd twin beyond NEAR_M lunges procedurally (the
          // `st === 'fighting'` pose above); the rig has to be TOLD to swing, or the
          // fight stops the moment the player is close enough to be shown a rig.
          swinging: st === 'fighting' && !sl.moving && meleeSwinging(t, a.phase),
          lookYaw: sl.lookYaw,
          seat: sl.life ? sl.life.seat : sl.seat?.seat ?? null, handProp: handPropFor(st),
          carrying: st === 'to_market' || st === 'carting',
          swimming: sl.swimming,
          indoor: inRoom, // (slice H) the actor wears its materials' room-lit twins while it stands in the player's room
        });
      } else {
        target.set(i2, m.compose(p, q, s));
      }
      counts.set(target, i2 + 1);
    }
    this.rigLayer.update(nowMs / 1000, atlas.localX, atlas.localZ); // cadence by distance to the player
    s.set(1, 1, 1);
    this.lastLookers = lookers;
    this.updateLifeLayers(n, t);
    this.updateWarClashes(t);
    // The actual count-pack happens in the RenderSub culler; this just stages the logical instances.
  }

  // ── SOCIETY WAVE: activity stations (props + seats) ──────────────────────
  /**
   * Every 2 s: which stations stand near the player, and their props (built inside STATION_NEAR_M, dropped
   * past STATION_FAR_M — one merged mesh each, shared geometry). Every 250 ms: deal the seats among the
   * versonas that have ARRIVED in a hosted state. Both are cheap and neither runs per versona per frame.
   */
  private updateStations(now: number): void {
    const px = atlas.localX, pz = atlas.localZ;
    if (now - this.stationsAt > 2000) {
      this.stationsAt = now;
      const tbl = (atlas.conn?.db as unknown as { activityStation?: { iter(): Iterable<{ stationId: bigint; worldId: bigint; kind: string; x: number; z: number; facing: number; capacity: number }> } } | undefined)?.activityStation;
      const near: StationRow[] = [];
      const keep = new Set<string>();
      if (tbl && atlas.currentWorldId !== null) {
        for (const r of tbl.iter()) {
          if (r.worldId !== atlas.currentWorldId) continue;
          const d = Math.hypot(r.x - px, r.z - pz);
          if (d > STATION_FAR_M) continue;
          const id = r.stationId.toString();
          const have = this.stationProps.has(id);
          if (d > STATION_NEAR_M && !have) continue;
          near.push({ id, kind: r.kind, x: r.x, z: r.z, facing: r.facing, capacity: r.capacity });
          keep.add(id);
          if (!have) {
            const mesh = buildStationProp(r.kind);
            if (mesh) {
              mesh.position.set(r.x, this.versonaGround ? this.versonaGround(r.x, r.z) : 0, r.z);
              mesh.rotation.y = r.facing;
              mesh.updateMatrix();
              this.scene.add(mesh);
              this.stationProps.set(id, mesh);
            }
          }
        }
      }
      for (const [id, mesh] of this.stationProps) {
        if (keep.has(id)) continue;
        this.scene.remove(mesh); // shared geometry + material: nothing to dispose
        this.stationProps.delete(id);
      }
      this.nearStations = near;
    }
    if (now - this.seatsAt > 250) {
      this.seatsAt = now;
      if (this.nearStations.length === 0) { if (this.stationSeats.size > 0) this.stationSeats = new Map(); return; }
      const people = this._people; people.length = 0;
      for (const a of this.versonaAnim.values()) {
        if (now - a.t0 < a.dur && !a.route) continue;          // still gliding to the station
        const act = activityDef(a.state);
        if (!act || act.stations.length === 0) continue;
        if (Math.abs(a.toX - px) > STATION_FAR_M || Math.abs(a.toZ - pz) > STATION_FAR_M) continue;
        people.push({ key: a.key, state: a.state, x: a.toX, z: a.toZ });
      }
      this.stationSeats = assignSeats(this.nearStations, people);
    }
  }

  /** Diagnostics for probes: the stations near the player, their props and the seats dealt. */
  stationDiag(): { near: number; props: number; seated: number; byStation: Record<string, number> } {
    const byStation: Record<string, number> = {};
    for (const s of this.stationSeats.values()) { const st = this.nearStations.find((n) => n.id === s.stationId); const k = st?.kind ?? '?'; byStation[k] = (byStation[k] ?? 0) + 1; }
    return { near: this.nearStations.length, props: this.stationProps.size, seated: this.stationSeats.size, byStation };
  }

  // ── The legibility layer: carried crates + venue labels ──────────────────
  private readonly _vfQ2 = new THREE.Quaternion();
  private readonly _vfX = new THREE.Vector3(1, 0, 0);
  private readonly _vfSlots: Array<{ a: VersonaAnimEntry; key: string; x: number; z: number; gy: number; yaw: number; bob: number; moving: boolean; indoors: string | null; talking: boolean; home: { x: number; z: number; y: number; yaw: number; lying: boolean } | null; job: string; jobAt: number; elder: boolean; near: boolean; noticedAt: number; gazeW: number; attn: AttentionKind; attnX: number; attnZ: number; lookYaw: number | null; seat: Seat | null; bedY: number; swimming: boolean; waterAt: number; life: LifeRig | null }> = [];
  /** Attention priority (2026-09-15): the versona the player's drawn weapon is on (soft target id). */
  private aimedVersona: string | null = null;
  /** Recent impacts (absolute metres, seconds) — pruned past STARTLE_S; at most 8 live. */
  private readonly startles: Array<{ x: number; z: number; at: number }> = [];
  private readonly attnCounts = { partner: 0, threat: 0, startle: 0 };
  /** Bodies walking a route-ahead polyline this frame (versona_route), those waiting at the horizon, and forecasts voided since load. */
  private routeCount = 0;
  private routeHeld = 0;
  private routeVoids = 0;
  /** A shot landed here: nearby settled versonas turn their heads to it (attention priority). */
  startle(x: number, z: number, atS: number): void {
    this.startles.push({ x, z, at: atS });
    if (this.startles.length > 8) this.startles.shift();
  }
  /** Versonas who know the player's face (playerRelation: seen thrice or spoken with) — they glance longer (versona_gaze). */
  private readonly familiarIds = new Set<string>();
  private familiarAt = 0;
  /** slots filled by the last frame (accessors below read them) */
  private _vfN = 0;
  private lastLookers = 0;
  /** interiorSpec is not free — cache per enterable id (home-life wave). */
  private readonly homeSpecs = new Map<string, InteriorSpec>();
  /** The furnish-pack plan of a deed (the pieces its residents may take), cached per (deed, pack); [] when unfurnished. */
  private deedPlanFor(b: EnterableBuilding, spec: InteriorSpec): PlanSlot[][] {
    let pack = 0;
    try {
      const db = atlas.conn?.db as unknown as { structureFurnish?: { structureId: { find(id: bigint): { pack: number } | undefined } } } | undefined;
      pack = db?.structureFurnish?.structureId.find(BigInt(b.id.slice(5)))?.pack ?? 0;
    } catch { pack = 0; }
    const key = `${b.id}|${pack}`;
    let plan = this.deedPlans.get(key);
    if (!plan) {
      if (this.deedPlans.size > 64) this.deedPlans.clear();
      const fp = pack ? furnishPack(pack) : null;
      plan = fp ? packPlan(fp, resolvePurpose(b.id, b.key).purpose, { hw: spec.hw, hd: spec.hd, stair: spec.stair }, spec.floors) : [];
      this.deedPlans.set(key, plan);
    }
    return plan;
  }
  private readonly deedPlans = new Map<string, PlanSlot[][]>();

  // ── RESIDENTS USE THE WHOLE BUILDING (the interiors programme, slice L) ──────────────────────────────────────────
  private lifeMemo: { id: string; version: number; liftKey: string; room: LifeRoom | null } | null = null;
  private lifePlanMemo: { room: LifeRoom; k: number; sig: string; plan: LifePlan; states: unknown } | null = null;
  private lifeFrame = -1;
  private lifeCtx: { room: LifeRoom; plan: LifePlan; tS: number; cabHeld: boolean } | null = null;
  private lifeClock = 0;
  private lifeWho: { at: number; id: string; list: LifeResident[]; sig: string } | null = null;
  /** The enterables registry's size and a stamp bumped when it changes — every body's `inB` memo is keyed by it. */
  private enterSize = -1;
  private enterStamp = 0;
  private readonly lifeStats = { residents: 0, anchors: 0, walking: 0, onStairs: 0, inCab: 0, hidden: 0, plans: 0, rooms: 0, slot: 0 };
  /**
   * The itinerary's context for the interior the player stands in, once per frame: the room (rebuilt when the visit's
   * plan changes or the lift's service does), the residents (re-gathered twice a second), the slot's plan and the
   * clock. Null = today's placement (the lever, a legacy / bespoke room, the plan not landed, no routed anchor).
   */
  private lifeFor(b: EnterableBuilding, act: ActiveInterior | null): { room: LifeRoom; plan: LifePlan; tS: number; cabHeld: boolean } | null {
    if (this.lifeFrame === this.clearFrame) return this.lifeCtx;
    this.lifeFrame = this.clearFrame;
    this.lifeCtx = null;
    const share = liftShare();
    if (share.id !== b.id) share.residentCab = null;
    if (!interiorLifeEnabled() || !act || act.id !== b.id || act.route !== 'descriptor' || !act.descriptor || !act.view) return null;
    const svc = share.id === b.id ? share.service : null;
    // A BOUND VENUE (slice E): its counter's staff side, the customer's place and the queue are never a resident's.
    // Asked of the binding table itself (the venue module's own rule, under its lever) — the same answer on every
    // client and on the first frame; the published counter is the fallback while the seed is not in hand.
    const inCommons = atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId;
    const bound = inCommons && this.commonsSeedForChunks && b.id.startsWith('settle:') ? venueOfEnterable(this.commonsSeedForChunks, b) : null;
    const vcb = bound ? null : venueCounter();
    const venueKind = bound ? bound.kind : vcb && vcb.id === b.id ? vcb.binding.kind : null;
    const liftKey = `${svc?.runs ? svc.served.join(',') : ''}|${venueKind ?? ''}|${letStoreVersion()}`; // (slice H) + the let store: a lease signed or ended re-deals the room
    let memo = this.lifeMemo;
    if (!memo || memo.id !== b.id || memo.version !== act.version || memo.liftKey !== liftKey) {
      const room = lifeRoomForVisit(b, act, svc, venueKind); // the canonical anchors (the same on every client) + what THIS client draws
      memo = this.lifeMemo = { id: b.id, version: act.version, liftKey, room };
      this.lifeStats.rooms++;
      this.lifePlanMemo = null;
    }
    const room = memo.room;
    if (!room) return null;
    // the residents: every body in a home state whose row stands in this building (twice a second — rows move rarely)
    const nowMs = performance.now();
    let who = this.lifeWho;
    if (!who || who.id !== b.id || nowMs - who.at > 500) {
      const list: LifeResident[] = [];
      for (const e of this.versonaAnim.values()) {
        if (!HOME_STATES.has(e.state)) continue;
        if (Math.abs(e.toX - b.x) > 40 || Math.abs(e.toZ - b.z) > 40 || enterableAt(e.toX, e.toZ)?.id !== b.id) continue;
        const child = (this.crowdKeys.get(e.crowd) ?? '') === 'avatar:child_colonist';
        list.push({ id: e.key, state: e.state, child, elder: !child && founderIsElder(e.key) });
      }
      list.sort((x, y) => (x.id < y.id ? -1 : x.id > y.id ? 1 : 0));
      who = this.lifeWho = { at: nowMs, id: b.id, list, sig: list.map((r) => `${r.id}:${lifeClass(r.state)}`).join('|') }; // the CLASS: a resting ↔ chatting flip re-plans nothing
    }
    // the clock: wall seconds, never backwards (this dev box's clock steps back; a body must not)
    this.lifeClock = Math.max(this.lifeClock, Date.now() / 1000);
    const tS = this.lifeClock, k = slotOf(tS);
    let pm = this.lifePlanMemo;
    if (!pm || pm.room !== room || pm.k !== k || pm.sig !== who.sig) {
      pm = this.lifePlanMemo = { room, k, sig: who.sig, plan: planLife(room, who.list, k, this.todAtFn()), states: who };
      this.lifeStats.plans++;
    }
    else if (pm.states !== who) { for (const r of who.list) { const pr = pm.plan.residents.get(r.id); if (pr) pr.state = r.state; } pm.states = who; } // the live state (a standing body's clip) — never a re-plan
    share.residentCab = residentCabAt(pm.plan, tS);
    this.lifeStats.residents = who.list.length; this.lifeStats.anchors = room.anchors.length; this.lifeStats.slot = k;
    this.lifeCtx = { room, plan: pm.plan, tS, cabHeld: share.id === b.id && share.playerBusy };
    return this.lifeCtx;
  }
  /** The sky's hour (0..1) at an instant, from the replicated sky row and its own stamp — the same on every client. */
  private todAtFn(): (tS: number) => number {
    let tod = 0.5, cycle = 0, at = 0;
    try {
      const conn = atlas.conn, wid = atlas.currentWorldId;
      const row = conn && wid !== null ? (conn.db as unknown as { skyState: { worldId: { find(id: bigint): { timeOfDay: number; cycleSeconds: number; updatedAt: { microsSinceUnixEpoch: bigint } } | undefined } } }).skyState.worldId.find(wid) : undefined;
      if (row) { tod = row.timeOfDay; cycle = row.cycleSeconds; at = Number(row.updatedAt.microsSinceUnixEpoch / 1000n) / 1000; }
    } catch { /* no sky row: noon */ }
    return (tS: number): number => { if (cycle <= 0) return tod; const v = (tod + (tS - at) / cycle) % 1; return v < 0 ? v + 1 : v; };
  }
  /** Probe / gate facts about the residents' itinerary (`__versonaLife().life()`). */
  lifeDiag(): Record<string, unknown> {
    const ctx = this.lifeCtx, out: Array<Record<string, unknown>> = [];
    let walking = 0, onStairs = 0, inCab = 0;
    if (ctx) for (const id of ctx.plan.residents.keys()) {
      const p = poseAt(ctx.room, ctx.plan, id, ctx.tS);
      if (!p) { out.push({ id, mode: 'out' }); continue; }
      if (p.mode === 'walk') walking++; else if (p.mode === 'stairs') onStairs++; else if (p.mode === 'cab') inCab++;
      out.push({ id, mode: p.mode, floor: p.floor, anchor: p.anchor, x: Math.round(p.x * 100) / 100, y: Math.round(p.y * 100) / 100, z: Math.round(p.z * 100) / 100, rig: p.rig, inCab: p.inCab });
    }
    return { enabled: interiorLifeEnabled(), on: !!ctx, inside: atlas.interiorId, ...this.lifeStats, walking, onStairs, inCab, tS: ctx?.tS ?? 0, rides: ctx?.plan.rides ?? [], trips: ctx ? ctx.plan.trips.size : 0, cab: liftShare().residentCab, poses: out };
  }
  private homeSpecFor(b: EnterableBuilding): InteriorSpec {
    let s = this.homeSpecs.get(b.id);
    if (!s) {
      if (this.homeSpecs.size > 128) this.homeSpecs.clear(); // bound the cache across visited towns
      s = interiorSpec(b.w, b.h, b.d, b.id.startsWith('deed:') ? b.key : undefined); // a deed's shell is cut with its key (BuildingInteriorModule does the same)
      this.homeSpecs.set(b.id, s);
    }
    return s;
  }
  private crateMesh: THREE.InstancedMesh | null = null;
  // ── SOCIETY WAVE: activity stations — the props near the player and the seats dealt at them ──
  /** Stations within STATION_NEAR_M (re-gathered every 2 s; the table is a few thousand static rows). */
  private nearStations: StationRow[] = [];
  private stationProps = new Map<string, THREE.Mesh>();
  private stationSeats = new Map<string, Seat>();
  private stationsAt = 0;
  private seatsAt = 0;
  private readonly _people: Person[] = [];
  private lifeDiagTick = 0;
  /** Town-slot resolution cache (feeds the PHYSICAL bazaar terminal; the
   *  floating venue NAME sprites moved to the local map, 2026-08-13). */
  private labelSeed = '';
  private sitesMemo: SharedSite[] | null = null;
  private labelSiteId = -2;
  private labelSlots: TownSlot[] | null = null;
  private labelCiv: 'a' | 'b' = 'a';
  /** The bazaar terminal pedestal parked at the nearest market slot. */
  private bazaarTerminal: THREE.Group | null = null;
  /** Dev override for the life-layer origin (headless probes can't stand in
   *  a crowd — the row-freeze bug; see memory: versona teleport adoption). */
  private lifeFocus: { x: number; z: number } | null = null;

  private clashScenes = new Map<string, { banner: THREE.Sprite; sparks: THREE.Sprite[]; tex: THREE.CanvasTexture }>();

  private clashBannerTexture(fallen: string): THREE.CanvasTexture {
    const cv = document.createElement('canvas');
    cv.width = 512; cv.height = 128;
    const g = cv.getContext('2d')!;
    g.fillStyle = 'rgba(28,10,10,0.82)';
    g.fillRect(0, 0, 512, 128);
    g.strokeStyle = '#a04338';
    g.lineWidth = 6;
    g.strokeRect(3, 3, 506, 122);
    g.textAlign = 'center';
    g.font = '700 44px system-ui';
    g.fillStyle = '#ffd9c9';
    g.fillText('⚔ SKIRMISH', 256, 52);
    g.font = '26px system-ui';
    g.fillStyle = '#e8b9a8';
    g.fillText(fallen ? `${fallen} has fallen` : 'militias clash on the road', 256, 96);
    return new THREE.CanvasTexture(cv);
  }

  private updateWarClashes(t: number): void {
    interface ClashRow { clashId: bigint; x: number; z: number; fallenName: string; endsAt: { microsSinceUnixEpoch: bigint } }
    const conn = atlas.conn;
    const tbl = (conn?.db as unknown as { warClash?: { iter(): Iterable<ClashRow> } } | undefined)?.warClash;
    const nowUs = BigInt(Date.now()) * 1000n;
    const live = new Set<string>();
    // Clashes are Commons rows: in a personal world at the same x/z none stand (the loop below then
    // retires every scene) — bug sweep 2026-09-22.
    const inCommons = atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId;
    try {
      for (const c of inCommons ? (tbl?.iter() ?? []) : []) {
        if (c.endsAt.microsSinceUnixEpoch < nowUs) continue;
        const d = Math.hypot(c.x - atlas.localX, c.z - atlas.localZ);
        if (d > 600) continue; // scene only when the player could walk over
        const key = c.clashId.toString();
        live.add(key);
        let sc = this.clashScenes.get(key);
        if (!sc) {
          const tex = this.clashBannerTexture(c.fallenName);
          const banner = new THREE.Sprite(markMrtOverlay(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true })));
          banner.scale.set(10, 2.5, 1);
          const gy = this.versonaGround ? this.versonaGround(c.x, c.z) : 0;
          banner.position.set(c.x, gy + 7, c.z);
          this.versonaRoot.add(banner);
          const sparks: THREE.Sprite[] = [];
          for (let i = 0; i < 4; i++) {
            const sp = new THREE.Sprite(markMrtOverlay(new THREE.SpriteMaterial({
              color: i % 2 ? 0xffd27a : 0xcfe8ff, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
            })));
            sp.scale.setScalar(0.5);
            this.versonaRoot.add(sp);
            sparks.push(sp);
          }
          sc = { banner, sparks, tex };
          this.clashScenes.set(key, sc);
        }
        // Sparks flicker among the fighters (clash-point jitter reads as blows).
        for (let i = 0; i < sc.sparks.length; i++) {
          const sp = sc.sparks[i];
          const ph = t * (7 + i) + i * 2.1;
          const on = Math.sin(ph) > 0.55;
          if (on && !sp.visible) {
            // A spark igniting IS a blow landing — ring it, distance-scaled.
            const dClash = Math.hypot(c.x - atlas.localX, c.z - atlas.localZ);
            if (dClash < 500) sfxAt('sfx_melee_clash', { x: c.x, y: atlas.localY + 1, z: c.z }, { gain: Math.max(0.05, 0.35 - dClash * 0.0007), rate: 0.9 + (i % 3) * 0.08, cooldownMs: 260, caption: 'Blades clash' });
          }
          sp.visible = on;
          if (on) {
            const sx = c.x + Math.sin(ph * 1.7 + i) * 3.2;
            const sz = c.z + Math.cos(ph * 1.3 + i * 2) * 3.2;
            const gy = this.versonaGround ? this.versonaGround(sx, sz) : 0;
            sp.position.set(sx, gy + 1.1 + Math.abs(Math.sin(ph * 2)) * 0.6, sz);
            (sp.material as THREE.SpriteMaterial).opacity = 0.55 + Math.abs(Math.sin(ph * 3)) * 0.45;
          }
        }
        sc.banner.material.opacity = 0.75 + Math.sin(t * 2.2) * 0.15;
      }
    } catch { /* pre-publish server */ }
    for (const [key, sc] of this.clashScenes) {
      if (live.has(key)) continue;
      this.versonaRoot.remove(sc.banner);
      // (bug sweep) deferred — this runs inside the render frame that may
      // still submit these sprites (the warp-teardown law; HealthBars idiom).
      disposeLater(sc.banner.material);
      disposeLater(sc.tex);
      for (const sp of sc.sparks) { this.versonaRoot.remove(sp); disposeLater(sp.material); }
      this.clashScenes.delete(key);
    }
  }

  /** Chatter wave: the versonas in conversation near a point, from the last rendered frame
   *  (id, rendered position, state, whether they are inside a dwelling). */
  /** The body key a versona is drawn with (its crowd) — the voice follows the body (versona_voice). */
  bodyKeyOf(id: string): string | null {
    const a = this.versonaAnim.get(id);
    return a ? (this.crowdKeys.get(a.crowd) ?? null) : null;
  }

  /** THE INDOOR-TARGET LAW (2026-09-22): where a drawn versona stands — the
   *  interior id it is seated in, null when outdoors, undefined when it is not
   *  in the drawn set this frame. The T key refuses a target through a wall. */
  versonaIndoors(id: string): string | null | undefined {
    for (let i = 0; i < this._vfN; i++) { const sl = this._vfSlots[i]; if (sl.key === id) return sl.indoors; }
    return undefined;
  }
  /** INDOOR COMBAT (slice K, 2026-10-02): a drawn versona's seat this frame — the interior it is seated in (null =
   *  outdoors) and the height its feet are DRAWN at. The attack's candidate gather keeps a body in another building,
   *  or on another storey of mine, out of the pick (WildsModule); undefined = not in the drawn set. Read-only. */
  versonaSeat(id: string): { indoors: string | null; y: number } | undefined {
    for (let i = 0; i < this._vfN; i++) { const sl = this._vfSlots[i]; if (sl.key === id) return { indoors: sl.indoors, y: sl.gy }; }
    return undefined;
  }

  chattingSlots(px: number, pz: number, rangeM: number): Array<{ id: string; x: number; z: number; state: string; indoors: boolean }> {
    const out: Array<{ id: string; x: number; z: number; state: string; indoors: boolean }> = [];
    const r2 = rangeM * rangeM;
    for (let i = 0; i < this._vfN; i++) {
      const sl = this._vfSlots[i];
      if (!sl) break;
      const st = sl.a.state;
      if (st !== 'chatting' && st !== 'social') continue;
      if ((sl.x - px) ** 2 + (sl.z - pz) ** 2 > r2) continue;
      out.push({ id: sl.key, x: sl.x, z: sl.z, state: st, indoors: sl.indoors !== null });
    }
    return out;
  }

  /** Footprint wave: the versonas WALKING near a point this frame, outdoors (id, feet position, heading). */
  walkers(px: number, pz: number, rangeM: number): Array<{ id: string; x: number; y: number; z: number; yaw: number }> {
    const out: Array<{ id: string; x: number; y: number; z: number; yaw: number }> = [];
    const r2 = rangeM * rangeM;
    for (let i = 0; i < this._vfN; i++) {
      const sl = this._vfSlots[i];
      if (!sl || !sl.moving || sl.indoors !== null) continue;
      if ((sl.x - px) ** 2 + (sl.z - pz) ** 2 > r2) continue;
      out.push({ id: sl.key, x: sl.x, y: sl.bedY, z: sl.z, yaw: sl.yaw }); // the BED: the footprint module reads the water depth over it
    }
    return out;
  }

  /** npc_agent.job for a versona id ('' when unknown) — the task-clip selector. */
  private jobOf(id: string): string {
    const db = atlas.conn?.db as unknown as { npcAgent?: { versonaId: { find(k: bigint): { job: string } | undefined } } } | undefined;
    if (!db?.npcAgent) return '';
    try { return db.npcAgent.versonaId.find(BigInt(id))?.job ?? ''; } catch { return ''; }
  }

  /** The versonas who know the player's face: seen thrice or spoken with, never a nemesis (playerRelation). */
  /** Versonas who FEAR this player (disposition, 2026-09-15): their eyes never leave you nearby. */
  private readonly fearedIds = new Set<string>();
  private refreshFamiliar(nowMs: number): void {
    this.familiarAt = nowMs;
    this.familiarIds.clear();
    this.fearedIds.clear();
    try {
      const me0 = atlas.identityHex;
      const evTbl = (atlas.conn?.db as unknown as { playerRelationEvent?: { iter(): Iterable<{ versonaId: bigint; identity: { toHexString(): string }; kind: string; weight: number; halfLifeS: number; at: { microsSinceUnixEpoch: bigint } }> } } | undefined)?.playerRelationEvent;
      if (me0 && evTbl) {
        const byV = new Map<string, Array<{ kind: string; weight: number; halfLifeS: number; at: { microsSinceUnixEpoch: bigint } }>>();
        for (const e of evTbl.iter()) {
          if (e.identity.toHexString() !== me0) continue;
          const k = e.versonaId.toString();
          let arr = byV.get(k); if (!arr) { arr = []; byV.set(k, arr); }
          arr.push(e);
        }
        for (const [k, evs] of byV) if (dispositionOf(evs, nowMs).fear >= FEAR_REFUSE) this.fearedIds.add(k);
      }
    } catch { /* table not subscribed on this page */ }
    const me = atlas.identityHex;
    const db = atlas.conn?.db as unknown as { playerRelation?: { iter(): Iterable<{ versonaId: bigint; identity: { toHexString(): string }; seenCount: number; talkStreak: number; nemesis: boolean }> } } | undefined;
    if (!me || !db?.playerRelation) return;
    try {
      for (const r of db.playerRelation.iter()) {
        if (!r.nemesis && (r.seenCount >= 3 || r.talkStreak >= 1) && r.identity.toHexString() === me) this.familiarIds.add(r.versonaId.toString());
      }
    } catch { /* table not subscribed on this page */ }
  }

  /** Sprite bubbles above the nearest actors + crates in haulers' arms —
   *  the "that one is smithing while that one hauls to market" moment. */
  private updateLifeLayers(n: number, t: number): void {
    if (this.crateMesh === null && n === 0) return;
    if (this.crateMesh === null) {
      const crate = new THREE.InstancedMesh(
        new THREE.BoxGeometry(0.36, 0.3, 0.3),
        new THREE.MeshStandardMaterial({ color: 0x8a5a2c, roughness: 0.85 }),
        CRATE_MAX,
      );
      crate.count = 0;
      crate.frustumCulled = false;
      this.crowdAnchor.group.add(crate);
      this.crateMesh = crate;
    }
    const px = this.lifeFocus?.x ?? atlas.localX;
    const pz = this.lifeFocus?.z ?? atlas.localZ;
    // Haulers carry a visible crate to market.
    const crate = this.crateMesh;
    if (crate) {
      let ci = 0;
      for (let i = 0; i < n && ci < CRATE_MAX; i++) {
        const sl = this._vfSlots[i];
        if (sl.a.state !== 'to_market' && !(sl.a.state === 'carting' && sl.moving)) continue; // haulers + porters on their round
        const d2 = (sl.x - px) ** 2 + (sl.z - pz) ** 2;
        if (d2 > 60 * 60) continue;
        this._vfP.set(
          sl.x + Math.sin(sl.yaw) * 0.42,
          sl.gy + 1.02 + sl.bob,
          sl.z + Math.cos(sl.yaw) * 0.42,
        );
        this._vfQ.setFromAxisAngle(this._vfUp, sl.yaw);
        this._vfP.x -= renderAnchorX(); this._vfP.z -= renderAnchorZ(); // anchor-relative (crowdAnchor child)
        crate.setMatrixAt(ci++, this._vfM.compose(this._vfP, this._vfQ, this._vfS.set(1, 1, 1)));
      }
      crate.count = ci;
      crate.instanceMatrix.needsUpdate = true;
    }
    // Town-slot resolution (feeds ONLY the physical bazaar terminal now —
    // the floating venue NAME sprites moved to the local map, 2026-08-13:
    // area names are map data, never world objects).
    if (this.labelSeed !== '') {
      if (!this.sitesMemo) this.sitesMemo = sharedSites(this.labelSeed);
      let site: SharedSite | null = null;
      let bd = Infinity;
      for (const st of this.sitesMemo) {
        const d = (st.centerX - px) ** 2 + (st.centerZ - pz) ** 2;
        if (d < bd) { bd = d; site = st; }
      }
      const fp = site ? SHARED_FOOTPRINT[site.tier] : 0;
      if (site && bd < (fp * 1.5) ** 2) {
        if (this.labelSiteId !== site.id) {
          this.labelSiteId = site.id;
          this.labelSlots = townSlots(this.labelSeed, site);
          this.labelCiv = civilizationOf(site.siteSeed) as 'a' | 'b';
        }
      } else if (this.labelSiteId !== -2) {
        this.labelSiteId = -2;
        this.labelSlots = null;
      }
      const slots = this.labelSlots;
      // The market slot carries a PHYSICAL terminal (SWG bazaar law: the
      // market IS a place you walk to); B within 7 m opens the bazaar.
      const market = slots?.find((sl) => sl.key === 'market') ?? null;
      if (market) {
        if (!this.bazaarTerminal) {
          const g = new THREE.Group();
          const base = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.7, 1.1, 10), new THREE.MeshBasicMaterial({ color: 0x2c3850 }));
          base.position.y = 0.55;
          const screen = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.9, 0.12), new THREE.MeshBasicMaterial({ color: 0x6fa9e8 }));
          screen.position.y = 1.7;
          screen.rotation.x = -0.25;
          g.add(base, screen);
          this.versonaRoot.add(g);
          this.bazaarTerminal = g;
        }
        const gy2 = this.versonaGround ? this.versonaGround(market.x, market.z) : 0;
        this.bazaarTerminal.position.set(market.x, gy2, market.z);
        this.bazaarTerminal.visible = true;
      } else if (this.bazaarTerminal) {
        this.bazaarTerminal.visible = false;
      }
    } else if (this.bazaarTerminal) {
      // Off the Commons there is no market slot — the hub terminal must not stand in a personal world
      // (the hub square overlaps the plot around the origin; B then opened the Commons bazaar there).
      this.bazaarTerminal.visible = false;
    }
    // Probe surface (~1 Hz): the states histogram + layer counts.
    if ((++this.lifeDiagTick & 63) === 0) {
      const states: Record<string, number> = {};
      for (let i = 0; i < n; i++) states[this._vfSlots[i].a.state] = (states[this._vfSlots[i].a.state] ?? 0) + 1;
      (window as unknown as Record<string, unknown>).__versonaLife = {
        life: () => this.lifeDiag(), // (slice L) the residents' itinerary in the interior the player stands in
        rendered: n, crates: this.crateMesh?.count ?? 0, states,
        // route ahead (2026-09-15): bodies walking a server-published polyline this frame
        routes: this.routeCount, routesHeld: this.routeHeld, routeVoids: this.routeVoids,
        // civil inattention (2026-09-10): how many versonas look at the player right now, and who knows their face
        gaze: {
          lookers: this.lastLookers, familiar: this.familiarIds.size, near: this._vfSlots.slice(0, this._vfN).filter((sl) => sl.near).length,
          // attention priority (2026-09-15): the uncapped looks this frame, and the live startles
          priority: { ...this.attnCounts }, startles: this.startles.length, aimed: this.aimedVersona, feared: this.fearedIds.size,
        },
        // Live read of the attention counters (the object above refreshes every 64 frames — minutes under a
        // software renderer; a probe reads THIS between frames).
        attention: () => ({ ...this.attnCounts, lookers: this.lastLookers, startles: this.startles.length, aimed: this.aimedVersona }),
        // The drawn slots as the pose loop sees them (indoors slots are skipped by the loop — a probe must pick an outdoor one).
        slots: () => this._vfSlots.slice(0, this._vfN).map((sl) => ({ id: sl.key, x: sl.x, z: sl.z, state: sl.a.state, indoors: !!sl.indoors, moving: sl.moving, attn: sl.attn, lookYaw: sl.lookYaw, route: !!sl.a.route, routeLeft: sl.a.route ? Math.max(0, sl.a.route.L - (sl.a.route.s0 + sl.a.route.v * (performance.now() - sl.a.route.t0) / 1000)) : null, routeS: sl.a.route ? Math.min(sl.a.route.sMax, sl.a.route.s0 + sl.a.route.v * (performance.now() - sl.a.route.t0) / 1000) : null, routeV: sl.a.route?.v ?? null, routeL: sl.a.route?.L ?? null, routeMax: sl.a.route?.sMax ?? null, routeAge: sl.a.route ? performance.now() - sl.a.route.t0 : null, routePts: sl.a.route ? sl.a.route.pts.map((q) => [Math.round(q[0] * 10) / 10, Math.round(q[1] * 10) / 10]) : null, fromX: sl.a.fromX, fromZ: sl.a.fromZ, toX: sl.a.toX, toZ: sl.a.toZ })),
        // Probe lever: put the player's drawn weapon on a versona (the soft-target feed) — dev hosts only.
        aim: (id: string | null): boolean => {
          if (location.hostname !== 'localhost' && location.hostname !== '127.0.0.1') return false;
          this.aimedVersona = id;
          return true;
        },
        // Probe lever: a blast at (x, z) — or beside the player — turns nearby heads (dev hosts only).
        startle: (x?: number, z?: number): boolean => {
          if (location.hostname !== 'localhost' && location.hostname !== '127.0.0.1') return false;
          this.startle(Number.isFinite(x) ? (x as number) : atlas.localX + 3, Number.isFinite(z) ? (z as number) : atlas.localZ + 2, performance.now() / 1000);
          return true;
        },
        // Rubber-band governor diagnostics: how often the visual speed clamp
        // engaged + the fastest RAW jump it saw (m/s) since load.
        gov: { clamped: this.govClamped, maxRaw: Math.round(this.govMaxRaw * 10) / 10 },
        focus: (x: number, z: number): boolean => {
          if (location.hostname !== 'localhost' && location.hostname !== '127.0.0.1') return false;
          this.lifeFocus = Number.isFinite(x) ? { x, z } : null;
          return true;
        },
      };
    }
  }

  /**
   * Scatter foliage instances for each server-declared `instance_group` (renderMode
   * 'instanced'). The server owns WHICH region + WHICH asset; the per-blade positions
   * are a DETERMINISTIC client function of (region, seed) — decorative, not world state.
   */
  private buildFoliage(conn: DbConnection, worldId: bigint, seed: string): void {
    const groups = [...conn.db.instanceGroup.iter()].filter((g) => g.worldId === worldId && g.renderMode === 'instanced');
    const sig = groups.map((g) => `${g.regionX},${g.regionZ}:${g.assetId}`).join('|');
    if (sig === this.foliageSig) return; // unchanged
    this.foliageSig = sig;
    // Release the previous foliage's held asset refs (so the G5 LRU can reclaim them) + dispose our
    // material clones — the instancer deliberately does NOT own the caller-supplied geometry/material.
    this.releaseFoliage();
    this.foliage = new StaticInstancer(this.scene, 4096, 'world-foliage');
    this.foliage.setRainLayer(true); // canopies shelter (render wave 1)
    for (const g of groups) void this.scatterGroup(conn, worldId, seed, g.regionX, g.regionZ, g.assetId);
  }

  /** Release every held foliage asset ref + dispose every wind-material clone. */
  private releaseFoliage(): void {
    for (const rel of this.foliageReleases) rel();
    this.foliageReleases = [];
    for (const mat of this.foliageMats) disposeLater(mat); // mid-frame rebuilds (bus handlers) — never inline
    this.foliageMats = [];
    this.foliage?.dispose();
    this.foliage = null;
    this.worldFoliageXYZ = [];
  }

  private async scatterGroup(conn: DbConnection, worldId: bigint, seed: string, rx: number, rz: number, assetId: bigint): Promise<void> {
    const target = this.foliage; // capture — a rebuild may REPLACE this.foliage while we await the load
    let obj: THREE.Object3D;
    let release: () => void;
    try {
      // Same near-ring rule as the chunk scatter (R2): LOD1 below high.
      const rep = await this.assets.loadRepresentation(assetId, nearFoliageLod(activePreset()));
      obj = rep.object; release = rep.release;
    } catch {
      // The render_asset isn't in the registry yet (foliage scatters on account-ready, before the
      // render_asset subscription applies) or the GLB fetch failed — reset the signature so the next
      // refresh retries, instead of crashing as an unhandled rejection.
      this.foliageSig = '';
      return;
    }
    const mesh = firstMesh(obj);
    if (!mesh || !target || target !== this.foliage) { release(); return; } // no mesh / superseded
    this.foliageReleases.push(release); // hold the ref until the next rebuild releases it
    // Clone the material — applyWind mutates positionNode, and the cached material is SHARED with
    // every other consumer that clones this asset (props would inherit the foliage sway otherwise).
    const mat = mesh.material.clone();
    this.foliageMats.push(mat);
    // Props (street furniture: lamps, benches, signposts, crates) are SPARSE set-dressing — a few
    // per region, no wind sway; foliage (plants) is dense ground cover that sways. prefab_model
    // carries the category (subscribed by StructureRender, so it's in the shared connection cache).
    const isProp = [...conn.db.prefabModel.iter()].some((pm) => pm.assetId === assetId && pm.category === 'prop');
    if (!isProp) applyWind(mat, { maxHeight: 0.8, trample: trampleActive() }); // G4: foliage sways in the wind (props don't) + bends underfoot
    const N = isProp ? 6 : 128, HALF = 32, cx = rx * 64, cz = rz * 64;
    // Keep scatter OUT of building footprints — no plants/props growing through a structure. Each
    // structure's clear radius ≈ its catalog scale × the GLB half-diagonal (models normalize to ~1.9 m).
    const keepOut = worldId !== null ? structureKeepOuts(conn, worldId, cx, cz, HALF) : [];
    const groundKO = groundKeepOutsNear(cx - HALF, cz - HALF, cx + HALF, cz + HALF); // THE GROUND KEEP-OUT (2026-10-02)
    const up = new THREE.Vector3(0, 1, 0), q = new THREE.Quaternion(), s = new THREE.Vector3(), p = new THREE.Vector3(), m = new THREE.Matrix4();
    for (let i = 0; i < N; i++) {
      // Seed by asset too → two different assets in one region scatter to DIFFERENT positions
      // (the "objects inside the plants" bug: props were piling onto the foliage's exact spots).
      const [a, b, c] = rng3(rx, rz, i, Number(assetId));
      const x = cx + (a - 0.5) * 2 * HALF, z = cz + (b - 0.5) * 2 * HALF;
      if (keepOut.some((k) => (k.x - x) ** 2 + (k.z - z) ** 2 < k.r2)) continue; // inside a building footprint → skip
      // Footprint-aware seat (min height − embed): a point-sample floats plants on the
      // downhill side of slopes. Cliff faces (slope > ~50°) get no scatter at all.
      // Personal worlds seat on the ERODED per-seed grid (the rendered mesh),
      // not the Commons chunk sampler — otherwise scatter floats by the erosion delta.
      let seatY: number;
      let seatSlope: number;
      if (worldId === atlas.commonsWorldId) {
        const seat = terrainSeat(seed, x, z, 0.6, 0.12);
        seatY = seat.y;
        seatSlope = seat.slope;
      } else {
        const h = personalGroundY(seed, x, z);
        seatSlope = Math.hypot(personalGroundY(seed, x + 1, z) - h, personalGroundY(seed, x, z + 1) - h);
        seatY = h - 0.12;
      }
      if (seatSlope > 1.2) continue;
      if (groundKO.length && insideGroundKeepOut(groundKO, x, z, seatY)) continue; // inside a standing building or an interior
      p.set(x, seatY, z); q.setFromAxisAngle(up, c * Math.PI * 2); s.setScalar(0.7 + a * 0.6);
      const id = target.add(mesh.geometry, mat, m.compose(p, q, s));
      if (id >= 0 && target === this.foliage) this.worldFoliageXYZ.push(id, x, seatY, z); // the after-the-fact keep-out
    }
  }

  // ── Commons chunk streamer ────────────────────────────────────────────────────

  /**
   * Called from `refresh()` when entering / staying in the Commons.
   * Triggers the lazy biome-texture load (once), resets the per-frame seed, and
   * kicks an immediate window diff so the player's current cell builds right away.
   */
  private buildChunkTerrain(seed: string): void {
    this.commonsSeedForChunks = seed;
    // Remove the legacy single-plane terrain if it was built in non-Commons mode.
    if (this.terrainMesh) {
      this.scene.remove(this.terrainMesh);
      disposeLater(this.terrainMesh.geometry);
      disposeLater(this.terrainMesh.material as THREE.Material); // (bug sweep) the material leaked on every Commons entry
      this.terrainMesh = null;
    }
    // Build the fallback material immediately so chunks are visible before textures load.
    if (!this.chunkFallbackMat) {
      const fallback = new THREE.MeshStandardMaterial({ color: 0x3f5d3a, roughness: 0.95, metalness: 0 });
      applyWetness(fallback);
      this.chunkFallbackMat = fallback;
    }
    this.ensureGroundMaterial();
  }

  // ── GROUND MATERIAL (v1 six biome albedos | v2 twelve scanned layers by rules, 2026-09-25) ──
  /** The ground's vertex layout generation: bumps when v1 ↔ v2 flips (every chunk is rebuilt with the other attribute set). */
  private groundLayoutGen = 0;
  /** The attribute layout the CURRENT generation's chunks carry. */
  private groundKind: GroundMaterial = 'v1';
  /** Signature of the built (or building) material — the knobs it was built from. */
  private groundSig = '';
  private groundBuildSig = '';
  /** Layout generation each resident chunk was built with. */
  private readonly chunkGroundGen = new Map<string, number>();
  /** The near-mesh density the window last re-LODed for (R8a; a change forces a window re-diff). */
  private appliedNearSeg: number = groundKnobs().nearSeg;
  private groundTextures: GroundTextureSet | null = null;
  private groundMacroTex: THREE.Texture | null = null;
  /** Materials whose chunks are still being rebuilt — disposed once no chunk wears them. */
  private retiredGroundMats: THREE.Material[] = [];
  private groundRetireCheckAt = 0;
  /** The v2 macro-variation map (the far tier's material reads it too). */
  get groundMacro(): THREE.Texture | null { return this.groundMacroTex; }
  /** The ground material the chunks wear: 'v1' | 'v2' (the far tier builds its own per-level v2 material). */
  get groundVersion(): GroundMaterial { return this.groundKind; }
  /**
   * SHADER GAPS R6 (the shallow-water puddle patch): the DRAWN ground its bed is built from — the Commons seed (the key
   * `meshHeight` reads the resident chunks' own grids by) and the resident chunk mesh + its grid at (cx, cz), whose
   * interleaved rule attributes (gW0–2, gMask) give the ground class, the wet mask and the ruts. null off the Commons.
   */
  puddleGround(): { seed: string; chunk(cx: number, cz: number): { mesh: THREE.Mesh; seg: number } | null } | null {
    const seed = this.commonsSeedForChunks;
    if (!seed || this.groundKind !== 'v2') return null;
    return { seed, chunk: (cx, cz) => { const k = `${cx},${cz}`; const mesh = this.chunkMeshes.get(k), seg = this.chunkSegResident.get(k); return mesh && seg !== undefined ? { mesh, seg } : null; } };
  }

  /** The probe's diagnostic view (`__ground().debugView`) — part of the signature, so a change rebuilds. */
  private groundDebugView: 'off' | 'pom' | 'rock' = 'off';
  private groundSigNow(): string {
    const k = groundKnobs(), p = activePreset();
    return [k.material, k.simple, k.res, k.potato, k.triplanar, k.bands, k.pom, k.antiTiling, !!p.rainRipples, !!p.rainShelter, !!p.cloudShadows, !!p.specularAA, p.anisotropy ?? 4, this.groundTexGen, this.groundDebugView,
      k.microShadow, k.hexTiling, k.pomShadow, deformOn()].join('|') + '|' + puddleGroundSig(); // surfaces (2026-09-27): the new ground knobs rebuild the ground; weather surfaces R3: the deformation imprint; shader gaps R6: the puddle patch (its size sizes the storage buffer)
  }

  /**
   * (Re)build the ground material when its knobs changed. A v1 ↔ v2 flip also
   * bumps the layout generation and queues every resident chunk for a rebuild
   * (the attribute sets differ — the ZERO-READS law: a v2 shader on a v1
   * geometry reads zero weights and paints black). Old meshes keep the old
   * material until their replacement lands (the re-LOD swap path).
   */
  private ensureGroundMaterial(): void {
    const sig = this.groundSigNow();
    if (sig === this.groundSig || sig === this.groundBuildSig) return;
    this.groundBuildSig = sig;
    const k = groundKnobs();
    if (k.material !== this.groundKind || this.groundSig === '') {
      if (k.material !== this.groundKind) {
        this.groundKind = k.material;
        this.groundLayoutGen++;
        // Rebuild every resident chunk with the other attribute set, nearest first.
        const pcx = chunkOf(atlas.localX, CHUNK_SIZE), pcz = chunkOf(atlas.localZ, CHUNK_SIZE);
        const keys = [...this.chunkMeshes.keys()].map((key) => { const [cx, cz] = key.split(',').map(Number); return { cx, cz, d: Math.max(Math.abs(cx - pcx), Math.abs(cz - pcz)) }; }).sort((a, b) => a.d - b.d);
        for (const e of keys) if (!this.chunkBuildQueue.some((q) => q.cx === e.cx && q.cz === e.cz)) this.chunkBuildQueue.push({ cx: e.cx, cz: e.cz });
      }
    }
    const kind = this.groundKind;
    void this.buildGroundMaterial(sig, kind).then((mat) => {
      if (!mat) { if (this.groundBuildSig === sig) this.groundBuildSig = ''; return; }
      if (this.groundBuildSig !== sig) { disposeLater(mat); return; } // superseded while loading
      const old = this.biomeMat;
      this.biomeMat = mat;
      this.groundSig = sig; this.groundBuildSig = '';
      // Hot-swap onto every resident chunk that carries THIS layout; the rest keep theirs until rebuilt.
      for (const [key, mesh] of this.chunkMeshes) if ((mesh.userData.groundKind ?? 'v1') === kind && this.chunkGroundGen.get(key) === this.groundLayoutGen) mesh.material = mat;
      if (old && old !== mat) this.retiredGroundMats.push(old);
      mat.needsUpdate = true;
    });
  }

  /** Bumped when a high rung lands behind the first set — part of the signature, so the material rebuilds on it. */
  private groundTexGen = 0;
  private groundUpgrading = '';
  /** The high rung behind the 2048 first set: load it (THE RUNG LADDER), install it, rebuild — unless the ask moved on. */
  private async upgradeGroundArrays(res: number, want: string): Promise<void> {
    if (this.groundUpgrading === want) return;
    this.groundUpgrading = want;
    try {
      const shared = (this.appRef as unknown as { gltf?: { ktx2?: { loadAsync(u: string): Promise<THREE.Texture> } } } | null)?.gltf?.ktx2 ?? null;
      const loaded = await loadGroundTextures(this.appRef?.renderer.native, shared, res, false, activePreset().anisotropy ?? 4);
      const k = groundKnobs();
      if (!loaded || groundSetName(k.res, k.potato) !== want || k.material !== 'v2' || !this.commonsSeedForChunks) { if (loaded) { disposeLater(loaded.a); disposeLater(loaded.b); } return; }
      const prev = this.groundTextures;
      this.groundTextures = loaded;
      this.groundTexGen++;
      this.ensureGroundMaterial(); // builds synchronously past the (resident) macro await and hot-swaps the chunks
      if (prev && prev !== loaded) { disposeLater(prev.a); disposeLater(prev.b); }
    } finally { this.groundUpgrading = ''; }
  }

  /** Drop retired ground materials no chunk wears any more (throttled, from chunkTick). */
  private sweepRetiredGroundMats(): void {
    const now = performance.now();
    if (!this.retiredGroundMats.length || now - this.groundRetireCheckAt < 2000) return;
    this.groundRetireCheckAt = now;
    const worn = new Set<THREE.Material>();
    for (const mesh of this.chunkMeshes.values()) worn.add(mesh.material as THREE.Material);
    this.retiredGroundMats = this.retiredGroundMats.filter((m) => { if (worn.has(m)) return true; disposeLater(m); return false; });
  }

  private async buildGroundMaterial(sig: string, kind: GroundMaterial): Promise<THREE.MeshStandardMaterial | null> {
    const k = groundKnobs();
    const p = activePreset();
    const sea = commonsSeaLevel(this.commonsSeedForChunks);
    if (kind === 'v2') {
      this.groundMacroTex ??= await loadGroundMacro();
      let arrays: GroundArrays | null = null;
      if (!k.simple) {
        const want = groundSetName(k.res, k.potato);
        // THE RUNG LADDER (ground, 2026-09-26): the same ask never reloads — also after a fall down the ladder.
        // (a rebuild while the high rung is still loading behind the first set keeps the resident set — no second load)
        if (!this.groundTextures || (this.groundTextures.set !== want && this.groundTextures.requestedSet !== want && this.groundUpgrading !== want)) {
          const shared = (this.appRef as unknown as { gltf?: { ktx2?: { loadAsync(u: string): Promise<THREE.Texture> } } } | null)?.gltf?.ktx2 ?? null;
          // THE HIGH RUNGS ARRIVE SECOND: a 4096 / 8192 pair is 0.44 / 1.76 GB to fetch and transcode — with nothing
          // resident yet (world entry) the 2048 set goes up first so the ground draws in seconds, and the high rung
          // loads behind it and rebuilds the material when it is resident (upgradeGroundArrays).
          const first = !this.groundTextures && !k.potato && k.res > 2048 ? 2048 : k.res;
          const loaded = await loadGroundTextures(this.appRef?.renderer.native, shared, first, k.potato, p.anisotropy ?? 4);
          if (loaded) { const prev = this.groundTextures; this.groundTextures = loaded; if (prev) { disposeLater(prev.a); disposeLater(prev.b); } }
          if (first !== k.res) void this.upgradeGroundArrays(k.res, want);
        }
        if (this.groundTextures) arrays = { a: this.groundTextures.a, b: this.groundTextures.b, layers: this.groundTextures.layers };
      }
      if (this.groundBuildSig !== sig) return null;
      const mat = new THREE.MeshStandardMaterial({ roughness: 0.9, metalness: 0 });
      mat.name = 'ground-v2';
      // No arrays (a failed load, or the minimal tier) → the per-vertex colour path; it reads the same attributes.
      applyGroundV2(mat, {
        arrays, macro: this.groundMacroTex, seaLevel: sea, quality: k.simple || !arrays ? 'simple' : 'full',
        potato: this.groundTextures?.set === '512p', triplanar: k.triplanar, antiTiling: k.antiTiling, bands: k.bands,
        rainRipples: !!p.rainRipples, rainShelter: !!p.rainShelter, specularAA: !!p.specularAA,
        pom: k.pom, // R8c: rock / strata / gravel parallax under 25 m (high), every near layer (ultra)
        debugView: this.groundDebugView, // the probe's diagnostic colour ('off' in play)
        microShadow: k.microShadow, hexTiling: k.hexTiling, pomShadow: k.pomShadow, // surfaces (2026-09-27): §2.1 / §2.3 / §2.4
        deform: deformOn(), // weather surfaces R3: snow and mud that remember (the shelter atlas, no sampler)
        puddleSim: puddleGroundSig() !== 'puddle:off', // shader gaps R6: the shallow-water puddle patch (a storage buffer, no sampler; WebGPU only)
      });
      if (p.cloudShadows) applyCloudShadows(this.app, mat as never); // stage 4: the chunks' ground
      return mat;
    }
    return this.buildGroundV1();
  }

  /** v1 — the six biome albedos (lib/wetness.ts applyBiomeGround), kept selectable for A/B. */
  private buildGroundV1(): Promise<THREE.MeshStandardMaterial | null> {
    this.biomeTexLoading = true;
    return new Promise((resolveMat) => {
      const keys = Object.keys(BIOME_GROUND) as Biome[];
      void Promise.all(keys.map((b) => this.assets.loadTexture(BIOME_GROUND[b])))
        .then((textures) => {
          const texMap = {} as Record<Biome, THREE.Texture>;
          keys.forEach((b, i) => {
            const t = textures[i];
            t.repeat.set(TERRAIN_TEXTURE_SCALE, TERRAIN_TEXTURE_SCALE);
            texMap[b] = t;
          });
          // Per-biome NORMAL maps, derived at runtime from the albedos (Sobel on
          // luminance — zero new assets; null on any decode failure → albedo-only).
          // GATED on the device's sampled-texture budget: 6 albedos + 6 normals +
          // engine textures = 18 samplers in one fragment stage, which is an
          // INVALID pipeline on WebGPU's default 16/stage limit (the whole terrain
          // vanished). The engine now requests the adapter's real limits, but weak
          // hardware (and the WebGL2 fallback) still degrades to albedo-only here.
          // D3D12/Windows allows 48 sampled TEXTURES but only 16 SAMPLERS per
          // stage — each texture() node binds its own sampler, so gate on the
          // MINIMUM of both limits or the pipeline is invalid (field report #2).
          const dev = (this.app?.renderer.native.backend as unknown as { device?: { limits?: { maxSampledTexturesPerShaderStage?: number; maxSamplersPerShaderStage?: number } } })?.device;
          const lim = dev?.limits;
          const texBudget = Math.min(lim?.maxSampledTexturesPerShaderStage ?? 16, lim?.maxSamplersPerShaderStage ?? 16);
          const nrmMap: Partial<Record<Biome, THREE.Texture>> = {};
          if (texBudget >= 24) {
            for (const b of keys) {
              const n = normalFromAlbedo(texMap[b]);
              if (n) nrmMap[b] = n;
            }
          }
          // Build the shared biome material and upgrade every existing chunk mesh.
          // The sea level threads the wet-sand shoreline band into the shader.
          const mat = new THREE.MeshStandardMaterial({ roughness: 0.95, metalness: 0 });
          applyBiomeGround(mat, texMap, nrmMap, commonsSeaLevel(this.commonsSeedForChunks), activePreset().groundQuality ?? 'full',
            { antiTiling: !!activePreset().groundAntiTiling && (activePreset().groundQuality ?? 'full') === 'full', rainRipples: !!activePreset().rainRipples, rainShelter: !!activePreset().rainShelter, specularAA: activePreset().specularAA ? { varianceCap: specularAAVarianceCap(activeTier()) } : null });
          if (activePreset().cloudShadows) applyCloudShadows(this.app, mat as never); // stage 4: the ground (and the far tier, which wears this material)
          mat.name = 'ground-v1';
          this.biomeTexLoading = false;
          resolveMat(mat); // ensureGroundMaterial hot-swaps it onto the v1 chunks
        })
        .catch(() => {
          this.biomeTexLoading = false; // keep using fallback
          resolveMat(null);
        });
    });
  }

  /**
   * Per-frame tick registered as an ECS system (Phase.RenderSub, priority 35).
   * Diffs the player's current chunk window against the previous one and
   * builds/removes chunks accordingly. Builds at most 4 per frame to avoid hitching.
   */
  private chunkTick(): void {
    this.forestColliderTick();
    if (!this.commonsSeedForChunks || atlas.currentWorldId !== atlas.commonsWorldId) return;
    this.sweepRetiredGroundMats(); // ground v2: free a replaced ground material once no chunk wears it

    // Tier re-scatter drain: ONE chunk per frame. Re-scattering all ~49
    // resident chunks in a single call was a multi-second tier-switch stall
    // (the 6-8 s main-thread long-task family in the real-GPU tier sweeps).
    // Keys that streamed out mid-drain are skipped (chunkFoliage.has guard).
    // Two per frame (2026-09-09: one left a 49-chunk window re-scattering for
    // 49 frames); a key disposed by the 0 % path (no group any more) still
    // re-scatters so the chunk regains its (empty) group and stays eligible.
    for (let n = 0; n < 2; n++) {
      const rk = this.rescatterQueue.shift();
      if (rk === undefined) break;
      const [cx, cz] = rk.split(',').map(Number);
      if (!Number.isFinite(cx) || !Number.isFinite(cz)) continue;
      if (!this.chunkMeshes.has(rk)) continue; // streamed out mid-drain
      if (this.chunkFoliage.has(rk)) this.disposeChunkFoliage(rk);
      void this.scatterChunkFoliage(rk, { cx, cz });
    }

    const pcx = chunkOf(atlas.localX, CHUNK_SIZE);
    const pcz = chunkOf(atlas.localZ, CHUNK_SIZE);
    // The window is a pure function of (pcx, pcz) — recompute + diff only on
    // a cell crossing. Standing still paid ~49 key objects + ~100 strings +
    // two Sets of garbage EVERY frame for an always-empty diff.
    if (pcx !== this.lastWindowPcx || pcz !== this.lastWindowPcz) {
      this.lastWindowPcx = pcx;
      this.lastWindowPcz = pcz;
      // Foliage ring LOD: chunks whose near/far classification changed get a
      // re-scatter (trees swap full↔LOD1, undergrowth appears/disappears),
      // amortized by the 1-per-frame drain above.
      for (const [fkey, ring] of this.chunkFoliageRing) {
        if (!this.chunkFoliage.has(fkey)) continue;
        const [fcx, fcz] = fkey.split(',').map(Number);
        const nowFar = this.foliageRingOf(Math.max(Math.abs(fcx - pcx), Math.abs(fcz - pcz)));
        if (nowFar !== ring && !this.rescatterQueue.includes(fkey)) this.rescatterQueue.push(fkey);
      }
      const nextWindow = chunkWindow(pcx, pcz, Math.max(CHUNK_RADIUS, activePreset().chunkRadius ?? CHUNK_RADIUS));
      const { add, remove } = diffWindows(this.prevWindow, nextWindow);

      // Enqueue new chunks (any that aren't already in the queue or built).
      for (const k of add) {
        const key = `${k.cx},${k.cz}`;
        if (!this.chunkMeshes.has(key) && !this.chunkBuildQueue.some((q) => q.cx === k.cx && q.cz === k.cz)) {
          this.chunkBuildQueue.push(k);
        }
      }

      // Terrain LOD refresh — the same idea as the foliage-ring re-scatter
      // above, for the GROUND. A resident chunk whose ring changed is queued
      // for a rebuild at its new seg (refineChunkSeg applies the dead band).
      // Queued nearest-first: the chunk under the player's feet matters more
      // than one at the window edge, and the dispatch loop is FIFO.
      const reLod: Array<{ k: ChunkKey; cheb: number }> = [];
      for (const [rkey, resident] of this.chunkSegResident) {
        if (!this.chunkMeshes.has(rkey) || this.chunkPending.has(rkey)) continue;
        const [rcx, rcz] = rkey.split(',').map(Number);
        if (!Number.isFinite(rcx) || !Number.isFinite(rcz)) continue;
        const cheb = Math.max(Math.abs(rcx - pcx), Math.abs(rcz - pcz));
        if (refineChunkSeg(cheb, resident, groundKnobs().nearSeg) === null && this.chunkGroundGen.get(rkey) === this.groundLayoutGen) continue;
        if (this.chunkBuildQueue.some((q) => q.cx === rcx && q.cz === rcz)) continue;
        reLod.push({ k: { cx: rcx, cz: rcz }, cheb });
      }
      reLod.sort((a, b) => a.cheb - b.cheb);
      for (const e of reLod) this.chunkBuildQueue.push(e.k);

      // Remove departing chunks.
      for (const k of remove) {
        const key = `${k.cx},${k.cz}`;
        const mesh = this.chunkMeshes.get(key);
        if (mesh) {
          this.scene.remove(mesh);
          disposeLater(mesh.geometry); // deferred: a chunk leaving the window can be in the submitted frame
          this.chunkMeshes.delete(key);
          this.chunkSegResident.delete(key);
          setChunkSeg(key, null);
          this.physics?.removeTerrainCollider(key);
        }
        // Dispose the chunk's foliage group (also disposes all its child geometries/materials).
        this.disposeChunkFoliage(key);
        // Also purge from the build queue if it hasn't been built yet, and drop any
        // in-flight worker job (assembleChunk discards results with no pending entry).
        const qi = this.chunkBuildQueue.findIndex((q) => q.cx === k.cx && q.cz === k.cz);
        if (qi !== -1) this.chunkBuildQueue.splice(qi, 1);
        this.chunkPending.delete(key);
      }

      this.prevWindow = nextWindow;
    }

    // Terrain trimesh colliders: keep exact-match physics under the NEAR ring only
    // (9 chunks around the player). Self-healing: skipped while Rapier is still
    // loading, added on a later tick once physics is ready.
    // Terrain colliders follow the NEAR ring — re-evaluated only when the
    // player's chunk, the chunk set, or physics readiness changed (it used to
    // rescan every chunk mesh EVERY frame, splitting 49 keys per frame for nothing).
    const physReady = !!this.physics?.ready;
    if (this.colliderSyncDirty || pcx !== this.colliderSyncPcx || pcz !== this.colliderSyncPcz || physReady !== this.colliderSyncPhysReady) {
      this.colliderSyncPcx = pcx; this.colliderSyncPcz = pcz; this.colliderSyncPhysReady = physReady;
      this.colliderSyncDirty = this.syncTerrainColliders(pcx, pcz); // true while a near chunk still lacks its collider (one build per tick)
    }

    if (!this.chunkWorkerBroken) {
      // Stall watchdog: a module worker whose import graph HANGS (cold-vite
      // dep-optimize race) fires NO 'error' event — chunkPending pins at 4, the
      // dispatch loop blocks forever, and the world streams in as a terrain-less
      // "blue void" while main-thread settlements keep loading. Silence for
      // CHUNK_WORKER_STALL_MS with jobs in flight ⇒ treat the worker as dead.
      // (onmessage refreshes the beat, so a slow main thread that merely lags
      // ASSEMBLY — pending stays >0 — never false-trips this.)
      // BOOT vs stall budgets: before the first-ever beat the worker may be
      // legitimately importing its module graph (cold vite over LAN TLS on a
      // Quest takes tens of seconds) — give the boot a bigger window.
      const stallBudget = this.chunkWorkerEverBeat ? CHUNK_WORKER_STALL_MS : CHUNK_WORKER_BOOT_MS;
      if (this.chunkPending.size > 0 && performance.now() - this.chunkWorkerBeatMs > stallBudget) {
        this.failChunkWorker(`silent for ${stallBudget / 1000}s`);
      }
    }
    if (!this.chunkWorkerBroken) {
      // Worker path: dispatch queued jobs (bounded in-flight) + assemble finished
      // results (cheap) — the ~35 ms/chunk noise eval never touches this thread.
      while (this.chunkBuildQueue.length > 0 && this.chunkPending.size < 4) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const k = this.chunkBuildQueue.shift()!;
        if (!this.requestChunk(k, pcx, pcz)) { this.chunkBuildQueue.unshift(k); break; } // worker unavailable → sync next tick
      }
      let assembled = 0;
      // 1/frame (was 2): a near-chunk assembly (2401-vert attribute write +
      // normals) plus its foliage scatter is already a full frame's budget.
      while (this.chunkResults.length > 0 && assembled < 1) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.assembleChunk(this.chunkResults.shift()!);
        assembled++;
      }
      return;
    }

    // Sync fallback (no Worker in this environment, or a geometry-layout mismatch):
    // ONE chunk per frame — a ~35 ms NEAR build is already over budget; batching four
    // of them (the old behavior) produced ~140 ms boundary-crossing hitches.
    if (this.chunkBuildQueue.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const k = this.chunkBuildQueue.shift()!;
      this.buildOneChunk(k, pcx, pcz);
    }
  }

  private colliderSyncPcx = Number.NaN;
  private colliderSyncPcz = Number.NaN;
  private colliderSyncPhysReady = false;
  /** Set when a chunk mesh is assembled or removed — the next frame re-evaluates the NEAR ring. */
  private colliderSyncDirty = true;

  /**
   * Maintain terrain trimesh colliders for the chunks in the player's NEAR ring.
   * Returns true when a near chunk still lacks its collider (call again next tick).
   */
  private syncTerrainColliders(pcx: number, pcz: number): boolean {
    const phys = this.physics;
    if (!phys || !phys.ready) return true;
    // At most ONE trimesh build per tick: crossing a chunk boundary makes up to
    // 3 chunks newly "near", and each collider build runs Rapier's BVH bake
    // (4608 tris) — batching them contributed to the boundary-crossing hitch.
    let added = false, pending = false;
    for (const [key, mesh] of this.chunkMeshes) {
      let cc = mesh.userData.chunkCoords as { cx: number; cz: number } | undefined;
      if (!cc) { const [cx, cz] = key.split(',').map(Number); cc = { cx, cz }; mesh.userData.chunkCoords = cc; }
      const near = Math.max(Math.abs(cc.cx - pcx), Math.abs(cc.cz - pcz)) <= 1;
      const has = phys.hasTerrainCollider(key);
      if (near && !has) {
        if (added) { pending = true; continue; } // remaining near chunks get theirs on the next ticks
        const geo = mesh.geometry;
        const idx = geo.index;
        const pos = geo.attributes.position as THREE.BufferAttribute;
        if (!idx || !pos) continue;
        const indices = idx.array instanceof Uint32Array ? idx.array : new Uint32Array(idx.array as ArrayLike<number>);
        phys.addTerrainCollider(key, pos.array as Float32Array, indices, mesh.position.x, mesh.position.z);
        added = true;
      } else if (!near && has) {
        phys.removeTerrainCollider(key);
      }
    }
    return pending;
  }

  /** Post one chunk job to the worker (lazily created). False = worker unavailable. */
  private requestChunk(k: ChunkKey, playerCx: number, playerCz: number): boolean {
    const key = `${k.cx},${k.cz}`;
    if (this.chunkPending.has(key)) return true;
    const chebyshev = Math.max(Math.abs(k.cx - playerCx), Math.abs(k.cz - playerCz));
    // Resident LOD, or undefined when the chunk is not built. Read THROUGH
    // chunkMeshes: the two are written and cleared together, and going through
    // the mesh map enforces that invariant here rather than assuming it — a
    // stale ledger entry would otherwise pick a rebuild seg for a first build.
    const residentSeg = this.chunkMeshes.has(key) ? this.chunkSegResident.get(key) : undefined;
    // A resident chunk is re-requested only when its LOD is wrong for the ring
    // it is now in (refineChunkSeg — null means "leave it alone") — or when it
    // carries the other ground layout (a v1 ↔ v2 flip rebuilds every chunk).
    const nearSeg = groundKnobs().nearSeg;
    const staleGround = this.chunkMeshes.has(key) && this.chunkGroundGen.get(key) !== this.groundLayoutGen;
    if (this.chunkMeshes.has(key) && !staleGround && (residentSeg === undefined || refineChunkSeg(chebyshev, residentSeg, nearSeg) === null)) return true;
    if (!this.chunkWorker) {
      try {
        this.chunkWorker = new Worker(new URL('../workers/terrain_chunk_worker.ts', import.meta.url), { type: 'module' });
        if (this.commonsSeedForChunks) postRiverTable(this.chunkWorker, this.commonsSeedForChunks); // shared network (perf frontier item 12)
        this.chunkWorker.onmessage = (e: MessageEvent<{ kind?: string; key: string; seg: number; job?: number; heights: Float32Array; biomeWA: Float32Array; biomeWB: Float32Array; roadW?: Float32Array; fieldW?: Float32Array; ground?: Float32Array }>) => {
          this.chunkWorkerBeatMs = performance.now(); // alive — acks/results flowing
          this.chunkWorkerEverBeat = true;
          if (e.data.kind === 'ack') return; // job-receipt heartbeat, not a result
          this.chunkResults.push(e.data);
        };
        this.chunkWorker.onerror = () => this.failChunkWorker('worker error');
      } catch {
        this.chunkWorkerBroken = true; // headless harness / no Worker — sync path
        return false;
      }
    }
    // A rebuild targets the seg refineChunkSeg asked for (which honours the
    // dead band); a first build takes the nominal ladder. Using the nominal
    // ladder for a rebuild would re-flag the chunk on the very next crossing.
    const seg = residentSeg === undefined
      ? segForRing(chebyshev, nearSeg)
      : (refineChunkSeg(chebyshev, residentSeg, nearSeg) ?? residentSeg);
    // Per-request id (echoed by the worker): replies were matched by KEY alone,
    // so a terrain edit that evicted + re-requested a chunk while its pre-edit
    // job was still in the worker applied the STALE reply and dropped the
    // fresh one — the edit visually reverted until the next eviction.
    const job = ++this.chunkJobSeq;
    this.chunkPending.set(key, { cx: k.cx, cz: k.cz, job });
    this.chunkWorkerBeatMs = performance.now();
    // Building stamps ride the payload: the worker computes stampedHeight with
    // its OWN module instance, which must see the same flattening rects.
    const bsx = k.cx * CHUNK_SIZE, bsz = k.cz * CHUNK_SIZE;
    const stamps = buildingStampsNear(bsx - CHUNK_SIZE, bsz - CHUNK_SIZE, bsx + 2 * CHUNK_SIZE, bsz + 2 * CHUNK_SIZE);
    const edits = terrainEditsNear(bsx - CHUNK_SIZE, bsz - CHUNK_SIZE, bsx + 2 * CHUNK_SIZE, bsz + 2 * CHUNK_SIZE);
    this.chunkWorker.postMessage({ key, seed: this.commonsSeedForChunks, cx: k.cx, cz: k.cz, seg, size: CHUNK_SIZE, stamps, edits, job, ground: this.groundKind, sea: commonsSeaLevel(this.commonsSeedForChunks),
      normals: terrainNormalsMode() }); // shader gaps R5: the worker returns analytic normals (lib/terrain_normals.ts)
    this.chunkPendingGen.set(key, this.groundLayoutGen);
    return true;
  }

  /**
   * Declare the chunk worker dead and hand its in-flight jobs back to the queue
   * so the sync path rebuilds them. Recovery matters as much as detection: the
   * pending jobs were already dequeued, so without the requeue a worker death
   * left up to 4 permanent holes in the terrain around the player.
   */
  private failChunkWorker(reason: string): void {
    if (this.chunkWorkerBroken) return;
    this.chunkWorker?.terminate();
    this.chunkWorker = null;
    for (const k of this.chunkPending.values()) this.chunkBuildQueue.unshift(k);
    this.chunkPending.clear();
    this.chunkResults.length = 0; // orphaned: their chunks are requeued
    // ONE respawn before the permanent sync fallback: a false-killed worker
    // doomed the whole session to ~35 ms main-thread chunk builds. The next
    // requestChunk lazily re-creates it; a second failure is final.
    if (this.chunkWorkerRespawns < 1) {
      this.chunkWorkerRespawns++;
      this.chunkWorkerEverBeat = false;
      this.chunkWorkerBeatMs = performance.now();
      console.warn(`[Environment] terrain worker failed (${reason}) — respawning once before the sync fallback`);
      return;
    }
    this.chunkWorkerBroken = true;
    console.warn(`[Environment] terrain worker failed (${reason}) — rebuilding chunks synchronously`);
  }

  /**
   * Retire a resident chunk mesh so a replacement can take its slot: the same
   * teardown the window-eviction and restamp paths do (deferred geometry
   * dispose per the warp-teardown law, collider drop, foliage drop — foliage
   * is scattered against the chunk's rendered heights, which change with seg).
   */
  // ── ground v2 probe levers (the census reads the chunks' OWN vertex data — what the shader sees) ──
  private groundRulesAt(x: number, z: number): { key: string; seg: number; weights: Record<string, number>; top: string; tint: number[]; mask: number[]; slope: number } | null {
    const cx = Math.floor(x / CHUNK_SIZE), cz = Math.floor(z / CHUNK_SIZE), key = `${cx},${cz}`;
    const mesh = this.chunkMeshes.get(key); const seg = this.chunkSegResident.get(key);
    if (!mesh || seg === undefined) return null;
    const g = mesh.geometry, w0 = g.getAttribute('gW0');
    if (!w0) return null;
    const cell = CHUNK_SIZE / seg;
    const col = Math.max(0, Math.min(seg, Math.round((x - cx * CHUNK_SIZE) / cell))), row = Math.max(0, Math.min(seg, Math.round((z - cz * CHUNK_SIZE) / cell)));
    const i = row * (seg + 1) + col;
    const v = (name: string): number[] => { const a = g.getAttribute(name) as THREE.BufferAttribute; return [a.getX(i), a.getY(i), a.getZ(i), a.getW(i)]; };
    const ws = [...v('gW0'), ...v('gW1'), ...v('gW2')];
    const keys = ['loam', 'turf', 'litter', 'moss', 'rock', 'strata', 'gravel', 'beach', 'dune', 'mud', 'snow', 'alien'];
    const weights: Record<string, number> = {}; let top = 0;
    ws.forEach((wv, k) => { weights[keys[k]] = +wv.toFixed(3); if (wv > ws[top]) top = k; });
    const n = g.getAttribute('normal') as THREE.BufferAttribute; const ny = Math.max(1e-3, n.getY(i));
    return { key, seg, weights, top: keys[top], tint: v('gTint').map((q) => +q.toFixed(3)), mask: v('gMask').map((q) => +q.toFixed(3)), slope: +(Math.sqrt(Math.max(0, 1 - ny * ny)) / ny).toFixed(3) };
  }
  private groundCensus(x: number, z: number, r: number, minSlope: number): { vertices: number; top: Record<string, number>; rockShare: number; meanWet: number } {
    const top: Record<string, number> = {}; let vertices = 0, rock = 0, wet = 0;
    for (let gx = x - r; gx <= x + r; gx += 4) for (let gz = z - r; gz <= z + r; gz += 4) {
      if ((gx - x) ** 2 + (gz - z) ** 2 > r * r) continue;
      const q = this.groundRulesAt(gx, gz); if (!q || q.slope < minSlope) continue;
      vertices++; top[q.top] = (top[q.top] ?? 0) + 1; rock += (q.weights.rock + q.weights.strata); wet += q.mask[2];
    }
    return { vertices, top, rockShare: vertices ? +(rock / vertices).toFixed(3) : 0, meanWet: vertices ? +(wet / vertices).toFixed(3) : 0 };
  }

  /** The ground material a chunk carrying `kind`'s attributes may wear (the fallback until that kind's material exists). */
  private groundMaterialFor(kind: GroundMaterial): THREE.Material {
    const m = this.biomeMat;
    if (m && m.name === `ground-${kind}`) return m;
    return this.chunkFallbackMat ?? new THREE.MeshStandardMaterial({ color: 0x3f5d3a, roughness: 0.95, metalness: 0 });
  }

  private retireChunkMesh(key: string): void {
    const mesh = this.chunkMeshes.get(key);
    if (!mesh) return;
    this.scene.remove(mesh);
    disposeLater(mesh.geometry); // the frame in flight may still reference it (warp-teardown law)
    this.chunkMeshes.delete(key);
    this.chunkSegResident.delete(key);
    setChunkSeg(key, null);
    this.physics?.removeTerrainCollider(key);
    this.disposeChunkFoliage(key);
  }

  /** Peak-to-trough height of a chunk's grid — the skirt depth rides on it. */
  private static reliefOfGrid(h: ArrayLike<number>): number {
    let lo = Infinity, hi = -Infinity;
    for (let i = 0; i < h.length; i++) { const v = h[i]; if (v < lo) lo = v; if (v > hi) hi = v; }
    return Number.isFinite(lo) && Number.isFinite(hi) ? hi - lo : 0;
  }

  /** Assemble a worker-generated chunk: geometry alloc + attribute writes only (~1-2 ms). */
  private assembleChunk(r: { key: string; seg: number; job?: number; heights: Float32Array; biomeWA: Float32Array; biomeWB: Float32Array; roadW?: Float32Array; fieldW?: Float32Array; ground?: Float32Array }): void {
    const k = this.chunkPending.get(r.key);
    // A superseded job's reply (an older request id than the one pending for
    // this key) is dropped WITHOUT touching the entry — the fresh reply is
    // still in flight. Legacy workers that echo no id take the old path.
    if (k && r.job !== undefined && k.job !== undefined && r.job !== k.job) return;
    this.chunkPending.delete(r.key);
    // Dropped while in flight (window moved on / world left). A key that IS
    // already built is no longer dropped: that is the re-LOD swap (the old
    // mesh is retired just before the new one is added, below).
    if (!k || atlas.currentWorldId !== atlas.commonsWorldId) return;
    // Ground v2: a reply built for the other ground layout (a v1 ↔ v2 flip landed while it
    // was in flight) would wear a shader that reads attributes it lacks — rebuild it.
    const replyKind: GroundMaterial = r.ground ? 'v2' : 'v1';
    const replyGen = this.chunkPendingGen.get(r.key); this.chunkPendingGen.delete(r.key);
    if (replyKind !== this.groundKind || (replyGen !== undefined && replyGen !== this.groundLayoutGen)) { this.chunkBuildQueue.push(k); return; }

    const geo = new THREE.PlaneGeometry(CHUNK_SIZE, CHUNK_SIZE, r.seg, r.seg);
    geo.rotateX(-Math.PI / 2);
    const pos = geo.attributes.position as THREE.BufferAttribute;

    // Layout guard: the worker indexed vertices as row·(seg+1)+col with
    // x = col/seg·size − size/2. Verify against the real geometry (first + last
    // vertex); on mismatch (a future three.js layout change) fall back to sync builds.
    const side = r.seg + 1;
    const expectX = (i: number): number => ((i % side) / r.seg) * CHUNK_SIZE - CHUNK_SIZE / 2;
    const expectZ = (i: number): number => (((i / side) | 0) / r.seg) * CHUNK_SIZE - CHUNK_SIZE / 2;
    const last = pos.count - 1;
    if (
      pos.count !== side * side ||
      Math.abs(pos.getX(0) - expectX(0)) > 1e-3 || Math.abs(pos.getZ(0) - expectZ(0)) > 1e-3 ||
      Math.abs(pos.getX(last) - expectX(last)) > 1e-3 || Math.abs(pos.getZ(last) - expectZ(last)) > 1e-3
    ) {
      geo.dispose();
      this.chunkBuildQueue.push(k); // rebuild THIS chunk synchronously next tick
      this.failChunkWorker('geometry layout mismatch'); // requeues the other in-flight jobs
      return;
    }

    for (let i = 0; i < pos.count; i++) pos.setY(i, r.heights[i]);
    pos.needsUpdate = true;
    if (r.ground) {
      // Ground v2: the rules' interleaved buffer (weights, tint, masks); no `uv` — the shader is world-planar.
      attachGround(geo, r.ground);
      geo.deleteAttribute('uv');
    } else {
      geo.setAttribute('biomeWA', new THREE.BufferAttribute(r.biomeWA, 3));
      geo.setAttribute('biomeWB', new THREE.BufferAttribute(r.biomeWB, 3));
      // Road-surface tint weight (worn routes; zeros when an old worker build
      // predates the attribute — the material still binds a valid buffer).
      geo.setAttribute('roadW', new THREE.BufferAttribute(r.roadW ?? new Float32Array(pos.count), 1));
      // Tilled-field tint weight (fieldW — same zero-fallback law as roadW).
      geo.setAttribute('fieldW', new THREE.BufferAttribute(r.fieldW ?? new Float32Array(pos.count), 1));
    }
    // shader gaps R5: the worker's ANALYTIC normals (a fixed 5.33 m world step — continuous across LOD seams), else the old facets
    const rN = (r as { normals?: Float32Array }).normals;
    if (rN && rN.length === pos.count * 3) { seatAnalyticNormals(geo, rN, r.seg, THREE.BufferAttribute); terrainNormalStats.workerNormals++; }
    else { geo.computeVertexNormals(); terrainNormalStats.meshChunks++; }
    cutCaveMouths(geo, this.commonsSeedForChunks, (k.cx + 0.5) * CHUNK_SIZE, (k.cz + 0.5) * CHUNK_SIZE, r.seg);
    // Skirt LAST: after the cave cut, so the apron hangs from the border the
    // mouth left behind, and after computeVertexNormals so the copies inherit
    // the edge's normals exactly (a skirt with its own normals lights as a
    // seam of its own).
    addChunkSkirt(geo, r.seg, skirtDepthFor(EnvironmentModule.reliefOfGrid(r.heights)));
    if (r.ground) interleaveGround(geo); // five vec4s → ONE vertex buffer (after the skirt grew them)

    const mat = this.groundMaterialFor(replyKind);
    const mesh = new THREE.Mesh(geo, mat);
    // Terrain now CASTS as well as receives (2026-09-15): hills shadow
    // valleys and ridges throw across the ground at a low sun.
    //
    // The blocker was the bias. It was a fixed 0.02 m, which is a fraction of
    // a shadow texel on the cheap tiers and many texels on the rich ones, so
    // terrain self-shadowing was guaranteed to acne somewhere on the ladder.
    // LightingModule now scales normalBias to the actual cascade-0 texel size,
    // which is the quantity the offset has to beat, so a heightfield is as
    // safe a caster as anything else. Only the NEAR ring casts — the far tier
    // still sets castShadow false, and chunks beyond the CSM's maxFar are
    // outside every cascade anyway.
    mesh.castShadow = activePreset().shadowsEnabled !== false;
    mesh.receiveShadow = true;
    mesh.position.set((k.cx + 0.5) * CHUNK_SIZE, 0, (k.cz + 0.5) * CHUNK_SIZE);
    mesh.matrixAutoUpdate = false; mesh.updateMatrix(); // perf frontier: static chunks skip the per-frame matrix recompose
    // Re-LOD swap: retire the outgoing mesh (collider + foliage included) only
    // now that the replacement is fully built, so the ground is never missing
    // for a frame and a failed build leaves the old chunk standing.
    this.retireChunkMesh(r.key);
    this.scene.add(mesh);
    if (this.appRef) void warmThenShow(this.appRef, mesh); // R4: buffers upload in a warm task, not the first draw
    this.colliderSyncDirty = true; this.chunkMeshes.set(r.key, mesh);
    stitchChunkEdges(k.cx, k.cz, (cx, cz) => this.chunkMeshes.get(`${cx},${cz}`)?.geometry); // shader gaps R5: the border exchange with resident neighbours
    this.chunkSegResident.set(r.key, r.seg);
    mesh.userData.groundKind = replyKind; this.chunkGroundGen.set(r.key, this.groundLayoutGen);
    // meshHeight interpolates THIS chunk's rendered grid directly (fast path).
    setChunkHeights(r.key, r.seg, r.heights);
    this.foliagePending++;
    void this.scatterChunkFoliage(r.key, k).finally(() => {
      this.foliagePending--;
      // New foliage = new wind-material CLONES = pending pipelines — warm them
      // before the player looks that way (the look-direction freeze fix).
      // TARGETED at this chunk's foliage group: a whole-scene compileAsync
      // paid an O(scene) sweep per scatter (live-profiled 143-649 ms hitches
      // while walking, 2026-07-31).
      const grp = this.chunkFoliage.get(r.key);
      if (this.appRef && grp) schedulePipelineWarm(this.appRef, grp);
    });
  }

  /**
   * Build a single terrain chunk mesh for chunk (cx, cz) and add it to the scene.
   * `seg` is chosen by Chebyshev distance to the player's chunk.
   */
  private buildOneChunk(k: ChunkKey, playerCx: number, playerCz: number): void {
    const key = `${k.cx},${k.cz}`;
    const chebyshev = Math.max(Math.abs(k.cx - playerCx), Math.abs(k.cz - playerCz));
    // See requestChunk: read the resident LOD through chunkMeshes.
    const residentSeg = this.chunkMeshes.has(key) ? this.chunkSegResident.get(key) : undefined;
    // Already built: rebuild only to change LOD or ground layout (race guard otherwise).
    const nearSeg = groundKnobs().nearSeg;
    const staleGround = this.chunkMeshes.has(key) && this.chunkGroundGen.get(key) !== this.groundLayoutGen;
    if (this.chunkMeshes.has(key) && !staleGround
      && (residentSeg === undefined || refineChunkSeg(chebyshev, residentSeg, nearSeg) === null)) return;
    const seg = residentSeg === undefined
      ? segForRing(chebyshev, nearSeg)
      : (refineChunkSeg(chebyshev, residentSeg, nearSeg) ?? residentSeg);

    const geo = new THREE.PlaneGeometry(CHUNK_SIZE, CHUNK_SIZE, seg, seg);
    geo.rotateX(-Math.PI / 2); // lay flat in XZ (Y up)

    // Center of this chunk in world space.
    const worldCX = (k.cx + 0.5) * CHUNK_SIZE;
    const worldCZ = (k.cz + 0.5) * CHUNK_SIZE;

    // Displace vertices by the deterministic terrain height function.
    // Also bake per-vertex biome weights into two vec3 attributes so the ground shader
    // can read the EXACT seeded JS biomeWeights — matching foliage/height by construction.
    // biomeWA = [w0, w1, w2] (regolith_plains, crystal_forest, dune_desert)
    // biomeWB = [w3, w4, w5] (rocky_highlands,  moss_wetland,   fungal_basin)
    const pos = geo.attributes.position as THREE.BufferAttribute;
    const biomeWAData = new Float32Array(pos.count * 3);
    const biomeWBData = new Float32Array(pos.count * 3);
    const roadWData = new Float32Array(pos.count);
    const fieldWData = new Float32Array(pos.count);
    const heightGrid = new Float32Array(pos.count); // meshHeight fast-path cache
    const w6 = this.groundKind === 'v2' ? new Float64Array(pos.count * 6) : null; // ground v2: the rules' climate input
    for (let i = 0; i < pos.count; i++) {
      const wx = worldCX + pos.getX(i);
      const wz = worldCZ + pos.getZ(i);
      const h = stampedHeight(this.commonsSeedForChunks, wx, wz); // roads/pads conform the ground (trimesh colliders inherit)
      heightGrid[i] = h;
      pos.setY(i, h);
      // Pack biome weights — BIOMES order matches the shader's attribute read order.
      const bw = biomeWeights(this.commonsSeedForChunks, wx, wz);
      const pw = paintedWeights([bw[BIOMES[0]], bw[BIOMES[1]], bw[BIOMES[2]], bw[BIOMES[3]], bw[BIOMES[4]], bw[BIOMES[5]]], wx, wz);
      biomeWAData[i * 3 + 0] = pw[0]; // regolith_plains
      biomeWAData[i * 3 + 1] = pw[1]; // crystal_forest
      biomeWAData[i * 3 + 2] = pw[2]; // dune_desert
      biomeWBData[i * 3 + 0] = pw[3]; // rocky_highlands
      biomeWBData[i * 3 + 1] = pw[4]; // moss_wetland
      biomeWBData[i * 3 + 2] = pw[5]; // fungal_basin
      roadWData[i] = roadSurfaceMask(this.commonsSeedForChunks, wx, wz);
      fieldWData[i] = fieldMask(this.commonsSeedForChunks, wx, wz);
      if (w6) for (let q = 0; q < 6; q++) w6[i * 6 + q] = pw[q];
    }
    pos.needsUpdate = true;
    if (w6) {
      // Ground v2 (sync path): the same rules the worker runs.
      attachGround(geo, chunkGroundBuffer({ seed: this.commonsSeedForChunks, sea: commonsSeaLevel(this.commonsSeedForChunks), minX: worldCX - CHUNK_SIZE / 2, minZ: worldCZ - CHUNK_SIZE / 2, size: CHUNK_SIZE, seg, heights: heightGrid, weights: w6, road: roadWData, field: fieldWData }, currentTerrainKernel()));
      geo.deleteAttribute('uv');
    } else {
      geo.setAttribute('biomeWA', new THREE.BufferAttribute(biomeWAData, 3));
      geo.setAttribute('biomeWB', new THREE.BufferAttribute(biomeWBData, 3));
      geo.setAttribute('roadW', new THREE.BufferAttribute(roadWData, 1));
      geo.setAttribute('fieldW', new THREE.BufferAttribute(fieldWData, 1));
    }
    // shader gaps R5: analytic normals on the sync path too (lib/terrain_normals.ts)
    if (terrainNormalsMode() === 'analytic') { const seed = this.commonsSeedForChunks; seatAnalyticNormals(geo, analyticChunkNormals((x, z) => stampedHeight(seed, x, z), heightGrid, worldCX, worldCZ, CHUNK_SIZE, seg).normals, seg, THREE.BufferAttribute); terrainNormalStats.syncNormals++; }
    else { geo.computeVertexNormals(); terrainNormalStats.meshChunks++; }
    cutCaveMouths(geo, this.commonsSeedForChunks, worldCX, worldCZ, seg);
    addChunkSkirt(geo, seg, skirtDepthFor(EnvironmentModule.reliefOfGrid(heightGrid))); // see assembleChunk
    if (w6) interleaveGround(geo);

    const mat = this.groundMaterialFor(w6 ? 'v2' : 'v1');
    const mesh = new THREE.Mesh(geo, mat);
    mesh.castShadow = activePreset().shadowsEnabled !== false; // see assembleChunk
    mesh.receiveShadow = true;
    // Place the chunk at its world position via mesh.position instead of geo.translate,
    // so the geometry vertices stay origin-centered (local offsets) and the height
    // displacement lookup (wx = worldCX + pos.getX(i)) is correct without double-shifting XZ.
    mesh.position.set(worldCX, 0, worldCZ);
    mesh.matrixAutoUpdate = false; mesh.updateMatrix(); // static chunk (see the worker path)

    this.retireChunkMesh(key); // re-LOD swap (see assembleChunk) — no-op on a first build
    this.scene.add(mesh);
    if (this.appRef) void warmThenShow(this.appRef, mesh); // R4
    this.colliderSyncDirty = true; this.chunkMeshes.set(key, mesh);
    stitchChunkEdges(k.cx, k.cz, (cx, cz) => this.chunkMeshes.get(`${cx},${cz}`)?.geometry); // shader gaps R5: the border exchange
    this.chunkSegResident.set(key, seg);
    mesh.userData.groundKind = w6 ? 'v2' : 'v1'; this.chunkGroundGen.set(key, this.groundLayoutGen);
    // meshHeight interpolates THIS chunk's rendered grid directly (fast path).
    setChunkHeights(key, seg, heightGrid);

    // Kick off per-biome foliage scatter for this chunk asynchronously.
    // The scatter resolves prefab keys → assetIds from the catalog, loads each
    // asset, and instances it. If the chunk is removed before the load finishes
    // the scatterChunkFoliage guard disposes the partial group cleanly.
    this.foliagePending++;
    void this.scatterChunkFoliage(key, k).finally(() => { this.foliagePending--; });
  }

  /** Dispose all Commons chunk meshes and their per-chunk foliage groups (called on world exit or module dispose). */
  private clearChunks(): void {
    for (const [key, mesh] of this.chunkMeshes) {
      this.scene.remove(mesh);
      disposeLater(mesh.geometry); // (bug sweep) world-exit frames run multi-second — never free draw-listed geometry synchronously
      setChunkSeg(key, null);
      this.physics?.removeTerrainCollider(key);
    }
    this.chunkMeshes.clear();
    this.chunkSegResident.clear();
    this.chunkPending.clear();   // any in-flight worker results get discarded on arrival
    this.chunkResults.length = 0;
    // Reset the window-diff gate: prevWindow empties below, so the next tick
    // in a world MUST recompute even if the player is on the same cell.
    this.lastWindowPcx = Number.NaN;
    this.lastWindowPcz = Number.NaN;
    // Dispose all chunk foliage groups + release per-chunk asset refs.
    // Take a snapshot of keys first — disposeChunkFoliage mutates the maps.
    for (const key of [...this.chunkFoliage.keys()]) this.disposeChunkFoliage(key);
    // disposeChunkFoliage also clears chunkFoliageRefs entries, but handle any orphaned refs
    // (refs whose foliage group was never registered — e.g. mid-scatter eviction without a group).
    for (const [, refs] of this.chunkFoliageRefs) for (const r of refs) { if (r.v) this.assets.disposeVariant(r.id, 1); else this.assets.dispose(r.id); }
    this.chunkFoliageRefs.clear();
    this.chunkBuildQueue = [];
    this.prevWindow = [];
    this.grassDriver?.reset(); // the grass field stood on these chunks (visual-realism R2)
    this.forestHorizon?.clear(); // the stands belong to this world (R4b)
  }

  /**
   * Re-scatter every RESIDENT chunk's foliage at the active preset's density
   * (tier switch). Chunk keys are `${cx},${cz}` — the streamer only scatters at
   * chunk BUILD time, so already-resident chunks must be redone here.
   */
  /** Chunks awaiting a tier re-scatter — drained ONE per frame by chunkTick. */
  private rescatterQueue: string[] = [];

  /**
   * Drop the cached species materials so the next scatter rebuilds them with
   * the current foliage-FX switches.
   *
   * They are NOT disposed here. The batches still holding them are emptied by
   * the re-scatter that follows, and a material that was drawn this frame must
   * not be destroyed under the GPU shield — letting them fall out of reference
   * is the safe path (the wrapper GC reclaims them). Only the module teardown
   * disposes species materials.
   */
  private flushSpeciesMaterials(): void {
    this.speciesMats.clear();
    this.speciesMatSet.clear();
    this.foliageFx.t.length = 0;
    this.leafKeyOf.clear(); // surfaces (2026-09-27): the handles' species keys go with the handles
    this.foliageFx.w.length = 0;
    this.builtFoliageFx = null;
  }

  private rescatterFoliage(): void {
    if (!this.commonsSeedForChunks) return;
    const keys = [...this.chunkFoliage.keys()];
    if ((activePreset().foliageDensityScale ?? 1) <= 0) {
      // 0 % (2026-09-09, "you can not make foliage density 0 % and trees do
      // not go away"): the per-frame drain left the last chunks' trees
      // standing — a second at 60 fps, over a minute under a slow renderer.
      // Nothing is rebuilt at 0, so dispose EVERY resident chunk now (49
      // cheap disposes) and let the drain re-register the empty groups.
      for (const key of keys) this.disposeChunkFoliage(key);
      this.chunkFoliage.clear();
      this.rescatterQueue = keys;
      return;
    }
    // Enqueue only — the synchronous dispose-all + re-kick-all version stalled
    // the main thread for seconds on every tier switch (49 chunks × dispose +
    // 49 concurrent async scatters crowding the same frames).
    this.rescatterQueue = keys;
  }

  /**
   * THE GROUND KEEP-OUT, after the fact (2026-10-02, the owner: "grass was rendering inside the interiors of the
   * buildings"). A settlement assembles AFTER its chunks' ground cover was scattered, so the scatter's own test never
   * saw its landmarks or hamlet buildings. When the keep-out changes (lib/ground_keepout.ts): every resident instance
   * a new shape stands over is removed (nothing else in the chunk moves — no re-scatter, no flicker), the flora worker
   * re-plants the chunk once the changes go quiet (it filters its own reply), and where a shape came down the chunk is
   * queued to grow back. Every 300 ms; the work is only where the log says something changed.
   */
  private groundKeepOutTick(): void {
    const now = performance.now();
    if (now - this.keepOutCheckAt < 300) return;
    this.keepOutCheckAt = now;
    const v = groundKeepOutVersion();
    if (v !== this.keepOutV) {
      const ch = groundKeepOutChangesSince(this.keepOutV);
      this.keepOutV = v;
      this.keepOutStats.changes++;
      const added = new Set<string>();
      const touch = (x0: number, z0: number, x1: number, z1: number, removed: boolean): void => {
        const cx0 = Math.floor(x0 / CHUNK_SIZE), cx1 = Math.floor(x1 / CHUNK_SIZE), cz0 = Math.floor(z0 / CHUNK_SIZE), cz1 = Math.floor(z1 / CHUNK_SIZE);
        const keys: string[] = [];
        if (cx1 - cx0 > 64 || cz1 - cz0 > 64) keys.push(...this.chunkFoliageHandles.keys()); // the whole log: every resident chunk
        else for (let cz = cz0; cz <= cz1; cz++) for (let cx = cx0; cx <= cx1; cx++) { const k = `${cx},${cz}`; if (this.chunkFoliageHandles.has(k)) keys.push(k); }
        for (const k of keys) {
          added.add(k);
          if (removed && (this.chunkFoliageRing.get(k) ?? 3) <= 1) this.keepOutRegrowDue.set(k, now + 1500);
        }
      };
      if (ch === null) touch(-1e9, -1e9, 1e9, 1e9, false);
      else for (const q of ch) touch(q.x0, q.z0, q.x1, q.z1, q.removed);
      // remove the instances the new shapes stand over — at once (an interior may be entered right now)
      for (const key of added) {
        const handles = this.chunkFoliageHandles.get(key), xyz = this.chunkFoliageHandleXYZ.get(key);
        if (!handles || !xyz || !this.chunkFoliageInstancer) continue;
        const [cx, cz] = key.split(',').map(Number);
        const ko = groundKeepOutsNear(cx * CHUNK_SIZE - 2, cz * CHUNK_SIZE - 2, (cx + 1) * CHUNK_SIZE + 2, (cz + 1) * CHUNK_SIZE + 2);
        if (!ko.length) continue;
        this.keepOutFloraDue.set(key, now + 1500); // only a chunk a shape actually stands in is re-planted
        for (let i = 0; i < handles.length; i++) {
          if (handles[i] < 0) continue;
          if (insideGroundKeepOut(ko, xyz[3 * i], xyz[3 * i + 2], xyz[3 * i + 1])) { this.chunkFoliageInstancer.remove(handles[i]); handles[i] = -1; this.keepOutStats.removed++; }
        }
      }
      // the world foliage groups (server instance groups) — the same rule
      if (this.foliage && this.worldFoliageXYZ.length) {
        const W = this.worldFoliageXYZ;
        for (let i = 0; i < W.length; i += 4) {
          if (W[i] < 0) continue;
          const ko = groundKeepOutsNear(W[i + 1] - 1, W[i + 3] - 1, W[i + 1] + 1, W[i + 3] + 1);
          if (ko.length && insideGroundKeepOut(ko, W[i + 1], W[i + 3], W[i + 2])) { this.foliage.remove(W[i]); W[i] = -1; this.keepOutStats.removed++; }
        }
      }
    }
    // re-plant the flora / regrow a chunk once its changes have gone quiet
    const seed = this.commonsSeedForChunks;
    for (const [key, due] of this.keepOutRegrowDue) {
      if (due > now) continue;
      this.keepOutRegrowDue.delete(key);
      this.keepOutFloraDue.delete(key); // a full re-scatter re-plants the flora too
      if (this.chunkFoliage.has(key) && !this.rescatterQueue.includes(key)) { this.rescatterQueue.push(key); this.keepOutStats.regrow++; }
    }
    for (const [key, due] of this.keepOutFloraDue) {
      if (due > now) continue;
      this.keepOutFloraDue.delete(key);
      const grp = this.chunkFoliage.get(key), ring = this.chunkFoliageRing.get(key), conn = atlas.conn, wid = atlas.commonsWorldId;
      if (!grp || ring === undefined || ring > 2 || !seed || !conn || wid === null) continue;
      const [cx, cz] = key.split(',').map(Number);
      const ctr = { x: (cx + 0.5) * CHUNK_SIZE, z: (cz + 0.5) * CHUNK_SIZE };
      this.requestFloraScatter(key, { cx, cz }, ring, grp, seed, structureKeepOuts(conn, wid, ctr.x, ctr.z, CHUNK_SIZE / 2));
      this.keepOutStats.floraReplanted++;
    }
  }

  /** Remove and dispose a single chunk's foliage group (idempotent). */
  /** Ring of a chunk at Chebyshev distance `cheb` from the player's chunk (see FoliageRing). */
  private foliageRingOf(cheb: number): FoliageRing {
    const fr = activePreset().foliageRing ?? 1;
    return cheb <= 1 ? 0 : cheb <= fr ? 1 : cheb <= fr + FOREST_FAR_RINGS ? 2 : 3;
  }

  /**
   * Trunk colliders for the woods within FOREST_COLLIDE_M of the player (every 0.5 s): the
   * per-species trimesh cut from the cooked LOD2 variant (lib/tree_collider), scaled per
   * instance — the player walks BETWEEN trunks and under crowns, never into a canopy box.
   */
  private forestColliderTick(): void {
    if (!this.physics) return;
    this.floraScatter?.colliderTick(atlas.localX, atlas.localZ); // rocks + logs within 40 m (visual-realism R3/R6)
    const now = performance.now();
    if (now - this.lastForestColliderAt < 500) return;
    this.lastForestColliderAt = now;
    const px = atlas.localX, pz = atlas.localZ;
    const R2 = FOREST_COLLIDE_M * FOREST_COLLIDE_M, DROP2 = (FOREST_COLLIDE_M + 12) ** 2;
    const want = new Set<string>();
    for (const [key, recs] of this.chunkForestTrees) {
      for (let i = 0; i < recs.length; i++) {
        const t = recs[i];
        const d2 = (t.x - px) ** 2 + (t.z - pz) ** 2;
        if (d2 > R2) continue;
        const cid = `forest:${key}:${i}`;
        want.add(cid);
        if (this.forestColliders.has(cid)) continue;
        const proxy = this.trunkProxyFor(t.assetId);
        if (!proxy) continue;
        if (this.physics.addStaticCollider(cid, proxy, { x: t.x, y: t.y, z: t.z, yaw: t.yaw, scale: t.scale })) this.forestColliders.set(cid, key);
      }
    }
    for (const [cid, key] of this.forestColliders) {
      if (want.has(cid)) continue;
      const i = Number(cid.slice(cid.lastIndexOf(':') + 1));
      const t = this.chunkForestTrees.get(key)?.[i];
      if (t && (t.x - px) ** 2 + (t.z - pz) ** 2 < DROP2) continue; // hysteresis
      this.physics.removeStaticCollider(cid);
      this.forestColliders.delete(cid);
    }
  }
  /** The species' trunk proxy (cached; built once from its LOD2 variant, held for the session). */
  private trunkProxyFor(assetId: bigint): ColliderProxy | null {
    const k = String(assetId);
    const hit = this.trunkProxies.get(k);
    if (hit !== undefined) return hit;
    this.trunkProxies.set(k, null); // pending — the next tick picks it up
    void this.assets.loadRepresentation(assetId, 2).then((r) => {
      const m = firstMesh(r.object);
      const proxy = m ? trunkProxyFromGeometry(m.geometry) : null;
      this.trunkProxies.set(k, proxy);
      this.trunkProxyReleases.push(r.release);
      if (proxy) this.forestStats.proxies++;
    }).catch(() => { /* stays null: the tree is walk-through until its proxy lands */ });
    return null;
  }

  /**
   * Hand a chunk to the flora scatter worker (visual-realism R3/R6): geology in rings 0–2 (the far ring keeps
   * cliffs + big boulders), the understory in ring 0. The chunk's keep-outs the worker cannot see — placed
   * structures, settlement plan roads and pads (plans live on this thread), baked lakes — filter the reply.
   */
  private requestFloraScatter(key: string, k: ChunkKey, ring: FoliageRing, grp: THREE.Group, seed: string, keepOut: Array<{ x: number; z: number; r2: number }>): void {
    const fs = this.floraScatter; const p = activePreset();
    if (!fs || ring > 2 || ((p.geologyDensity ?? 0) <= 0 && (p.understoryDensity ?? 0) <= 0)) return;
    const x0 = k.cx * CHUNK_SIZE, z0 = k.cz * CHUNK_SIZE;
    const nearSite = nearestSiteDistFor(seed, x0 + CHUNK_SIZE / 2, z0 + CHUNK_SIZE / 2) < 1800;
    const groundKO = groundKeepOutsNear(x0 - 4, z0 - 4, x0 + CHUNK_SIZE + 4, z0 + CHUNK_SIZE + 4);
    const blocked = (x: number, z: number, r: number, wet: boolean): boolean => {
      for (const kv of keepOut) if ((kv.x - x) ** 2 + (kv.z - z) ** 2 < kv.r2 + r * r) return true;
      if (groundKO.length && insideGroundKeepOut(groundKO, x, z, meshHeight(seed, x, z))) return true; // the ground keep-out (2026-10-02)
      if (nearSite && (roadSurfaceMask(seed, x, z) > 0.12 || settlementClearance(seed, x, z) !== null)) return true;
      const lake = lakeSurfaceAtBaked(x, z);
      if (lake !== null && meshHeight(seed, x, z) < lake + (wet ? -0.25 : 0.1)) return true;
      return false;
    };
    void fs.scatter({
      key, seed, cx: k.cx, cz: k.cz, size: CHUNK_SIZE, seg: this.chunkSegResident.get(key) ?? CHUNK_SEG_NEAR, sea: commonsSeaLevel(seed),
      ring, geology: p.geologyDensity ?? 0, understory: p.understoryDensity ?? 0, woods: p.foliageDensityScale ?? 1,
      plantLod: nearFoliageLod(p), // the tier's near-foliage LOD (potato/low 2, med 1, high/ultra 0)
      stamps: buildingStampsNear(x0 - 32, z0 - 32, x0 + CHUNK_SIZE + 32, z0 + CHUNK_SIZE + 32),
      edits: terrainEditsNear(x0 - 32, z0 - 32, x0 + CHUNK_SIZE + 32, z0 + CHUNK_SIZE + 32),
    }, () => this.chunkFoliage.get(key) === grp, blocked).catch((e) => console.warn('[Environment] flora scatter failed', e));
  }

  /** The shared material of an understory species (visual-realism R6): the wind sway + trample, cloud shadows, the season tint. */
  private understoryMaterial(assetId: bigint, lod: number, geometry: THREE.BufferGeometry, material: THREE.Material): THREE.Material {
    const key = `u${assetId}#${lod}`;
    let mat = this.speciesMats.get(key);
    if (mat) return mat;
    mat = material.clone();
    (mat as THREE.Material & { userData: Record<string, unknown> }).userData.ownedClone = true;
    geometry.computeBoundingBox();
    const bb = geometry.boundingBox;
    const h = bb ? Math.max(0.3, bb.max.y - bb.min.y) : 1;
    if (foliageWindOn()) applyWind(mat, { maxHeight: h, swayStrength: 0.1, trample: trampleActive() });
    else pinStaticMotionVectorsOn(mat);
    if (isCardGeometry(geometry)) applyCardNormalBend(mat); // surfaces (2026-09-27): the reeds / herbs cards lit from the sky (lib/card_normals.ts)
    applySeasonTint(mat);
    if (activePreset().cloudShadows) applyCloudShadows(this.app, mat as never);
    keyFoliageGraph(mat, `understory:${key}`); // vegetation (2026-09-26): the wind's maxHeight is per species — THE CLASSIC-KEY LAW
    this.speciesMats.set(key, mat); this.speciesMatSet.add(mat);
    return mat;
  }

  private disposeChunkFoliage(key: string): void {
    this.floraScatter?.release(key); // the chunk's rocks, deadwood, understory and their colliders (visual-realism R3/R6)
    // the woods: trunk colliders, asset refs, records
    for (const [cid, ck] of this.forestColliders) if (ck === key) { this.physics?.removeStaticCollider(cid); this.forestColliders.delete(cid); }
    const fr = this.chunkForestReleases.get(key); if (fr) { for (const r of fr) r(); this.chunkForestReleases.delete(key); }
    const ft = this.chunkForestTrees.get(key); if (ft) { this.forestStats.trees -= ft.length; this.forestStats.giants -= ft.filter((t) => t.cls === 'giant').length; this.chunkForestTrees.delete(key); }
    const handles = this.chunkFoliageHandles.get(key);
    if (handles && this.chunkFoliageInstancer) {
      for (const id of handles) if (id >= 0) this.chunkFoliageInstancer.remove(id); // −1 = the keep-out already took it
    }
    this.chunkFoliageHandles.delete(key);
    this.chunkFoliageHandleXYZ.delete(key);
    const ih = this.chunkImpostorHandles.get(key);
    if (ih) { for (const h of ih) h.inst.remove(h.id); this.chunkImpostorHandles.delete(key); }
    const grp = this.chunkFoliage.get(key);
    if (grp) {
      this.scene.remove(grp);
      // Dispose all InstancedMesh children: geometry + material clones we own.
      grp.traverse((child) => {
        if (child instanceof THREE.InstancedMesh) {
          // The geometry is the original loaded mesh geometry (shared) — do NOT dispose it here;
          // the AssetModule's own ref-counting handles that. Dispose only the per-chunk material
          // clone we created (marked via userData.ownedClone).
          if ((child.material as THREE.Material & { userData?: { ownedClone?: boolean } }).userData?.ownedClone) {
            disposeLater(child.material as THREE.Material); // deferred (warp-teardown law)
          }
        }
      });
      this.chunkFoliage.delete(key);
    }
    // Release per-chunk asset refs so the G5 LRU can reclaim foliage geometry.
    // Idempotent: the cache release is clamped; chunkFoliageRefs.delete ensures no double-release.
    const refs = this.chunkFoliageRefs.get(key);
    if (refs) {
      for (const r of refs) { if (r.v) this.assets.disposeVariant(r.id, 1); else this.assets.dispose(r.id); }
      this.chunkFoliageRefs.delete(key);
    }
    this.chunkFoliageRing.delete(key);
  }

  /**
   * Scatter per-biome foliage across a freshly-built Commons chunk.
   *
   * For each candidate scatter point we:
   *  1. Pick the point's dominant biome via `biomeAt(seed, worldX, worldZ)`.
   *  2. Choose a flora prefab key from `BIOME_FLORA[biome].prefabKeys`
   *     deterministically (by point index so the per-asset de-stack still applies).
   *  3. Resolve the prefab key → assetId via `conn.db.prefabModel`.
   *  4. Load the asset, clone the material, and add an instance to a
   *     THREE.Group that is stored in `chunkFoliage` and added to the scene.
   *
   * The building keep-out and the props-sparse / plants-dense rule from
   * `scatterGroup` are both reused here.
   *
   * If the chunk is removed before this async method finishes the group is
   * cleaned up by the `chunkFoliage.has(key)` guard below.
   */
  private async scatterChunkFoliage(key: string, k: ChunkKey): Promise<void> {
    const conn = atlas.conn;
    if (!conn || !this.commonsSeedForChunks) return;

    const seed = this.commonsSeedForChunks;
    const worldId = atlas.commonsWorldId;

    // Create and register the foliage group for this chunk immediately so that
    // `disposeChunkFoliage` can clean it up if the chunk is evicted mid-scatter.
    const grp = new THREE.Group();
    grp.name = `foliage:${key}`;
    this.chunkFoliage.set(key, grp);
    this.scene.add(grp);

    const worldCX = (k.cx + 0.5) * CHUNK_SIZE;
    const worldCZ = (k.cz + 0.5) * CHUNK_SIZE;
    const HALF = CHUNK_SIZE / 2;

    // Foliage ring LOD (the content-LOD successor to F11/F17): beyond the near
    // chunk ring (~256 m Chebyshev) trees carry ~15k tri each with no distance
    // treatment — the top census group post-F11 (~62M tri at high/ultra). Far
    // chunks scatter the cooked LOD1 variants (trees + rocks) and skip
    // undergrowth entirely (subpixel at that range). Ring transitions re-scatter
    // via the 1/frame drain (see chunkTick).
    const pccx = chunkOf(atlas.localX, CHUNK_SIZE);
    const pccz = chunkOf(atlas.localZ, CHUNK_SIZE);
    const cheb = Math.max(Math.abs(k.cx - pccx), Math.abs(k.cz - pccz));
    // TERRAIN-ONLY beyond the tier's foliage ring: extending the draw distance
    // grows the ground footprint but must NOT scatter the tree whale to the
    // horizon (foliage is 99% of the frame's triangles). Empty group stands.
    const ring = this.foliageRingOf(cheb);
    this.chunkFoliageRing.set(key, ring);
    if (ring === 3) return;
    const farRing = cheb > 1;
    const biomeScatter = ring <= 1; // beyond the tier's foliage ring only the woods' far trees stand

    // Building keep-out — same logic as scatterGroup. The catalog scale is read through ONE map per
    // pass, and only structures whose clear disc can reach THIS chunk are kept (bug sweep 2026-09-22):
    // every structure searched the whole prefab catalog, then every candidate tree was tested against
    // every structure in the world.
    const keepOut: Array<{ x: number; z: number; r2: number }> = worldId !== null ? structureKeepOuts(conn, worldId, worldCX, worldCZ, HALF) : [];
    const groundKO = groundKeepOutsNear(worldCX - HALF, worldCZ - HALF, worldCX + HALF, worldCZ + HALF);

    // Build a prefabKey → assetId lookup from the catalog.
    const keyToAsset = new Map<string, bigint>();
    for (const pm of conn.db.prefabModel.iter()) keyToAsset.set(pm.prefabKey, pm.assetId);

    type MeshData = { geometry: THREE.BufferGeometry; material: THREE.Material };
    // Asset load cache: one load per unique assetId within this chunk scatter pass.
    // If a load fails we skip that asset for the rest of the pass (don't retry inline).
    const loadedMeshes = new Map<bigint, MeshData | null>();
    const variantLoaded = new Set<bigint>();
    /** Release respecting HOW the asset was acquired (variant vs canonical). */
    const rel = (id: bigint): void => { if (variantLoaded.has(id)) this.assets.disposeVariant(id, 1); else this.assets.dispose(id); };
    const loadAsset = async (assetId: bigint): Promise<MeshData | null> => {
      if (loadedMeshes.has(assetId)) return loadedMeshes.get(assetId) ?? null;
      // Guard: PASS IDENTITY, not key presence — a rescatter (tier change /
      // ring reclassification) disposes then immediately re-registers the key,
      // so a stale in-flight pass would sail through `.has(key)` and race the
      // new pass (orphaned instancer handles + leaked refs; sweep-confirmed).
      if (this.chunkFoliage.get(key) !== grp) { loadedMeshes.set(assetId, null); return null; }
      let obj: THREE.Object3D;
      try {
        // Far ring: always LOD1. Near ring: the tier's nearFoliageLod (R2 —
        // potato/low/med take LOD1 up close too; high/ultra keep LOD0).
        const lod = farRing ? 1 : nearFoliageLod(activePreset());
        if (lod === 1) {
          // loadRepresentation resolves variant-vs-canonical BEFORE loading, so
          // variantLoaded matches the key actually acquired (loadVariant's own
          // fallback returned the canonical while we tagged it 'variant' — the
          // later disposeVariant then released NOTHING).
          const r = await this.assets.loadRepresentation(assetId, 1);
          obj = r.object;
          if (this.assets.hasVariant(assetId, 1)) { variantLoaded.add(assetId); this.nearLodStats.lod1++; } else this.nearLodStats.lod0++;
        } else {
          obj = await this.assets.load(assetId);
          this.nearLodStats.lod0++;
        }
      } catch { loadedMeshes.set(assetId, null); return null; }
      if (this.chunkFoliage.get(key) !== grp) { rel(assetId); loadedMeshes.set(assetId, null); return null; }
      const m = firstMesh(obj);
      if (!m) { rel(assetId); loadedMeshes.set(assetId, null); return null; }
      loadedMeshes.set(assetId, m);
      return m;
    };

    // ── Per-biome scatter ────────────────────────────────────────────────────────
    // We iterate all 6 biomes and scatter each biome's flora over the candidate
    // points that fall in that biome. This ensures the density scalar is honoured
    // per biome and avoids re-computing biomeAt more than once per point.
    //
    // Implementation: one flat scatter pass — for each candidate point in the
    // chunk we resolve its biome, pick a flora key deterministically, and instance
    // it. The total candidate count is the MAX density across biomes so that dense
    // biomes get their full count; sparse biomes simply skip most points (the rng3
    // seed-by-asset de-stack ensures positions stay distinct per asset).
    // Tier scale on flora density (F1 — foliage is the frame's triangle whale;
    // ~15k tri per tree with no LOD). Applied to the per-biome budget below so
    // dense and sparse biomes shrink proportionally.
    const foliageScale = activePreset().foliageDensityScale ?? 1;
    const maxDensity = Math.max(...Object.values(BIOME_FLORA).map((b) => b.density));

    // Per-biome InstancedMesh builders (accumulated, flushed to the group at the end).
    // Map: assetId → { matrices: THREE.Matrix4[], meshData: MeshData, isTree: boolean }
    const batches = new Map<string, { assetId: bigint; matrices: THREE.Matrix4[]; meshData: MeshData; isTree: boolean }>();
    const shadowBatches = new Set<string>();
    const impHandles: Array<{ inst: ImpostorInstancer; id: number }> = [];

    const up = new THREE.Vector3(0, 1, 0);

    for (let i = 0; biomeScatter && i < maxDensity; i++) {
      // Candidate position — seeded by chunk coords + i (asset-independent base).
      const [ax, az] = rng3(k.cx, k.cz, i, 0);
      const wx = worldCX + (ax - 0.5) * 2 * HALF;
      const wz = worldCZ + (az - 0.5) * 2 * HALF;

      // Building keep-out (same rule as scatterGroup).
      if (keepOut.some((kv) => (kv.x - wx) ** 2 + (kv.z - wz) ** 2 < kv.r2)) continue;

      // Settlement street discipline (Unbuilt Town wave): wild flora used to
      // co-scatter straight onto carriageways, worn tracks, plazas and pad
      // ground (nothing settlement-aware existed here). Streets and squares
      // now reject wild growth — the deliberate street trees the settlement
      // dressing plants are the only trees inside the fabric.
      if (roadSurfaceMask(seed, wx, wz) > 0.22) continue;
      if (settlementClearance(seed, wx, wz) !== null) continue; // inside a pad/port footprint

      // Resolve biome at this world-space point.
      const biome = biomeAt(seed, wx, wz);
      const flora = BIOME_FLORA[biome];
      if (!flora || flora.prefabKeys.length === 0) continue;

      // Skip if this point is beyond the biome's TIER-SCALED density budget
      // (sparse biomes reject most candidates while dense biomes accept all of
      // them up to their count; the tier scale shrinks every biome's budget).
      if (i >= flora.density * foliageScale) continue;

      // The candidate's CLASS (tree / rock / plant) still comes from its index — the old lottery's mix —
      // but a plant's SPECIES is the one best suited to the spot (visual-realism R5: viability by
      // moisture, shade, slope, altitude — never `i % keys`), and with the understory / geology layers
      // on, their rules own the plants and the rocks here (lib/understory.ts, lib/geology).
      let prefabKey = flora.prefabKeys[i % flora.prefabKeys.length];
      // Trees (keys ending in _tree) scatter sparse (1 in 5 candidates accepted).
      const isTree = prefabKey.endsWith('_tree');
      // Rocks: size-based sparsity — small common, large rare (landmark boulders).
      const isRock = prefabKey.includes(':rock_');
      if (isRock && (activePreset().geologyDensity ?? 0) > 0) continue; // the geology layer seats rock by shape
      if (!isTree && !isRock) {
        if (ring === 0 && (activePreset().understoryDensity ?? 0) > 0) continue; // the understory layer plants by ecology
        const bw = biomeWeights(seed, wx, wz);
        const eco = pickSpecies(UNDERSTORY[biome], { wet: ecoWetness(999, 0, bw.moss_wetland, bw.dune_desert), shade: forestDensity(seed, wx, wz, commonsSeaLevel(seed)), slope: 0.1, alt: 50, waterDist: 999 }, rng3(k.cx, k.cz, i, 7)[2]);
        if (eco) prefabKey = eco.t.key;
      }
      const assetId = keyToAsset.get(prefabKey);
      if (assetId === undefined) continue; // key not in catalog — skip silently
      // Far ring: undergrowth (non-tree, non-rock) is subpixel beyond ~256 m.
      if (farRing && !isTree && !isRock) continue;
      if (isTree) {
        // De-stack: use the same rng3 with assetId seed to pick a unique position
        // for this asset, mirroring the scatterGroup approach. Only accept 1-in-5.
        const [, , tc] = rng3(k.cx, k.cz, i, Number(assetId));
        if (tc > 0.2) continue; // sparse
      } else if (isRock) {
        const [, , rc] = rng3(k.cx, k.cz, i, Number(assetId));
        const keep = prefabKey.includes('rock_large') ? 0.05 : prefabKey.includes('rock_medium') ? 0.12 : 0.3;
        if (rc > keep) continue;
      }

      // Load the mesh (cached within this scatter pass).
      const mesh = await loadAsset(assetId);
      if (!mesh) continue;

      // Bail if this pass went stale while loading (evicted OR superseded).
      if (this.chunkFoliage.get(key) !== grp) {
        // Release only entries still HOLDING a ref: null entries either never
        // acquired one or already released it — re-releasing them stole refs
        // held by warmFloraAssets / other chunks (sweep-confirmed corruption).
        for (const [aid, md] of loadedMeshes) if (md) rel(aid);
        return;
      }

      // Build the instance matrix.
      // Seed per-asset position jitter (de-stack: prevents all assets at same spot).
      const [jx, jz, rot] = rng3(k.cx, k.cz, i, Number(assetId));
      const ix = worldCX + (jx - 0.5) * 2 * HALF;
      const iz = worldCZ + (jz - 0.5) * 2 * HALF;
      // Re-check keep-out for the per-asset jittered position.
      if (keepOut.some((kv) => (kv.x - ix) ** 2 + (kv.z - iz) ** 2 < kv.r2)) continue;

      // Footprint-aware seat (min height − embed) + cliff skip — trees sample a wider
      // footprint than groundcover so large canopies never float on the downhill side.
      const seat = terrainSeat(seed, ix, iz, isTree ? 1.2 : isRock ? 0.9 : 0.5, isTree ? 0.2 : isRock ? 0.3 : 0.1);
      // Rocks TOLERATE steep ground (talus/scree is exactly where they belong).
      if (seat.slope > (isTree ? 0.9 : isRock ? 2.0 : 1.2)) continue;
      // THE GROUND KEEP-OUT (2026-10-02): no plant, rock or tree inside a standing building or an interior's box —
      // landmarks and hamlet buildings stand on no pad, and grass came up through their floors.
      if (groundKO.length && insideGroundKeepOut(groundKO, ix, iz, seat.y)) continue;
      // No vegetation under the sea OR under a baked hydrology lake — rocks
      // are fine on the bed shallows.
      if (!isRock && seat.y < commonsSeaLevel(seed) + 0.4) continue;
      if (!isRock) {
        const lake = lakeSurfaceAtBaked(ix, iz);
        if (lake !== null && seat.y < lake + 0.1) continue;
        const river = riverWaterAt(seed, ix, iz); // no plants in a river channel (terrain wave 2026-09-08)
        if (river !== null && seat.y < river + 0.1) continue;
      }
      const q = new THREE.Quaternion().setFromAxisAngle(up, rot * Math.PI * 2);
      const scaleBase = isTree ? 1.0 + jx * 0.5 : isRock ? 0.7 + jx * 0.9 : 0.6 + jx * 0.5;
      const s = new THREE.Vector3(scaleBase, scaleBase, scaleBase);
      const p = new THREE.Vector3(ix, seat.y, iz);
      const mat = new THREE.Matrix4().compose(p, q, s);

      // FAR RING TREES → OCTAHEDRAL IMPOSTORS (perf frontier 2026-09-08, item 5):
      // one billboard per tree from a per-species atlas instead of the LOD1 mesh.
      if (farRing && isTree) {
        const inst = this.impostorFor(assetId, mesh.geometry, mesh.material);
        if (inst) {
          const iid = inst.add(ix, seat.y, iz, scaleBase);
          if (iid >= 0) { impHandles.push({ inst, id: iid }); continue; }
        }
      }
      const bk = `${assetId}#${variantLoaded.has(assetId) ? 1 : 0}`;
      if (isTree || isRock) shadowBatches.add(bk); // groundcover never casts: sub-pixel shadows, thousands of casters per cascade
      let batch = batches.get(bk);
      if (!batch) {
        batch = { assetId, matrices: [], meshData: mesh, isTree };
        batches.set(bk, batch);
      }
      batch.matrices.push(mat);
    }

    // ── THE WOODS (flora update 2026-09-07) ─────────────────────────────────
    // Clustered stands from the seed-derived forest field (lib/forest_field):
    // biome species in three size classes (medium 7–12 m, large 12–22 m, giant
    // 28–52 m) scaled from the ~1.9 m Meshy bodies, seated by footprint, kept
    // off roads, pads, the sea and baked lakes. Their own LOD ladder: ring 0
    // large/giant at the tier's near LOD (medium always LOD1), ring 1 LOD1,
    // ring 2 (beyond the tier's foliage ring) large + giant only at LOD2 —
    // a forest silhouette to the horizon at a few thousand tris per stand.
    const forestRecs: ForestTreeRec[] = [];
    const forestReleases: Array<() => void> = [];
    {
      const sea = commonsSeaLevel(seed);
      // The tier thins the stand (the existing foliage density lever) and picks the levels per class:
      //   ring 0  medium LOD2 below high / LOD1 at high+ · large LOD1 (LOD0 on ultra) · giant LOD1 (LOD0 on ultra)
      //   ring 1  medium + large LOD2 · giant LOD1
      //   ring 2  giants + every third large at LOD2 (the forest horizon), no medium
      const tier = activeTier();
      const trees = forestTreesForChunk(seed, k.cx * CHUNK_SIZE, k.cz * CHUNK_SIZE, CHUNK_SIZE, sea, activePreset().foliageDensityScale ?? 1);
      const fLoaded = new Map<string, { md: MeshData; rawH: number; minY: number } | null>();
      const lodFor = (cls: TreeClass): number => {
        if (ring === 2) return 2;
        if (ring === 1) return cls === 'giant' && (tier === 'high' || tier === 'ultra') ? 1 : 2;
        if (cls === 'medium') return tier === 'high' || tier === 'ultra' ? 1 : 2;
        if (cls === 'large') return tier === 'ultra' ? 0 : tier === 'high' || tier === 'med' ? 1 : 2;
        return tier === 'ultra' ? 0 : tier === 'high' ? 1 : 2; // giants: 150k at LOD0 — 137 of them at LOD1 were 10 M tris on potato
      };
      let farLargeN = 0;
      for (const t of trees) {
        if (ring === 2 && t.cls === 'medium') continue;
        if (ring === 2 && t.cls === 'large' && (farLargeN++ % 3) !== 0) continue;
        if (clearingAt(seed, t.x, t.z, t.density) > 0.5) continue; // R5: clearings — the flora worker drops the same trunks
        if (keepOut.some((kv) => (kv.x - t.x) ** 2 + (kv.z - t.z) ** 2 < kv.r2)) continue;
        if (roadSurfaceMask(seed, t.x, t.z) > 0.22) continue;
        if (settlementClearance(seed, t.x, t.z) !== null) continue;
        const foot = t.cls === 'giant' ? 4 : t.cls === 'large' ? 2 : 1.2;
        const seat = terrainSeat(seed, t.x, t.z, foot, 0.25);
        if (seat.slope > 0.9) continue;
        if (groundKO.length && insideGroundKeepOut(groundKO, t.x, t.z, seat.y)) continue; // the ground keep-out
        if (seat.y < sea + 0.4) continue;
        const lake = lakeSurfaceAtBaked(t.x, t.z);
        if (lake !== null && seat.y < lake + 0.1) continue; // no woods on a lake
        const river = riverWaterAt(seed, t.x, t.z);
        if (river !== null && seat.y < river + 0.1) continue; // nor in a river
        let assetId = keyToAsset.get(t.key);
        if (assetId === undefined && t.cls === 'giant') assetId = keyToAsset.get(giantFallback(biomeAt(seed, t.x, t.z))); // the giant self-heals to the largest species
        if (assetId === undefined) continue;
        const lod = lodFor(t.cls);
        const ck = `${assetId}#${lod}`;
        let fl = fLoaded.get(ck);
        if (fl === undefined) {
          if (this.chunkFoliage.get(key) !== grp) { for (const r of forestReleases) r(); for (const [aid, md] of loadedMeshes) if (md) rel(aid); return; }
          try {
            const r = await this.assets.loadRepresentation(assetId, lod);
            const m = firstMesh(r.object);
            if (!m) { r.release(); fLoaded.set(ck, null); continue; }
            m.geometry.computeBoundingBox();
            const bb = m.geometry.boundingBox!;
            fl = { md: m, rawH: Math.max(0.2, bb.max.y - bb.min.y), minY: bb.min.y };
            fLoaded.set(ck, fl); forestReleases.push(r.release);
          } catch { fLoaded.set(ck, null); continue; }
        }
        if (!fl) continue;
        if (this.chunkFoliage.get(key) !== grp) { for (const r of forestReleases) r(); for (const [aid, md] of loadedMeshes) if (md) rel(aid); return; }
        const sc = t.targetH / fl.rawH;
        const y = seat.y - fl.minY * sc;
        if (ring === 2) {
          // The horizon stands (ring 2) draw as impostors too — the silhouette to the horizon at 2 triangles a tree.
          const inst = this.impostorFor(assetId, fl.md.geometry, fl.md.material);
          if (inst) {
            const iid = inst.add(t.x, y, t.z, sc);
            if (iid >= 0) { impHandles.push({ inst, id: iid }); forestRecs.push({ x: t.x, y, z: t.z, yaw: t.yaw, scale: sc, assetId, cls: t.cls, targetH: t.targetH }); continue; }
          }
        }
        const q = new THREE.Quaternion().setFromAxisAngle(up, t.yaw);
        const mat = new THREE.Matrix4().compose(new THREE.Vector3(t.x, y, t.z), q, new THREE.Vector3(sc, sc, sc));
        let batch = batches.get(ck);
        if (!batch) { batch = { assetId, matrices: [], meshData: fl.md, isTree: true }; batches.set(ck, batch); }
        batch.matrices.push(mat);
        forestRecs.push({ x: t.x, y, z: t.z, yaw: t.yaw, scale: sc, assetId, cls: t.cls, targetH: t.targetH });
      }
    }

    // Guard: this pass may have gone stale (evicted OR superseded) while awaiting loads.
    if (this.chunkFoliage.get(key) !== grp) {
      // Release only ref-holding (non-null) entries — see the bail above.
      for (const [aid, md] of loadedMeshes) if (md) rel(aid);
      return;
    }

    // Flush batches → InstancedMesh children of the chunk's foliage group.
    // Record each used assetId in chunkFoliageRefs[key] (NOT the shared foliageReleases[]) so that
    // disposeChunkFoliage can release them per-chunk, keeping the G5 LRU able to reclaim foliage.
    const chunkRefs: { id: bigint; v: boolean }[] = [];
    if (!this.chunkFoliageInstancer) this.chunkFoliageInstancer = new StaticInstancer(this.scene, 16384, 'chunk-foliage');
    const handles: number[] = [];
    const handleXYZ: number[] = [];
    this.chunkFoliageHandles.set(key, handles);
    this.chunkFoliageHandleXYZ.set(key, handleXYZ);
    if (impHandles.length) this.chunkImpostorHandles.set(key, impHandles);
    for (const [bk, batch] of batches) {
      if (batch.matrices.length === 0) continue;
      const assetId = batch.assetId;

      // ONE shared material per species AND level (not per chunk): applyWind mutations
      // are identical everywhere, and each per-chunk clone used to compile its
      // own pipeline on first view (the look-direction freeze factory).
      const matKey = bk;
      let mat = this.speciesMats.get(matKey);
      if (!mat) {
        mat = batch.meshData.material.clone();
        (mat as THREE.Material & { userData: Record<string, unknown> }).userData.ownedClone = true;
        // Translucency and WIND are separate switches (2026-09-14): a custom
        // profile can keep a cheap tier and still have the trees move. The
        // pair the cache was built with is remembered in builtFoliageFx so a
        // menu change rebuilds these materials instead of silently reusing
        // them (they are built once per species and cached).
        const wantShading = activePreset().foliageShading !== false;
        const wantWind = foliageWindOn();
        this.builtFoliageFx = { shading: wantShading, wind: wantWind };
        // surfaces (2026-09-27): a groundcover CARD (vertical quads, horizontal normals) bends its shading normal toward
        // the sky — under a high sun the cooked cards drew near-black (the reeds on the beach). lib/card_normals.ts.
        const isCard = !batch.isTree && isCardGeometry(batch.meshData.geometry);
        if (!batch.isTree) {
          if (wantWind) applyWind(mat as THREE.MeshStandardMaterial, { maxHeight: 0.8, trample: trampleActive() });
          if (isCard) applyCardNormalBend(mat);
        } else if (wantShading || wantWind) {
          // Shader wave stage 2: leaves lit from behind (wrap + back transmission)
          // and the engine's trunk/branch/leaf wind on every tree species.
          try {
            const bb = batch.meshData.geometry.boundingBox ?? (batch.meshData.geometry.computeBoundingBox(), batch.meshData.geometry.boundingBox!);
            // surfaces (2026-09-27): §5.3 the transmitted colour follows the leaf's albedo and a crown mask keeps the bark
            // dark — one material draws trunk and leaves, and a flat tint made the back-lit crown one olive silhouette.
            if (wantShading) {
              const crown = this.crownFor(batch.meshData.geometry);
              // surfaces (2026-09-27): §5.3 the back transmission scales by the species' leaf thickness (lib/leaf_transmission.ts)
              const leafKey = this.speciesKeyOf(batch.assetId);
              const th = applyFoliageTranslucency(mat as never, { wrap: 0.45, backIntensity: 0.55 * this.leafKFor(leafKey), backPower: 2.5, albedoTint: this.leafLook.albedo, crown: crown ?? undefined });
              this.leafKeyOf.set(th, leafKey);
              if (crown) th.crownMix.value = this.leafLook.crown;
              this.foliageFx.t.push(th);
            }
            // meshBaseY: the trunk weight is measured from the geometry's own
            // base, so a centre-pivoted tree still gets a rigid bottom.
            // strength/direction are re-driven every frame from the weather
            // (updateFoliageDrivers) — these are just the calm-day defaults.
            if (wantWind) this.foliageFx.w.push(applyHierarchicalWind(mat as never, {
              meshHeight: Math.max(1, bb.max.y - bb.min.y), meshBaseY: bb.min.y,
              strength: 0.45, direction: [0.8, 0.6],
            }));
          } catch { /* best-effort */ }
        }
        if (activePreset().cloudShadows) applyCloudShadows(this.app, mat as never); // stage 4: every species, tree or groundcover
        applySeasonTint(mat); // R9: the season / dryness tint (window.__seasonTint)
        // vegetation (2026-09-26): a tree's wind bakes its own meshHeight / meshBaseY — THE CLASSIC-KEY LAW (plants' wind is one constant set: they may share)
        if (batch.isTree) keyFoliageGraph(mat, `foliage:${matKey}`);
        // the bent cards share ONE program (identical graphs), never the plain groundcover's (THE CLASSIC-KEY LAW)
        if (isCard) { const ck = cardProgramKey(wantWind, trampleActive(), !!activePreset().cloudShadows); (mat as unknown as { customProgramCacheKey: () => string }).customProgramCacheKey = () => ck; mat.userData.graphKey = ck; }
        this.speciesMats.set(matKey, mat);
        this.speciesMatSet.add(mat);
      }
      for (const mx of batch.matrices) {
        const id = this.chunkFoliageInstancer.add(batch.meshData.geometry, mat, mx);
        if (id >= 0) { handles.push(id); handleXYZ.push(mx.elements[12], mx.elements[13], mx.elements[14]); }
      }
      // (Per-chunk frustum culling is traded away: batches span loaded chunks —
      // the settlement-instancer precedent; draw-item count wins on r182.)

      // Record the asset ref per-chunk so disposeChunkFoliage can release it (the biome pass's
      // loads; the woods' loads release through their own closures below).
      // Do NOT push to this.foliageReleases[] — that list is for the non-Commons buildFoliage path.
      if (loadedMeshes.has(assetId) && !chunkRefs.some((r) => r.id === assetId)) chunkRefs.push({ id: assetId, v: variantLoaded.has(assetId) });
    }
    // Register all refs at once (idempotent: disposeChunkFoliage will clear this entry).
    // Species batches cast shadows (the pool creates meshes castShadow=false):
    // flag the batches we just touched via the pool — the previous full
    // scene.traverse ran once per streamed chunk.
    for (const [bk, batch] of batches) {
      if (batch.matrices.length === 0) continue;
      const mat = this.speciesMats.get(bk);
      const m = mat ? this.chunkFoliageInstancer?.meshFor(batch.meshData.geometry, mat) : null;
      // Perf frontier (shadow caster culling by size): trees and rocks cast, groundcover does not.
      if (m) (m as THREE.Object3D).castShadow = shadowBatches.has(bk);
      // Render wave 1: the same predicate picks the ROOFS for the rain-shelter bake — trees and rocks, never groundcover (a blade must not dry the ground).
      if (m) { if (shadowBatches.has(bk)) (m as THREE.Object3D).layers.enable(RAIN_SHELTER_LAYER); else (m as THREE.Object3D).layers.disable(RAIN_SHELTER_LAYER); }
    }
    if (chunkRefs.length > 0) this.chunkFoliageRefs.set(key, chunkRefs);
    if (forestRecs.length > 0) this.chunkForestTrees.set(key, forestRecs);
    if (forestReleases.length > 0) this.chunkForestReleases.set(key, forestReleases);
    this.forestStats.trees += forestRecs.length;
    this.forestStats.giants += forestRecs.filter((t) => t.cls === 'giant').length;
    if (ring === 2) this.forestStats.farTrees += forestRecs.length;

    // ── GEOLOGY + UNDERSTORY (visual-realism R3/R6, 2026-09-25): the flora worker plants rock by the terrain's
    // shape and the woods' floor by ecology; this pass only filters and uploads (lib/geology/flora_scatter.ts).
    this.requestFloraScatter(key, k, ring, grp, seed, keepOut);

    // Release any loaded assets that ended up with zero instances (filtered by keepOut etc.)
    // and were NOT recorded in chunkRefs — their refcount must come down.
    const usedAssets = new Set(chunkRefs.map((r) => r.id));
    for (const [aid, meshData] of loadedMeshes) {
      if (meshData !== null && !usedAssets.has(aid)) rel(aid);
    }
  }

  // ── Flora asset pre-warm ─────────────────────────────────────────────────────
  // Chunk scatter loads flora GLBs ON DEMAND at chunk arrival, so a cold cache
  // put fetch+decode on the exploration hot path. Measured consequence: at boot
  // the loading gate's ~75 s timeout could reveal a floraless world whose ~13 k
  // instances then landed MID-PLAY as one burst (the probe's bimodal spawn-city
  // census — 5.7 k vs 18.7 k instances — and the "sometimes lag"); mid-play,
  // the first chunk of any new biome paid the same stall. Warming the union of
  // BIOME_FLORA prefabs at entry (19 small GLBs, refs held for the session)
  // turns every scatter into a cache hit.
  private floraWarmIds: bigint[] = [];
  private floraWarmReleases: Array<() => void> = [];
  /** R2 diag: near-ring foliage loads by LOD (`__nearLod()`). */
  readonly nearLodStats = { lod0: 0, lod1: 0 };
  private floraWarmStarted = false;

  private warmFloraAssets(): void {
    if (this.floraWarmStarted) return;
    const conn = atlas.conn;
    if (!conn) return;
    const keyToAsset = new Map<string, bigint>();
    for (const pm of conn.db.prefabModel.iter()) keyToAsset.set(pm.prefabKey, pm.assetId);
    // Empty-catalog gotcha: the prefab_model subscription may not have streamed
    // yet — return WITHOUT latching so the next hook (world-entered) retries.
    if (keyToAsset.size === 0) return;
    const ids = new Set<bigint>();
    for (const flora of Object.values(BIOME_FLORA)) {
      for (const k of flora.prefabKeys) {
        const id = keyToAsset.get(k);
        if (id !== undefined) ids.add(id);
      }
    }
    for (const sp of Object.values(FOREST_SPECIES)) { // the woods' giants
      const id = keyToAsset.get(sp.giant);
      if (id !== undefined) ids.add(id);
    }
    if (ids.size === 0) return; // flora keys absent from this catalog — retry later
    this.floraWarmStarted = true;
    const queue = [...ids];
    void Promise.all(Array.from({ length: 3 }, async () => {
      for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
        try {
          // Warm the representation the near ring will actually use (R2) —
          // warming LOD0 on a tier that scatters LOD1 downloaded 10 MB shrubs
          // it never drew.
          const { object: obj, release } = await this.assets.loadRepresentation(id, nearFoliageLod(activePreset()));
          this.floraWarmReleases.push(release); // hold the ref: flora is in constant reuse, never LRU fodder
          // Pre-upload its textures too — first BIND uploads compressed KTX2 on
          // the main thread (profiled 6.3 s bursts at arrival); warm-time drain
          // moves that into the load window.
          if (this.appRef) queueTextureWarm(this.appRef, obj);
        } catch { /* best-effort — the scatter path's own load remains the fallback */ }
      }
    }));
  }

  dispose(): void {
    // 2026-08-23 sweep: teardown freed about half of what the module owned —
    // the chunk-foliage pool (its render-anchor listener kept calling a dead
    // pool on every rebase), species materials, crates, clash sprites, the
    // versona root, the ping timer, the keydown handler and the bus handlers.
    if (this.onBazaarKey) window.removeEventListener('keydown', this.onBazaarKey);
    this.onBazaarKey = null;
    for (const off of this.busOffs) off();
    this.busOffs = [];
    if (this.pingTimer !== null) { window.clearTimeout(this.pingTimer); this.pingTimer = null; }
    this.crowdAnchor.release();
    this.scene.remove(this.crowdAnchor.group);
    this.scene.remove(this.versonaRoot);
    for (const release of this.floraWarmReleases) release(); // matched to the representation acquired (LOD0 or LOD1)
    this.floraWarmReleases = [];
    this.floraWarmIds = [];
    if (this.terrainMesh) { this.scene.remove(this.terrainMesh); this.terrainMesh.geometry.dispose(); (this.terrainMesh.material as THREE.Material).dispose(); this.terrainMesh = null; }
    this.chunkWorker?.terminate();
    this.chunkWorker = null;
    this.clearChunks();
    this.chunkFoliageInstancer?.dispose();
    this.chunkFoliageInstancer = null;
    this.grassDriver?.dispose(); this.grassDriver = null; // before the trample field it samples
    this.floraScatter?.dispose(); this.floraScatter = null;
    this.forestHorizon?.dispose(); this.forestHorizon = null; // before the impostor instancers it adds to
    disposeTrample();
    for (const m of this.speciesMats.values()) m.dispose();
    this.speciesMats.clear(); this.foliageFx.t.length = 0; this.foliageFx.w.length = 0; this.leafKeyOf.clear(); // surfaces (2026-09-27): §5.3 keys go with their handles
    this.speciesMatSet.clear();
    if (this.crateMesh) { this.crateMesh.geometry.dispose(); (this.crateMesh.material as THREE.Material).dispose(); this.crateMesh.dispose(); this.crateMesh = null; }
    for (const mesh of this.stationProps.values()) this.scene.remove(mesh); // shared geometry + material stay for the session
    this.stationProps.clear(); this.stationSeats.clear(); this.nearStations = [];
    for (const sc of this.clashScenes.values()) { disposeLater(sc.banner.material); disposeLater(sc.tex); for (const sp of sc.sparks) disposeLater(sp.material); }
    this.clashScenes.clear();
    this.biomeMat?.dispose();
    this.chunkFallbackMat?.dispose();
    for (const m of this.retiredGroundMats) m.dispose(); this.retiredGroundMats = [];
    this.groundTextures?.a.dispose(); this.groundTextures?.b.dispose(); this.groundTextures = null; // ground v2 arrays
    this.groundMacroTex?.dispose(); this.groundMacroTex = null;
    for (const c of this.crowds.values()) c.dispose();
    for (const c of this.crowdsFar.values()) c.dispose();
    this.crowdsFar.clear();
    for (const c of this.crowdsIndoor.values()) c.dispose(); // (the twin materials are the bodies' cache — lib/indoor_body.ts)
    this.crowdsIndoor.clear();
    for (const inst of this.impostors.values()) inst?.dispose();
    this.impostors.clear();
    this.chunkImpostorHandles.clear();
    this.crowd?.dispose();
    this.releaseFoliage();
    this.npcGeo.dispose();
    this.npcMat.dispose();
  }
}

/** Deterministic 3-float hash from (regionX, regionZ, i, assetId) — same scatter every load, but
 * DIFFERENT per asset, so foliage + props in one region don't pile onto identical positions. */
function rng3(a: number, b: number, c: number, d: number): [number, number, number] {
  let s = ((Math.imul(a, 73856093) ^ Math.imul(b, 19349663) ^ Math.imul(c + 1, 83492791) ^ Math.imul(d + 1, 50331653)) >>> 0) || 1;
  const nx = (): number => { s = (Math.imul(s, 1103515245) + 12345) >>> 0; return s / 0xffffffff; };
  return [nx(), nx(), nx()];
}
